import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LanguageContext = createContext(null);

// Translations
const translations = {
  fr: {
    // Common
    loading: "Chargement...",
    save: "Sauvegarder",
    cancel: "Annuler",
    delete: "Supprimer",
    edit: "Modifier",
    add: "Ajouter",
    close: "Fermer",
    confirm: "Confirmer",
    back: "Retour",
    next: "Suivant",
    skip: "Passer",
    finish: "Terminer",
    error: "Erreur",
    success: "Succès",
    paused: "En pause",
    active: "Actif",
    completed: "Complété",
    total: "Total",
    days: "jours",
    weeks: "semaines",
    months: "mois",
    years: "années",
    all: "Toutes",
    filter: "Filtrer",
    search: "Rechercher",
    clear: "Effacer",
    no_results: "Aucun résultat",
    
    // Form fields
    amount: "Montant",
    category: "Catégorie",
    categories: "Catégories",
    name: "Nom",
    description: "Description",
    date: "Date",
    type: "Type",
    status: "Statut",
    optional: "optionnel",
    required: "requis",
    select: "Sélectionner...",
    password: "Mot de passe",
    confirm_password: "Confirmer le mot de passe",
    new_password: "Nouveau mot de passe",
    passwords_dont_match: "Les mots de passe ne correspondent pas",
    your_name: "Votre nom",
    situation: "Situation",
    budget_type: "Type de budget",
    
    // Messages
    loading_error: "Erreur de chargement",
    invalid_amount: "Montant invalide",
    failed_to_delete: "Erreur lors de la suppression",
    fill_all_fields: "Remplissez tous les champs",
    redirecting_login: "Redirection vers la connexion...",
    no_data: "Pas de données",
    
    // Dashboard/Stats
    total_spent: "Total dépensé",
    top_category: "Top catégorie",
    breakdown_by_category: "Répartition par catégorie",
    common_pot: "Pot commun",
    ai_predictions: "Prévisions IA",
    view_subscriptions: "Voir les abonnements",
    
    // Actions
    later: "Plus tard",
    how_it_works: "💡 Comment ça marche ?",
    forward_receipts: "Transférer vos reçus",
    transcript: "Transcription:",
    installment_payment: "Paiement en plusieurs fois",
    reached: "atteint",
    
    // More common
    notes: "Notes",
    icon: "Icône",
    frequency: "Fréquence",
    expenses: "Dépenses",
    expense_added: "Dépense ajoutée !",
    completed_plural: "Complétés",
    avg_per_day: "Moyenne/jour",
    monthly_amount: "Montant mensuel",
    budget_management_mode: "Mode de gestion du budget",
    min_8_chars: "Minimum 8 caractères",
    password_min_8: "Le mot de passe doit contenir au moins 8 caractères",
    payment_day: "Jour de prélèvement",
    create_account: "Créer un compte",
    forgot_password: "Mot de passe oublié ?",
    failed_to_save: "Erreur lors de la sauvegarde",
    failed_to_load: "Erreur de chargement",
    example_grocery: "Ex: Courses Carrefour",
    example_city: "Ex: Paris, Lyon, Marseille...",
    
    // UI labels
    invalid_code: "Code invalide",
    change_plan: "Changer de plan",
    no_budgets_configured: "Aucun budget configuré",
    ai_assistant: "Assistant IA",
    enable: "Activer",
    disable: "Désactiver",
    take_photo: "📷 Prendre une photo",
    category_details: "📋 Détail par catégorie",
    default_categories: "📋 Catégories par défaut",
    breakdown_by_category: "📊 Répartition par catégorie",
    predictions_by_category: "📊 Prévisions par catégorie",
    spending_trend: "📉 Tendance des dépenses",
    monthly_evolution: "📈 Évolution mensuelle",
    budget_progress: "💰 Progression des budgets",
    shared_budget: "💰 Budget partagé",
    shared_family_budget: "💰 Budget familial partagé",
    hybrid_budget: "🔄 Budget mixte",
    how_to_import: "💡 Comment importer vos reçus ?",
    your_personal_balance: "👤 Votre solde personnel",
    
    // More UI labels
    separate_budgets: "👤 Budgets séparés",
    common_pot_label: "🏦 Pot commun",
    my_categories: "✨ Mes catégories",
    complete: "✓ Complet",
    invitation_created: "✅ Invitation créée",
    limit_reached: "⚠️ Limite atteinte",
    failed: "échouées",
    saved: "Épargné",
    total_savings: "Épargne totale",
    listening: "Écoute en cours...",
    potential_savings: "Économies potentielles",
    available_savings: "Économies disponibles",
    deadline_passed: "Échéance dépassée",
    used: "utilisé",
    budget_status: "État des budgets",
    payment_history: "Évolution des versements",
    total_objectives: "Total objectifs",
    upcoming_payments: "Prochains prélèvements",
    scan_receipt: "Scanner un ticket",
    
    // OCR Scanner
    image_too_large: "Image trop grande (max 10MB)",
    could_not_read_receipt: "Impossible de lire le ticket",
    scan_error: "Erreur lors du scan",
    expense_added: "Dépense ajoutée !",
    prices_stored: "Prix enregistrés.",
    invoice_saved: "Facture sauvegardée.",
    error_creating: "Erreur lors de la création",
    of_receipt: "Du ticket de caisse",
    select_image: "Sélectionner une image",
    jpg_png_heic: "JPG, PNG ou HEIC",
    choose_from_gallery: "Choisir depuis la galerie",
    analyzing_receipt: "Analyse du ticket...",
    claude_extracting: "L'IA extrait les informations",
    use_today_date: "Utiliser la date d'aujourd'hui",
    keep_receipt_date: "Garder la date du ticket",
    merchant: "Commerce",
    merchant_name: "Nom du commerce",
    share_prices: "Partager les prix",
    save_as_invoice: "Sauvegarder comme facture",
    retry: "Reprendre",
    create_installments: "Créer {count}x",
    receipt_date_warning: "Ce ticket date du {date}. Si vous l'enregistrez avec cette date, il ne sera pas comptabilisé dans votre solde du mois en cours.",
    which_date_to_use: "Quelle date souhaitez-vous utiliser ?",
    counted_in_current_month: "Comptabilisé dans le mois en cours",
    keep_original_date: "Garder la date originale du ticket",
    not_counted_in_balance: "Ne sera pas comptabilisé dans votre solde actuel",
    products_detected_community: "produit(s) détecté(s) - aide la communauté !",
    keep_receipt_warranties: "Conservez ce ticket pour vos garanties",
    
    // Quick Add Modal
    split_over_months: "Répartir sur plusieurs mois",
    number_of_installments: "Nombre de mensualités",
    preview: "Aperçu :",
    recurring_subscription: "Abonnement récurrent",
    auto_charged_monthly: "Prélevé automatiquement chaque mois",
    select_day: "Choisir le jour",
    first_day: "1er",
    day_of_month: " du mois",
    summary_label: "Récapitulatif :",
    split_payment: "+ Paiement mixte",
    split_payment_label: "Paiement mixte",
    remaining_amount: "Reste: {amount}€",
    add_payment_method: "Ajouter un moyen de paiement",
    subscription_name: "Nom de l'abonnement",
    subscription_placeholder: "Ex: Netflix, Spotify, Assurance...",
    name_required_subscription: "Le nom est obligatoire pour un abonnement",
    for_label: "Pour",
    adding: "Ajout...",
    create_installment_payment: "Créer paiement en {count}x",
    add_expense: "Ajouter la dépense",
    charged_on_day: "Prélevé le {day} de chaque mois",
    
    // Email Scan Page
    paste_email_content: "Collez le contenu de l'email",
    receipt_detected: "Reçu détecté !",
    no_receipt_detected: "Pas de reçu détecté",
    analysis_error: "Erreur lors de l'analyse",
    imported_as_transaction: "Importé comme transaction !",
    email_scan: "Scan Emails",
    auto_import_receipts: "Import automatique des reçus",
    paste_confirmation_email: "Coller un email de confirmation",
    receipts_to_import: "Reçus à importer",
    import: "Importer",
    imported_receipts: "Reçus importés",
    
    // Bank Import
    import_bank_statement: "Importer un relevé bancaire",
    import_bank_desc: "Importez vos dépenses depuis un fichier PDF ou Excel",
    import_preview_desc: "Vérifiez et sélectionnez les transactions à importer",
    supported_formats: "Formats supportés",
    drop_file_here: "Glissez votre fichier ici",
    select_file: "Sélectionner un fichier",
    change_file: "Changer de fichier",
    target_month: "Mois cible",
    current_month: "Mois en cours",
    previous_month: "Mois précédent",
    analyzing: "Analyse en cours...",
    analyze: "Analyser",
    new: "Nouvelles",
    possible_duplicates: "Doublons possibles",
    duplicates: "Doublons",
    selected: "sélectionnées",
    select_all: "Tout",
    select_new_only: "Nouvelles uniquement",
    importing: "Import en cours...",
    transactions_imported: "transactions importées",
    no_transactions_selected: "Aucune transaction sélectionnée",
    import_preview_error: "Erreur lors de l'analyse du fichier",
    import_error: "Erreur lors de l'import",
    invalid_file_format: "Format de fichier invalide. Utilisez PDF ou Excel.",
    
    no_email_receipts: "Aucun reçu email",
    copy_email: "Copiez l'email",
    paste_here: "Collez ici",
    paste_email: "Coller un email",
    subject_optional: "Sujet (optionnel)",
    subject_placeholder: "Ex: Confirmation de commande Amazon",
    from_optional: "Expéditeur (optionnel)",
    email_content_required: "Contenu de l'email *",
    analyze: "Analyser",
    
    login: "Se connecter",
    connection: "Connexion",
    paste_confirmation_email: "Coller un email de confirmation",
    quick_access: "Accès rapide depuis ton écran",
    
    // More contextual
    active_users: "utilisateurs actifs",
    urgent: "urgent",
    transactions_label: "transactions",
    transaction_count: "transaction(s)",
    success_label: "réussies",
    result_count: "résultat(s)",
    remaining: "restant(s)",
    to_add_expense: "pour ajouter une dépense",
    paid: "payé(s)",
    pauses: "pauses",
    rows_detected: "lignes détectées",
    detected: "détecté",
    from_common_pot: "déduits du pot commun",
    from_your_budget: "déduits de votre budget",
    confidence: "confiance",
    purchase_count: "achat(s)",
    active_subscriptions: "abonnements actifs",
    invited_to_join: "Vous êtes invité à rejoindre le foyer",
    you_will_lose: "Vous perdrez :",
    you_both_win: "Vous gagnez tous les deux",
    you_set_password: "Vous définissez le mot de passe",
    invited_with_subscription: "Vous avez été invité avec un abonnement",
    you_left_household: "Vous avez quitté le foyer",
    already_have_account: "Vous avez déjà un compte ?",
    your_city: "Votre ville",
    your_monthly_income: "Votre revenu mensuel net",
    your_first_name: "Votre prénom",
    your_plan_includes: "Votre plan inclut :",
    visualize_spending: "Visualisez vos dépenses",
    city: "Ville",
    please_select_image: "Veuillez sélectionner une image",
    enter_name_and_code: "Veuillez entrer votre prénom et le code",
    enter_valid_amount: "Veuillez entrer un montant valide",
    validate_code: "Valider le code",
    use_my_savings: "Utiliser mes économies",
    budget_usage: "Utilisation du budget",
    an_error_occurred: "Une erreur est survenue",
    you_receive: "Tu reçois",
    quarter: "Trimestre",
    last_month: "Mois précédent",
    last_quarter: "Trimestre précédent",
    forward_to: "Transférez à :",
    transfer: "Transférer",
    transfer_to_balance: "Transfert vers solde courant",
    unlimited_transactions: "Transactions illimitées",
    transaction_updated: "Transaction modifiée !",
    transaction_added: "Transaction ajoutée !",
    all_categories: "Toutes les catégories",
    all_set: "Tout est prêt !",
    everything_you_need: "Tout ce dont tu as besoin",
    all_rights_reserved: "Tous droits réservés.",
    total_target: "Total objectifs",
    monthly_total: "Total mensuel",
    monthly_recurring_total: "Total mensuel récurrent",
    total_expenses: "Total dépenses",
    receipt_scanned: "Ticket scanné avec succès !",
    receipt_previous_month: "Ticket du mois précédent",
    your_referrals: "Tes filleuls",
    switch_store_savings: "par mois en changeant de magasin",
    total_received: "Total reçu",
    install_app: "Installer l'app",
    adjust_income: "Ajuster mon revenu",
    adjust_income_desc: "Primes, heures sup, congés...",
    description_optional: "Description (optionnel)",
    email_or_username: "Email ou nom d'utilisateur",
    of_month: "du mois",
    
    // Landing
    hero_title: "Ton pote qui comprend l'argent",
    hero_subtitle: "Sans te juger.",
    hero_cta: "Commencer gratuitement",
    hero_login: "Se connecter",
    feature1_title: "1 seconde pour tracker",
    feature1_desc: "Ajoute tes dépenses en un clin d'œil",
    feature2_title: "Conseils actionnables",
    feature2_desc: "Des conseils personnalisés, pas génériques",
    feature3_title: "Atteins tes objectifs",
    feature3_desc: "3x plus vite qu'avec les autres apps",
    
    // Auth & Messages
    login_with_google: "Continuer avec Google",
    logout: "Se déconnecter",
    login_success: "Connexion réussie !",
    invalid_credentials: "Identifiants incorrects",
    invalid_email: "Email invalide",
    password_8_chars: "Au moins 8 caractères",
    password_uppercase: "Au moins une majuscule",
    password_lowercase: "Au moins une minuscule",
    password_digit: "Au moins un chiffre",
    password_too_weak: "Mot de passe trop faible:",
    account_created: "Compte créé ! Connexion...",
    registration_error: "Erreur lors de l'inscription",
    
    // Landing features
    fast: "Rapide",
    fast_desc: "1 seconde pour ajouter une dépense",
    reach_goals: "Atteignez vos objectifs financiers",
    insights: "Insights",
    understand_habits: "Comprenez vos habitudes",
    secure: "Sécurisé",
    encrypted_data: "Données chiffrées",
    ai: "IA",
    personalized_advice: "Conseils personnalisés",
    manage_together: "Gérez ensemble",
    goals_achieved: "d'objectifs atteints",
    ready_to_start: "Prêt à commencer ?",
    free_no_commitment: "Gratuit • Sans engagement • RGPD compliant",
    hero_main_title_fr: "Ton pote qui comprend",
    hero_main_title_highlight: "l'argent",
    hero_main_desc: "Gère tes finances en 1 seconde. Conseils IA personnalisés. Mode famille avancé.",
    join_thousands_users: "Rejoins des milliers d'utilisateurs qui ont repris le contrôle de leurs finances",
    
    // Onboarding
    onboarding_welcome: "Bienvenue dans SpenSpot !",
    onboarding_salary: "Quel est ton salaire net mensuel ?",
    onboarding_city: "Dans quelle ville habites-tu ?",
    onboarding_situation: "Quelle est ta situation ?",
    onboarding_personality: "Choisis ton conseiller IA",
    onboarding_budget: "Voici ton budget suggéré",
    situation_single: "Célibataire",
    situation_couple: "En couple",
    situation_family: "Famille avec enfants",
    
    // Onboarding - Join household
    have_invite_code: "J'ai un code d'invitation",
    create_own_account: "Créer mon propre compte",
    join_household: "Rejoindre un foyer",
    family_joined: "Famille rejointe !",
    monthly_net_income: "Revenu mensuel net",
    household_composition: "Composition du foyer",
    add_household_members: "Ajoutez les membres de votre foyer",
    household_name: "Nom du foyer",
    first_name: "Prénom",
    cannot_join: "Impossible de rejoindre",
    accept_and_join: "J'accepte et je rejoins",
    welcome_to_household: "Bienvenue dans le foyer !",
    invitation_code: "Code d'invitation",
    invite_code_info: "Le code commence par INV- et est valable 7 jours",
    example_name: "Ex: Marie",
    join_family_name: "Rejoindre",
    created_by_members: "Créé par {owner} • {count} membre(s)",
    example_household_name: "Ex: Famille Dupont",
    email_for_invitation: "Email (pour invitation)",
    pocket_money_optional: "Argent de poche (optionnel)",
    add_member: "Ajouter un membre",
    summary: "Résumé",
    how_manage_finances: "Comment souhaitez-vous gérer les finances du foyer ?",
    common_pot_config: "Configuration du Pot Commun",
    my_monthly_contribution: "Ma contribution mensuelle",
    member_contribution: "Contribution de {name}",
    total_monthly_pot: "Pot commun mensuel total",
    oops_cannot_join: "Oups, impossible de rejoindre !",
    back_to_home: "Retour à l'accueil",
    track_spending: "Suivi des dépenses",
    challenges: "Défis",
    lets_go: "C'est parti ! 🚀",
    income_label: "Revenu",
    household_label: "Foyer",
    budget_mode_label: "Mode budget",
    complete_profile_info: "Complétez vos informations pour finaliser votre profil",
    person_enter_salary_signup: "Cette personne entrera son salaire lors de son inscription",
    adults_children_count: "{adults} adulte(s), {children} enfant(s)",
    adult_set_salary_invite: "Chaque adulte définira son salaire lors de son inscription via le code d'invitation",
    month: "mois",
    member: "Membre",
    
    // Dashboard
    dashboard: "Tableau de bord",
    available_balance: "Solde disponible",
    this_month: "Ce mois",
    budget_progress: "Progression budget",
    recent_transactions: "Transactions récentes",
    view_all: "Voir tout",
    no_transactions: "Aucune transaction",
    
    // Transactions
    transactions: "Transactions",
    add_expense: "Ajouter une dépense",
    amount: "Montant",
    category: "Catégorie",
    description: "Description",
    date: "Date",
    payment_method: "Méthode de paiement",
    
    // Categories
    cat_logement: "Logement",
    cat_transport: "Transport",
    cat_alimentation: "Alimentation",
    cat_abonnements: "Abonnements",
    cat_shopping: "Shopping",
    cat_loisirs: "Loisirs",
    cat_sante: "Santé",
    cat_epargne: "Épargne",
    cat_investissements: "Investissements",
    cat_education: "Éducation",
    cat_animaux: "Animaux",
    cat_enfants: "Enfants",
    cat_cadeaux: "Cadeaux",
    cat_entretien: "Entretien",
    cat_professionnel: "Professionnel",
    cat_divers: "Divers",
    cat_personnalise: "Personnalisé",
    cat_argent_de_poche: "Argent de poche",
    cat_autre: "Autre",
    cat_restaurant: "Restaurant",
    cat_food: "Alimentation",
    cat_voyage: "Voyage",
    cat_voyages: "Voyages",
    cat_vetements: "Vêtements",
    cat_beauté: "Beauté",
    cat_beaute: "Beauté",
    cat_carburant: "Carburant",
    cat_telephone: "Téléphone",
    cat_internet: "Internet",
    cat_assurance: "Assurance",
    cat_factures: "Factures",
    edit_budget: "Modifier le budget",
    save_changes: "Enregistrer",
    
    // Budget
    budgets: "Budgets",
    set_budget: "Définir un budget",
    budget_remaining: "Restant",
    budget_spent: "Dépensé",
    over_budget: "Dépassé",
    
    // Objectives
    objectives: "Objectifs",
    create_objective: "Créer un objectif",
    objective_name: "Nom de l'objectif",
    target_amount: "Montant cible",
    target_date: "Date cible",
    days_remaining: "jours restants",
    monthly_required: "€/mois requis",
    add_contribution: "Ajouter une contribution",
    
    // Challenges
    daily_challenge: "Défi du jour",
    challenge_completed: "Défi complété !",
    xp_gained: "XP gagnés",
    streak: "Série",
    level: "Niveau",
    
    // AI
    ai_advice: "Conseils IA",
    get_advice: "Obtenir des conseils",
    ai_analyzing: "SpenSpot analyse tes finances...",
    
    // Settings
    settings: "Paramètres",
    profile: "Profil",
    language: "Langue",
    theme: "Thème",
    notifications: "Notifications",
    privacy: "Confidentialité",
    help: "Aide",
    
    // Payment methods
    payment_card: "Carte bancaire",
    payment_cash: "Espèces",
    payment_transfer: "Virement",
    payment_other: "Autre",
    
    // Referral
    referral: "Parrainage",
    referral_title: "Invite tes amis !",
    referral_subtitle: "Gagnez tous les deux 1 mois Premium gratuit",
    your_code: "Ton code",
    copy_code: "Copier",
    copied: "Copié !",
    share: "Partager",
    friends_invited: "Amis invités",
    premium_earned: "Mois Premium gagnés",
    enter_referral_code: "Entrer un code parrain",
    apply_code: "Appliquer",
    referral_success: "Code appliqué ! +1 mois Premium",
    leaderboard: "Top parrains",
    
    // OCR
    scan_receipt: "Scanner un ticket",
    take_photo: "Prendre une photo",
    select_image: "ou sélectionner une image",
    analyzing: "Analyse du ticket...",
    merchant: "Commerce",
    
    // Alerts
    alerts: "Alertes",
    budget_exceeded: "Budget dépassé",
    budget_warning: "Attention budget",
    overdraft_warning: "Risque de découvert",
    
    // Notification Settings
    preferences_saved: "Préférences enregistrées",
    push_notifications: "Notifications Push",
    enabled: "Activées",
    blocked_by_browser: "Bloquées par le navigateur",
    not_enabled: "Non activées",
    budget_alerts: "Alertes Budget",
    alert_at_80: "Alerte à 80%",
    alert_80_desc: "Notification quand 80% du budget est utilisé",
    alert_at_100: "Alerte à 100% (dépassement)",
    alert_100_desc: "Notification quand le budget est dépassé",
    custom_threshold: "Seuil personnalisé",
    custom_threshold_desc: "Définir votre propre seuil d'alerte",
    balance_alerts: "Alertes de Solde",
    balance_alerts_enabled: "Alertes solde activées",
    balance_alerts_desc: "Notification quand le solde est bas",
    alert_threshold: "Seuil d'alerte",
    alert_threshold_desc: "Alerter quand le solde est inférieur à ce montant",
    zero_balance_alert: "Alerte solde zéro",
    zero_balance_alert_desc: "Notification quand le solde atteint zéro",
    overdraft_alert: "Alerte découvert",
    overdraft_alert_desc: "Notification quand le solde est négatif",
    overdraft_prediction: "Prévision de découvert",
    overdraft_prediction_desc: "Notification si le découvert est prévu",
    propose_using_savings: "Proposer d'utiliser l'épargne",
    propose_using_savings_desc: "Proposer de puiser dans vos objectifs si solde bas",
    installment_reminders: "Rappels Mensualités",
    reminder_x_days_before: "Rappel X jours avant",
    reminder_before_payment: "Notification avant la date de paiement",
    reminder_on_payment_day: "Rappel le jour même",
    paused_objectives_reminders: "Rappels Objectifs en Pause",
    monthly_reminder: "Rappel mensuel",
    show_until_day: "Afficher jusqu'au jour",
    real_time: "Temps réel",
    daily_summary: "Résumé quotidien",
    summary_time: "Heure du résumé :",
    send_test_notification: "Envoyer une notification de test",
    unblock_in_browser: "Débloquez dans les paramètres du navigateur",
    daily_budget_tip: "Budget quotidien",
    
    // Family
    family: "Famille",
    family_title: "Mon Foyer",
    create_family: "Créer un foyer",
    family_name: "Nom du foyer",
    family_created: "Foyer créé !",
    join_family: "Rejoindre un foyer",
    leave_family: "Quitter le foyer",
    delete_family: "Supprimer le foyer",
    invite_member: "Inviter un membre",
    add_member: "Ajouter un membre",
    add_child: "Ajouter un enfant",
    member_name: "Nom",
    member_role: "Rôle",
    member_salary: "Revenu mensuel",
    employment_status: "Statut professionnel",
    status_employed: "Salarié(e)",
    status_freelance: "Indépendant(e)",
    status_student: "Étudiant(e)",
    status_unemployed: "Sans emploi",
    status_other: "Autre",
    no_fixed_salary: "Pas de salaire fixe",
    variable_income_hint: "Revenus variables — à gérer dans « Mes revenus »",
    fixed_salary_question: "As-tu un salaire fixe ?",
    variable_income_nudge_title: "Tes revenus du moment",
    variable_income_nudge_desc: "Tu n'as pas de salaire fixe — pense à enregistrer tes revenus quand tu en reçois pour des projections précises.",
    log_income_now: "Enregistrer un revenu",
    manage_income_sources: "Gérer mes revenus",
    projected_this_month: "Projection ce mois",
    income_evolution_title: "Évolution de mes revenus",
    income_evolution_subtitle: "{n} derniers mois",
    income_evolution_empty: "Pas encore de revenus enregistrés. Commence à enregistrer pour suivre l'évolution.",
    fixed_income_label: "Fixe",
    variable_income_label: "Variable",
    view_variable_income_details: "Voir le détail des revenus variables",
    variable_income_breakdown_title: "Détail des revenus variables",
    variable_income_breakdown_desc: "Liste des revenus non récurrents enregistrés ce mois-ci",
    variable_income_breakdown_empty: "Aucun revenu variable enregistré ce mois-ci.",
    total_variable_income: "Total revenus variables",
    details: "Détails",
    role_owner: "Propriétaire",
    role_adult: "Adulte",
    role_child: "Enfant",
    family_code: "Code d'invitation",
    enter_family_code: "Entrer un code famille",
    total_income: "Revenu total du foyer",
    family_balance: "Solde familial",
    spending_by_member: "Dépenses par membre",
    budget_mode: "Mode budget",
    budget_shared: "Budget partagé",
    budget_separate: "Budgets séparés",
    budget_hybrid: "Budget mixte",
    budget_mode_shared_desc: "Un seul budget pour tout le foyer",
    budget_mode_separate_desc: "Chaque adulte gère son budget",
    budget_mode_hybrid_desc: "Budgets individuels + compte commun",
    common_budget: "Budget commun",
    personal_budget: "Budget personnel",
    common_percentage: "% au compte commun",
    for_member: "Pour",
    family_general: "Dépense famille",
    no_family: "Pas encore de foyer",
    no_family_desc: "Créez un foyer pour suivre les finances familiales ensemble",
    family_dashboard: "Dashboard Familial",
    family_transactions: "Transactions familiales",
    invite_by_email: "Inviter par email",
    invite_by_code: "Partager le code",
    pending_invites: "Invitations en attente",
    
    // Additional common translations
    spent: "dépensé",
    month: "mois",
    shared: "Partagé",
    household: "Foyer",
    show_amounts: "Afficher les montants",
    hide_amounts: "Masquer les montants",
    subscriptions: "Abonnements",
    preferences: "Préférences",
    daily_challenge_title: "Défi du jour",
    no_challenge: "Pas de défi aujourd'hui",
    challenge_answer: "Répondre",
    good_job: "Bien joué !",
    try_again: "Réessayer",
    
    // Dashboard specific
    hello: "Bonjour",
    hi: "Salut",
    see_all: "Voir tout",
    reports: "Rapports",
    scanner: "Scanner",
    scan_receipt: "Scanner ticket",
    voice: "Voix",
    voice_input: "Saisie vocale",
    shopping: "Shopping",
    recurring: "Récurrent",
    personal: "Personnel",
    common_pot: "Pot Commun",
    monthly_common_pot: "Pot commun mensuel",
    household_income: "Revenus foyer",
    personal_income: "Revenu personnel",
    monthly_income: "Revenu mensuel",
    initial_balance: "Solde initial",
    members: "membres",
    per_week: "par semaine",
    per_month: "par mois",
    on_request: "sur demande",
    you_receive: "Tu reçois",
    pocket_money_low: "Ton argent de poche est presque vide !",
    no_expenses_yet: "Pas encore de dépenses ce mois-ci",
    refused: "Refusée",
    approved: "Approuvée",
    pending: "En attente",
    what_for_optional: "Pour quoi faire ? (optionnel)",
    budget_shared_label: "💰 Budget partagé",
    budget_separate_label: "👤 Budgets séparés",
    budget_hybrid_label: "🔄 Budget mixte",
    hybrid: "Mixte",
    more_info: "Plus d'infos",
    
    // Family page
    household_members: "Membres du foyer",
    household_deleted: "Foyer supprimé",
    edit_salary: "Modifier mon salaire",
    net_monthly_income: "Revenu mensuel net",
    update_income_desc: "Mettez à jour votre revenu mensuel net. Ce montant sera utilisé pour calculer le budget familial.",
    pocket_money: "Argent de poche",
    my_pocket_money: "Mon argent de poche",
    balance_label: "Solde:",
    current_balance: "Solde actuel:",
    optional: "optionnel",
    transfer_ownership: "Transférer la propriété",
    first_name: "Prénom",
    family_name_label: "Nom de famille",
    invalid_amount: "Montant invalide",
    member_added: "Membre ajouté !",
    passwords_dont_match: "Les mots de passe ne correspondent pas",
    close: "Fermer",
    register: "Enregistrer",
    give: "Donner",
    copy_code: "Copier le code",
    common: "Commun",
    code_copied: "Code copié !",
    adult: "Adulte",
    invitation_created: "✅ Invitation créée",
    limit_reached: "⚠️ Limite atteinte",
    contribution_evolution: "Évolution des versements",
    or: "ou",
    you: "Vous",
    you_set_password: "Vous définissez le mot de passe",
    left_household: "Vous avez quitté le foyer",
    view_subscriptions: "Voir les abonnements",
    enter_valid_amount: "Veuillez entrer un montant valide",
    budget_usage: "Utilisation du budget",
    total_received: "Total reçu",
    total_spent: "Total dépensé",
    total_this_month: "Total ce mois",
    total_subscriptions_month: "Total abonnements ce mois",
    total_family: "Total Famille",
    savings_rate: "Taux d'épargne",
    salary_updated: "Salaire mis à jour !",
    request_money: "Demander de l'argent",
    request_sent: "Demande envoyée !",
    request_amount: "Montant demandé",
    my_pending_requests: "Mes demandes en attente",
    manage_family_finances: "Gérez les finances en famille",
    charts: "Graphiques",
    tools: "Outils",
    my_expenses: "Mes dépenses",
    hide: "Masquer",
    my_family: "Ma famille",
    i_want: "Je veux",
    spending_by_category: "Dépenses par catégorie",
    no_data: "Aucune donnée",
    child_dashboard: "Tableau de bord enfant",
    owner: "Propriétaire",
    child: "Enfant",
    username: "Nom d'utilisateur",
    password: "Mot de passe",
    confirm_password: "Confirmer le mot de passe",
    create_account: "Créer le compte",
    account_created: "Compte créé !",
    email_address: "Adresse email",
    invite_by_email_desc: "Un email d'invitation sera envoyé",
    send_invitation: "Envoyer l'invitation",
    invitation_sent: "Invitation envoyée !",
    pending_invitations: "Invitations en attente",
    cancel_invitation: "Annuler l'invitation",
    remove_member: "Retirer le membre",
    confirm_remove_member: "Êtes-vous sûr de vouloir retirer ce membre ?",
    allowance_settings: "Paramètres d'argent de poche",
    enable_pocket_money: "Activer l'argent de poche",
    allowance_amount: "Montant de l'allocation",
    allowance_frequency: "Fréquence",
    weekly: "Hebdomadaire",
    monthly: "Mensuel",
    on_demand: "Sur demande",
    approve: "Approuver",
    reject: "Refuser",
    request_approved: "Demande approuvée",
    request_rejected: "Demande refusée",
    child_requests: "Demandes des enfants",
    no_pending_requests: "Aucune demande en attente",
    member_spending: "Dépenses du membre",
    family_overview: "Vue d'ensemble famille",
    contribution_to_common: "Contribution au pot commun",
    personal_remaining: "Reste personnel",
    
    // Objectives
    my_objectives: "Mes Objectifs",
    save_for_projects: "Épargnez pour vos projets",
    create: "Créer",
    statistics: "Statistiques",
    progress: "Progression",
    challenge_completed: "Challenge complété !",
    keep_going: "Continue comme ça pour compléter le challenge !",
    well_done: "Bravo !",
    not_quite: "Pas tout à fait...",
    checking: "Vérification...",
    submit_answer: "Valider ma réponse",
    
    // Family page additional
    join_my_household: "Rejoins mon foyer SpenSpot",
    failed_to_update_salary: "Erreur lors de la mise à jour",
    failed_to_remove_member: "Erreur lors de la suppression",
    ownership_transferred: "Propriété transférée avec succès !",
    failed_to_transfer: "Erreur lors du transfert",
    settings_saved: "Paramètres enregistrés !",
    request_approved_emoji: "Demande acceptée ! 💰",
    request_denied: "Demande refusée",
    invite_a_member: "Inviter un membre",
    pending_invitations: "Invitations en attente",
    invitation_resent: "Invitation renvoyée !",
    resend_invitation: "Renvoyer l'invitation",
    invitation_cancelled: "Invitation annulée",
    cancel_invitation: "Annuler l'invitation",
    create_invitation: "Créer une invitation",
    upgrade_subscription: "Passer à l'abonnement supérieur",
    members_count: " membres",
    contribute: "Contribuer",
    monthly_budget: "Budget mensuel",
    spent_this_month: "Dépensé ce mois",
    common_expenses_by_member: "Dépenses communes par membre",
    expected_contributions: "Contributions mensuelles prévues",
    not_set: "Non défini",
    manage_pocket_money: "Gérer argent de poche",
    remove_from_household: "Retirer du foyer",
    pocket_money_requests: "Demandes d'argent de poche",
    deny: "Refuser",
    accept: "Accepter",
    breakdown_by_member: "Détail par membre",
    monthly_subscriptions: "Abonnements du mois",
    manage: "Gérer",
    by_category: "Par catégorie",
    upcoming_debits: "Prochains prélèvements",
    spending_by_category_title: "Dépenses par catégorie",
    allowance: "Allocation",
    cumulative: "Cumulé",
    spending_categories: "Catégories de dépenses",
    edit_my_salary: "Modifier mon salaire",
    net_monthly_income_euro: "Revenu mensuel net (€)",
    raise_bonus_job: "Augmentation, prime, changement de poste",
    instant_budget_update: "Modification instantanée du budget familial",
    remove_a_member: "Retirer un membre",
    remove: "Retirer",
    give_pocket_money: "Donner de l'argent de poche",
    enable_pocket_money_child: "Activer argent de poche",
    monthly_allowance_optional: "Allocation mensuelle (optionnel)",
    shared_budget: "Budget partagé",
    separate_budget: "Budget séparé",
    code_for: "Code pour",
    continue_action: "Continuer",
    contribute_to_common_pot: "Contribuer au Pot Commun",
    contribution_added: "Contribution ajoutée !",
    common_pot_contribution: "Contribution au pot commun",
    create_credentials: "Créer les identifiants",
    from_which_balance: "Depuis quel solde ?",
    email_sent_to: "Email envoyé à",
    example_groceries: "Ex: Courses du mois",
    example_family_name: "Ex: Famille Martin",
    example_name: "Ex: Marie",
    settings_updated: "Paramètres mis à jour",
    regular_allowance: "Montant régulier",
    amount_to_give: "Montant à donner",
    pocket_money_settings: "Paramètres argent de poche",
    my_balance: "Mon solde",
    savings_rate: "Taux d'épargne",
    available_exclaim: "Disponible !",
    
    // Subscriptions
    subscription_created: "Abonnement créé !",
    subscription_paused: "Abonnement mis en pause",
    subscription_updated: "Abonnement modifié !",
    subscription_reactivated: "Abonnement réactivé",
    subscription_deleted: "Abonnement supprimé",
    common_subscriptions: "Abonnements Communs",
    personal_subscriptions: "Abonnements Personnels",
    active_subscriptions_label: "Abonnements actifs",
    add_subscription: "Ajouter un abonnement",
    no_subscriptions_yet: "Aucun abonnement pour le moment",
    no_results_found: "Aucun résultat trouvé",
    clear_filters: "Effacer les filtres",
    paused_label: "En pause",
    example_netflix: "Ex: Netflix, Spotify...",
    example_family_plan: "Ex: Plan familial",
    manage_recurring_subs: "Gérez vos abonnements récurrents",
    my_subscriptions: "Mes Abonnements",
    pause_subscription: "Mettre en pause",
    name_amount_required: "Nom et montant requis",
    new_subscription: "Nouvel abonnement",
    search_subscription: "Rechercher un abonnement...",
    reactivate: "Réactiver",
    by_category_label: "Répartition par catégorie",
    of_month_suffix: " du mois",
    // Subscription frequencies & schedule
    frequency: "Fréquence",
    first_payment: "Premier paiement",
    first_payment_date: "Date du premier paiement",
    end_date: "Date de fin",
    end_date_optional: "Date de fin (optionnelle)",
    next_payment: "Prochain paiement",
    custom_days_label: "Tous les X jours",
    custom_days_placeholder: "Ex: 60",
    freq_weekly: "Toutes les semaines",
    freq_monthly: "Tous les mois",
    freq_quarterly: "Tous les 3 mois",
    freq_semi_annual: "Tous les 6 mois",
    freq_yearly: "Tous les ans",
    freq_custom_days: "Personnalisé (en jours)",
    upcoming_payments: "Échéances à venir",
    filter_this_month: "Ce mois-ci",
    filter_30_days: "30 prochains jours",
    filter_90_days: "90 prochains jours",
    total_to_plan: "Total à prévoir",
    no_upcoming_payments: "Aucune échéance à venir sur cette période",
    // Multi-member attribution
    attributed_to_members: "Pour quel(s) membre(s) ?",
    attributed_to_members_hint: "Sélectionne un ou plusieurs membres pour qui cette dépense est faite. Le débit reste sur le compte de celui qui paye.",
    spending_by_member: "Dépenses par membre",
    no_attributed_spending: "Aucune dépense attribuée ce mois-ci",
    filter_by_member: "Filtrer par membre",
    all_members: "Tous les membres",
    
    // More common strings
    view_objectives: "Voir objectifs",
    view_budgets: "Voir budgets",
    total_colon: "Total :",
    real_time: "Temps réel",
    success_rate: "Taux de réussite",
    select_category: "Sélectionnez une catégorie",
    select_image: "Sélectionner une image",
    secure: "Sécurisé",
    sync: "Sync",
    delete_transaction_confirm: "Supprimer cette transaction ?",
    delete_category_confirm: "Supprimer cette catégorie ?",
    delete_budget_confirm: "Supprimer ce budget ?",
    subject_optional: "Sujet (optionnel)",
    track_spending: "Suivi des dépenses",
    suggest_budget: "Suggérer un budget",
    suggestions_for_you: "Suggestions pour toi",
    objectives_statistics: "Statistiques des Objectifs",
    price_statistics: "Statistiques de prix",
    smart_shopping: "Shopping Intelligent",
    custom_threshold: "Seuil personnalisé",
    will_be_deducted_from: "Sera déduit de:",
    sign_in: "Se connecter",
    scan_receipt: "Scanner un ticket",
    email_scan: "Scan Emails",
    no_expiration: "Sans expiration",
    voice_input: "Saisie vocale",
    voice_input_title: "Saisie Vocale",
    role_colon: "Rôle :",
    summary: "Résumé",
    daily_summary: "Résumé quotidien",
    results_for: "Résultats pour",
    split_over_months: "Répartir sur plusieurs mois",
    negative_balance_ignored: "Solde négatif ignoré",
    previous_month_negative: "Solde négatif du mois précédent",
    initial_balance_short: "Solde initial",
    personal_situation: "Situation personnelle",
    overdraft_risk_alert: "🚨 Risque de Découvert",
    sync_complete_alert: "✅ Synchronisation",
    budget_alert: "⚠️ Alerte Budget",
    objectives_reminder_alert: "⏸️ Rappel Objectifs",
    email_placeholder: "email@exemple.com ou identifiant",
    
    // Additional translations
    email_required_adults: "L'email est requis pour les adultes",
    child_added_create_account: "Enfant ajouté ! Créez son compte pour qu'il puisse se connecter.",
    username_unavailable: "Nom d'utilisateur non disponible",
    invitation_sent_to_email: "Invitation envoyée à l'email",
    invite_by_email: "Inviter par email",
    child_creates_account: "L'enfant crée son compte",
    childs_email: "Email de l'enfant",
    create_account_btn: "Créer le compte",
    send_invitation: "Envoyer l'invitation",
    allow_child_manage_money: "Permettre à l'enfant de gérer son argent",
    auto_monthly_allowance_desc: "Montant automatique versé chaque mois (laisser vide pour désactiver)",
    common_pot_balance_label: "Solde pot commun",
    deducted_from_personal: "L'argent sera déduit de votre solde personnel",
    update_income_desc: "Mettez à jour votre revenu mensuel net. Ce montant sera utilisé pour calculer le budget familial.",
    household_deleted: "Foyer supprimé",
    
    // Objectives page
    add_contribution: "Ajouter une contribution",
    achieved_exclaim: "Atteint !",
    no_objectives_yet: "Aucun objectif pour le moment",
    shared_label: "Commun",
    pause_behavior: "Comportement de pause",
    auto_contribute: "Contribution automatique",
    contributed: "Contribué",
    create_first_objective: "Créer mon premier objectif",
    target_date: "Date cible",
    avg_duration_days: "Durée moyenne (jours)",
    failed_to_create: "Erreur lors de la création",
    failed_to_update: "Erreur lors de la modification",
    example_vacation: "Ex: Pour les vacances en famille",
    contribution_day: "Jour du prélèvement",
    days_paused: "Jours en pause",
    leave_empty_default: "Laissez vide pour rétablir le montant par défaut",
    change_this_month_contribution: "Modifier le prélèvement ce mois",
    change_label: "Modifier",
    modified_this_month: "Modifié ce mois",
    target_amount: "Montant cible",
    total_pauses: "Nombre de pauses",
    new_amount: "Nouveau montant",
    new_objective: "Nouvel objectif",
    objective_created: "Objectif créé !",
    most_paused_objective: "Objectif le plus pausé",
    objective_paused: "Objectif mis en pause",
    objective_updated: "Objectif modifié !",
    objective_reactivated: "Objectif réactivé",
    objective_deleted: "Objectif supprimé",
    objectives_paused_1x: "Objectifs pausés au moins 1x",
    objective_achieved: "Objectif atteint !",
    personal_label: "Personnel",
    
    // Missing keys causing crash
    fill_required_fields: "Remplissez tous les champs obligatoires",
    daily: "Quotidien",
    objective: "Objectif",
    who_pays_label: "Qui met de côté ?",
    i_only: "Moi seul",
    everyone: "Tout le monde",
    pause_reason: "Raison de la pause",
    no_objectives_to_display: "Aucun objectif à afficher",
    start_saving_for: "Commencez à épargner pour",
    remaining_amount: "Reste à épargner",
    monthly_contrib_of: "Contribution mensuelle de",
    will_pause_until: "Sera en pause jusqu'au",
    
    // More missing keys
    send: "Envoyer",
    suggestion: "Suggestion",
    deleted: "Supprimé",
    code: "Code",
    state: "État",
    
    // Invoices page
    invoices: "Factures",
    my_invoices: "Mes Factures",
    manage_invoices_desc: "Stockez vos factures et tickets en toute sécurité",
    upload_invoice: "Ajouter une facture",
    product_name: "Nom du produit",
    store_name: "Enseigne",
    purchase_date: "Date d'achat",
    warranty_months: "Garantie (mois)",
    warranty_expires: "Fin de garantie",
    warranty_status: "Statut garantie",
    warranty_active: "Active",
    warranty_expiring: "Expire bientôt",
    warranty_expired: "Expirée",
    warranty_expiring_alert: "Garantie expire bientôt",
    warranty_reminder: "Rappel de garantie",
    view_invoice: "Voir la facture",
    no_invoices_yet: "Aucune facture pour le moment",
    add_first_invoice: "Ajouter ma première facture",
    invoice_uploaded: "Facture ajoutée !",
    invoice_deleted: "Facture supprimée",
    invoice_updated: "Facture modifiée",
    download_invoice: "Télécharger",
    search_invoices: "Rechercher par produit, enseigne...",
    invoice_limit_reached: "Limite de factures atteinte",
    upgrade_for_more: "Passez à un abonnement supérieur pour plus d'espace",
    expiring_warranties: "Garanties qui expirent",
    days_remaining: "jours restants",
    invoice_details: "Détails de la facture",
    select_file: "Sélectionner un fichier",
    max_file_size: "Taille max: 5 MB",
    allowed_formats: "Formats: JPG, PNG, PDF",
    storage_used: "Espace utilisé",
    of_limit: "sur",
    
    // Settings page
    subscription_cancelled: "Abonnement annulé",
    quick_access_home: "Accès rapide depuis ton écran",
    anticipate_overruns: "Anticipez vos dépassements",
    interface_language: "Langue de l'interface",
    voice_ai_assistant: "Assistant vocal IA",
    ai_advisor: "Conseiller IA",
    create_join_household: "Créer ou rejoindre un foyer",
    failed_change_plan: "Erreur lors du changement de plan",
    expires_on: "Expire le",
    subscription: "Abonnement",
    free: "Gratuit",
    made_with_love: "Fait avec ❤️ en France",
    earn_free_premium: "Gagne 1 mois Premium gratuit !",
    keep_subscription: "Garder mon abonnement",
    manage_custom_categories: "Gérer mes catégories personnalisées",
    manage_household: "Gérer mon foyer",
    import_csv: "Importer CSV",
    import_bank_history: "Importer un historique bancaire",
    ai_personality: "Personnalité IA",
    preferences: "Préférences",
    unlimited_ai_questions: "Questions IA illimitées",
    replay_tutorial: "Revoir le tutoriel",
    other_label: "Autres",
    cancel_subscription: "Annuler l'abonnement",
    cancel_subscription_warning: "Êtes-vous sûr de vouloir annuler votre abonnement ? Vous perdrez l'accès aux fonctionnalités premium.",
    member_limit_reduced: "Limite de membres réduite à 2",
    confirm_cancellation: "Confirmer l'annulation",
    
    // EmailScanPage - additional strings
    copy_paste_order_email: "Ouvrez votre email de confirmation de commande (Amazon, Fnac, etc.) et copiez tout le contenu",
    click_paste_email: "Cliquez sur \"Coller un email\" et collez le contenu",
    spenspot_auto_detect: "SpenSpot détecte automatiquement le montant et le marchand. Cliquez sur Importer !",
    feature_in_development: "Cette fonctionnalité est en cours de développement. Pour l'instant, utilisez l'option \"Coller un email\".",
    auto_import_forward: "Pour importer automatiquement, transférez vos emails de commande",
    paste_order_email_content: "Copiez-collez le contenu de votre email de commande",
    paste_email_text_html: "Collez ici le contenu de l'email (texte ou HTML)...",
    paste_order_confirmation_instructions: "Collez vos emails de confirmation de commande pour les importer automatiquement",
    
    // RecurringPage - strings
    recurring_expenses: "Dépenses Récurrentes",
    subscriptions_payments: "Abonnements & prélèvements",
    detect: "Détecter",
    patterns_detected_count: "{count} pattern(s) détecté(s)",
    detection_error: "Erreur de détection",
    recurring_confirmed: "Dépense récurrente confirmée",
    confirmation_error: "Erreur de confirmation",
    detected_patterns: "Patterns détectés",
    confirmed_subscriptions: "Abonnements confirmés",
    no_confirmed_subscriptions: "Aucun abonnement confirmé",
    click_detect_analyze: "Cliquez sur \"Détecter\" pour analyser vos transactions",
    spenspot_analyzes_6_months: "SpenSpot analyse vos 6 derniers mois de transactions",
    patterns_auto_detected: "Les patterns récurrents sont détectés automatiquement",
    confirm_subs_for_reminders: "Confirmez vos abonnements pour recevoir des rappels",
    confirm_recurring_expense: "Confirmer cette dépense récurrente ?",
    occurrences: "Occurrences",
    next_expected_date: "Prochaine date prévue",
    in_days: "Dans",
    monthly_freq: "Mensuel",
    weekly_freq: "Hebdo",
    yearly_freq: "Annuel",
    
    // ShoppingPage
    search_error: "Erreur de recherche",
    smart_shopping: "Shopping Intelligent",
    price_comparison: "Comparateur de prix",
    search_product: "Rechercher un produit...",
    compare: "Comparer",
    results_for_query: "Résultats pour",
    not_enough_data_scan: "Pas assez de données. Scannez plus de tickets pour comparer les prix !",
    price_statistics: "Statistiques de prix",
    suggestions_for_you: "Suggestions pour toi",
    best_savings_opportunities: "Meilleures économies possibles",
    no_data_yet: "Pas encore de données",
    scan_receipts_to_compare: "Scannez vos tickets de caisse pour commencer à comparer les prix",
    scan_receipt_ocr: "Scannez vos tickets de caisse avec l'OCR",
    spenspot_analyzes_prices: "SpenSpot analyse et stocke les prix des produits",
    compare_prices_save: "Comparez les prix entre magasins et économisez !",
    
    // ObjectivesPage
    delete_objective_confirm: "Supprimer l'objectif ?",
    objectives_statistics: "Statistiques des Objectifs",
    success_rate: "Taux de réussite",
    pause_behavior: "Comportement de pause",
    total_pauses: "Nombre de pauses",
    days_paused: "Jours en pause",
    objectives_paused_1x: "Objectifs pausés au moins 1x",
    avg_duration_days: "Durée moyenne (jours)",
    most_paused_objective: "Objectif le plus pausé",
    savings_evolution: "Évolution de l'épargne",
    auto_monthly_contribution: "Prélèvement mensuel automatique",
    first_ordinal: "1er",
    action_irreversible: "Cette action est irréversible.",
    of_target: "sur {amount} visés",
    example_vacation: "Ex: Vacances d'été",
    
    // DashboardPage
    no_pocket_money_ask_parents: "Plus d'argent de poche ! Demande à tes parents.",
    i_spent: "J'ai dépensé",
    example_video_game: "Ex: Nouveau jeu vidéo",
    no_objectives: "Aucun objectif",
    create_household: "Créer un foyer",
    manage_finances_together: "Gérez les finances en famille",
    charts: "Graphiques",
    emails: "Emails",
    export: "Export",
    voice_input_label: "Saisie vocale",
    scan_receipt_label: "Scanner ticket",
    add_expense_label: "Ajouter dépense",
    hybrid_label: "Mixte",
    initial_colon: "Solde initial",
    
    // CategoriesPage
    name_required: "Nom requis",
    category_created: "Catégorie créée",
    category_updated: "Catégorie mise à jour",
    delete_category_question: "Supprimer cette catégorie ?",
    category_deleted: "Catégorie supprimée",
    manage_expense_categories: "Gérez vos catégories de dépenses",
    new_label: "Nouvelle",
    no_custom_categories: "Aucune catégorie personnalisée",
    create_category: "Créer une catégorie",
    edit_category: "Modifier la catégorie",
    new_category: "Nouvelle catégorie",
    example_category_vacation: "Ex: Vacances",
    color: "Couleur",
    create_categories_to_organize: "Créez des catégories pour mieux organiser vos dépenses",
    
    // OnboardingPage
    pocket_money_optional: "Argent de poche (optionnel)",
    back_to_home: "Retour à l'accueil",
    track_spending: "Suivi des dépenses",
    challenges: "Défis",
    lets_go: "C'est parti ! 🚀",
    income_label: "Revenu",
    household_label: "Foyer",
    budget_mode_label: "Mode budget",
    
    // FamilyPage
    allow_child_manage_money: "Permettre à l'enfant de gérer son argent",
    continue_btn: "Continuer",
    create_account_btn: "Créer le compte",
    example_name_marie: "Ex: Marie",
    send_invitation: "Envoyer l'invitation",
    code_for: "Code pour",
    email_sent_to: "Email envoyé à",
    settings_updated: "Paramètres mis à jour",
    common_pot_contribution: "Contribution au pot commun",
    contribution_added: "Contribution ajoutée !",
    contribute_common_pot: "Contribuer au Pot Commun",
    description_optional: "Description (optionnel)",
    example_monthly_groceries: "Ex: Courses du mois",
    
    // IncomeAdjustmentModal
    adjustment_added: "Ajustement ajouté",
    adjustment_add_failed: "Erreur lors de l'ajout",
    adjustment_deleted: "Ajustement supprimé",
    adjust_my_income: "Ajuster mon revenu",
    base_salary: "Salaire de base",
    adjustments: "Ajustements",
    effective_income: "Revenu effectif",
    add_adjustment: "Ajouter un ajustement",
    adjustment_type: "Type d'ajustement",
    amount_euro: "Montant (€)",
    this_month_adjustments: "Ajustements ce mois",
    invite_email_auto_sent: "Un email avec le code d'invitation sera envoyé automatiquement",
    current_balance: "Solde actuel",

    // Income Sources (multi-revenus)
    income_sources: "Sources de revenus",
    income_sources_title: "Gérer mes revenus",
    income_sources_desc: "Salaire, aides, indépendant, loyers...",
    add_income_source: "Ajouter une source",
    edit_income_source: "Modifier la source",
    income_type: "Type de revenu",
    income_amount: "Montant",
    income_frequency: "Fréquence",
    income_frequency_monthly: "Mensuel",
    income_frequency_weekly: "Hebdomadaire",
    income_frequency_daily: "Journalier",
    income_frequency_one_time: "Ponctuel",
    income_date: "Date",
    income_start_date: "Date de début (optionnel)",
    income_end_date: "Date de fin (optionnel)",
    income_custom_label: "Étiquette personnalisée (optionnel)",
    income_notes: "Notes (optionnel)",
    income_active: "Source active",
    income_inactive: "Inactive",
    total_monthly_income: "Total des revenus du mois",
    income_source_added: "Source ajoutée",
    income_source_updated: "Source mise à jour",
    income_source_deleted: "Source supprimée",
    income_source_save_failed: "Échec de l'enregistrement",
    income_source_delete_failed: "Échec de la suppression",
    confirm_delete_income_source: "Supprimer cette source de revenus ?",
    no_income_sources: "Aucune source de revenus. Ajoutez votre salaire pour commencer.",
    monthly_contribution: "Contribution ce mois",
    income_breakdown_by_frequency: "Détail par fréquence",
    family_total_income: "Revenus du foyer",
    personal_income: "Revenus personnels",
    quick_add_income: "Saisie rapide revenu",
    quick_add_income_desc: "Ajouter rapidement un revenu du jour",

    // LandingPage - Auth
    quick_secure_login: "Connexion rapide et sécurisée",
    credentials: "Identifiants",
    email_or_username: "Email ou nom d'utilisateur",
    login: "Connexion",
    create_spenspot_account: "Créez votre compte SpenSpot (email obligatoire)",
    enter_email_or_username: "Entrez votre email ou nom d'utilisateur",
    required_password_recovery: "Nécessaire pour récupérer votre mot de passe",
    email_or_username_placeholder: "email@exemple.com ou identifiant",
    already_have_account: "Déjà un compte ? Se connecter",
    no_account_signup: "Pas de compte ? S'inscrire",
    signup: "S'inscrire",
    log_in: "Connexion",
    
    // QuickAddModal
    split_payment: "Paiement mixte",
    add_split_payment: "+ Paiement mixte",
    remaining_amount: "Reste",
    add_payment_method: "Ajouter un moyen de paiement",
    subscription_name: "Nom de l'abonnement",
    subscription_placeholder: "Ex: Netflix, Spotify, Assurance...",
    name_required_subscription: "Le nom est obligatoire pour un abonnement",
    for_label: "Pour",
    adding: "Ajout...",
    create_installment_payment: "Créer paiement en {count}x",
    add_expense: "Ajouter la dépense",
    
    // PredictionsPage
    anticipate_spending: "Anticipez vos dépenses",
    refresh: "Actualiser",
    total_budget: "Budget total",
    predicted_end_month: "Prévu fin de mois",
    daily_budget_remaining: "Budget/jour restant",
    days_remaining_month: "jours restants ce mois",
    urgent_alerts: "Alertes urgentes",
    no_predictions_available: "Aucune prévision disponible",
    create_budget: "Créer un budget",
    
    // ChatPage
    error_loading_conversation: "Erreur lors du chargement",
    conversation_deleted: "Conversation supprimée",
    error_deleting: "Erreur lors de la suppression",
    limited_access: "Accès limité",
    ai_financial_assistant: "Assistant Financier IA",
    analyze_spending: "Analyser mes dépenses",
    suggest_budget: "Suggérer un budget",
    example_questions: "Exemples de questions :",
    thinking: "Réflexion en cours...",
    ask_question_placeholder: "Posez votre question...",
    password_requirements: "Min. 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre",
    create_budgets_to_see_predictions: "Créez des budgets pour voir les prévisions",
    ai_assistant_premium_required: "L'assistant IA est disponible avec les abonnements Standard et Premium.",
    ai_assistant_description: "Posez des questions sur vos finances, demandez des conseils de budget, ou analysez vos dépenses.",
    
    // ExportPage
    tax_export: "Export Fiscal",
    annual_expense_report: "Rapport annuel des dépenses",
    tax_year: "Année fiscale",
    monthly_average: "Moyenne mensuelle",
    for_accountant: "Pour comptable",
    visual_report: "Rapport visuel",
    csv_downloaded: "CSV téléchargé !",
    export_error: "Erreur lors de l'export",
    report_downloaded: "Rapport téléchargé ! Ouvrez-le et imprimez en PDF (Ctrl+P)",
    no_data_for_year: "Aucune donnée pour ",
    add_transactions_for_report: "Ajoutez des transactions pour générer votre rapport fiscal",
    export_disclaimer: "Ce rapport est généré à partir de vos dépenses enregistrées dans SpenSpot. Vérifiez les montants avec vos relevés bancaires avant de les utiliser pour votre déclaration fiscale.",
    
    // FamilyPage additional
    member_removed: "{name} a été retiré du foyer",
    pocket_money_given: "{amount}€ donnés à {name} ! 🎉",
    member_limit_reached: "Limite de {max} membres atteinte",
    subscription_limited: "Votre abonnement {plan} est limité.",
    children_count: "{count} enfant",
    children_count_plural: "{count} enfants",
    join_my_household_text: "Rejoins mon foyer \"{name}\" sur SpenSpot avec le code: {code}",
    child_no_account_warning: "⚠️ Cet enfant n'a pas encore de compte. Les dépenses ne seront pas suivies.",
    delete_family_warning: "Cette action est irréversible. Tous les membres seront retirés du foyer.",
    remove_member_confirm: "Êtes-vous sûr de vouloir retirer {name} du foyer ? Cette action est irréversible.",
    budget_reduction_warning: "⚠️ Le budget total du foyer sera réduit de {amount}/mois",
    transfer_ownership_description: "Choisissez un membre adulte qui deviendra le nouveau propriétaire du foyer. Vous deviendrez un membre standard.",
    no_eligible_members_transfer: "Aucun membre éligible pour le transfert. Invitez d'abord un autre adulte.",
    pocket_money_saved: "Argent de poche mis à jour !",
    allowance_saved: "Allocation mise à jour !",
    give_money_to: "Donner de l'argent à {name}",
    edit_child_pocket_money: "Modifier l'argent de poche de {name}",
    edit_child_allowance: "Modifier l'allocation de {name}",
    monthly_allowance: "Allocation mensuelle",
    no_allowance_set: "Aucune allocation définie",
    allowance_frequency: "Fréquence d'allocation",
    username_placeholder_fr: "emma_dupont",
    username_placeholder_en: "emma_smith",
    money_from_shared_budget: "L'argent sera retiré du budget partagé du foyer",
    money_from_personal_balance: "L'argent sera retiré de votre solde personnel",
    child_account_created: "Compte enfant créé ! 🎉",
    invite_sent: "Invitation envoyée !",
    money_from_shared_or_personal: "L'argent sera prélevé du budget partagé ou de votre solde personnel selon votre choix",
    no_children_with_pocket_money: "Aucun enfant avec argent de poche activé",
    salary_optional: "Salaire (optionnel)",
    common_pot_balance: "Solde pot commun: {amount}",
    money_deducted_personal: "L'argent sera déduit de votre solde personnel",
    manage_pocket_money_for: "Gérer l'argent de poche de {name}",
    monthly_auto_amount_desc: "Montant automatique versé chaque mois (laisser vide pour désactiver)",
    create_family_description: "Créez un foyer pour gérer les finances familiales ensemble",
    enter_invite_code_desc: "Entrez le code d'invitation partagé par le propriétaire du foyer",
    invite_code_validity: "Le code commence par INV- et est valable 7 jours",
    invitation_sent_to_email: "Invitation envoyée à {email}. L'adulte doit créer son compte.",
    account_created_login: "Compte créé ! {name} peut se connecter avec \"{username}\"",
    add_child_or_adult: "Ajoutez un enfant ou un adulte",
    how_will_login: "Comment {name} va-t-il se connecter ?",
    invitation_will_be_sent: "Une invitation sera envoyée pour créer son compte",
    pocket_money_description: "Activez si cet enfant reçoit de l'argent de poche et peut suivre ses dépenses",
    initial_balance_child: "Solde initial (0€ par défaut)",
    initial_balance_child_desc: "Montant déjà épargné par l'enfant",
    allowance_schedule_desc: "L'argent sera versé automatiquement selon le calendrier",
    create_child_account_title: "Créer un compte enfant ?",
    create_child_account_desc: "{name} pourra se connecter pour voir son solde et ses dépenses",
    skip_for_now: "Passer pour l'instant",
    create_child_account: "Créer le compte",
    username_simple_desc: "Simple à retenir - pas d'email requis",
    child_password_desc: "Un mot de passe facile à retenir pour l'enfant",
    invite_code_copied: "Code d'invitation copié !",
    how_to_create_account: "Comment voulez-vous créer le compte ?",
    invitation_will_be_sent_child: "Une invitation sera envoyée. L'enfant pourra créer son mot de passe.",
    username_requirements: "Lettres, chiffres et _ uniquement. Min 3 caractères.",
    invitation_sent_by_email: "Invitation envoyée par email !",
    code_created_share_manually: "Code créé ! Partagez-le manuellement.",
    upgrade_for_more_members: "Passez à {plan} pour inviter jusqu'à {limit} membres.",
    unlimited_members: "un nombre illimité de",
    max_limit_reached: "Vous avez atteint la limite maximale de votre abonnement.",
    code_single_use_warning: "⚠️ Ce code est à usage unique et expire dans 7 jours",
    only_owner_can_modify: "Seul le propriétaire peut modifier les paramètres du foyer.",
    
    // OnboardingPage
    welcome_exclaim: "Bienvenue ! 🎉",
    already_member_of: "Tu es déjà membre de ce foyer !",
    already_in_family: "Tu fais déjà partie d'un foyer.",
    join_new_family: "Rejoindre ce nouveau foyer",
    enter_your_salary: "Entre ton salaire (modifiable plus tard)",
    join_this_family: "Rejoindre ce foyer",
    continue_without_family: "Continuer sans foyer",
    cant_find_code: "Tu n'as pas de code ?",
    i_have_code: "J'ai un code d'invitation",
    
    // TransactionsPage
    delete_all_installments: "Supprimer toutes les mensualités de \"{description}\" ?",
    installments_deleted: "Paiements échelonnés supprimés",
    delete_transaction_confirm: "Supprimer cette transaction ?",
    search_placeholder: "Rechercher...",
    common_transactions: "Communes",
    personal_transactions: "Personnelles",
    active_installments: "Paiements échelonnés en cours",
    total_label: "Total :",
    monthly_label: "Mensualité :",
    no_transactions_period: "Aucune transaction pour cette période",
    start_tracking_expenses: "Commence à tracker tes dépenses !",
    setup_profile_desc: "En moins de 3 minutes, configurons ton profil pour des conseils personnalisés.",
    enter_name_and_code: "Entrez votre prénom et le code d'invitation reçu",
    max_member_limit_reached: "La limite maximale de membres a été atteinte.",
    ask_parents_upgrade: "Demande à tes parents de passer à l'abonnement {plan} pour que tu puisses rejoindre le foyer.",
    welcome_name: "Bienvenue {name} !",
    now_part_of_household: "Tu fais maintenant partie du foyer \"{name}\"",
    parents_can_give_pocket_money: "Tes parents pourront te donner de l'argent de poche que tu pourras gérer ici !",
    
    // ImportCSVModal
    import_csv_file: "Importer un fichier CSV",
    import_csv_desc: "Importez votre historique bancaire depuis un fichier CSV ou Excel exporté",
    click_to_choose_file: "Cliquez pour choisir un fichier",
    export_csv_tip: "Exportez vos relevés depuis votre banque en ligne au format CSV",
    map_columns: "Associez les colonnes :",
    preview_first_rows: "Aperçu (3 premières lignes) :",
    importing: "Import en cours...",
    import_complete: "Import terminé !",
    import_rows: "Importer {count} lignes",
    transactions_imported: "{count} transaction(s) importée(s)",
    
    // AlertsPanel
    alerts: "Alertes",
    other_info: "Autres informations",
    amount_transferred_from: "{amount}€ transférés de \"{name}\" vers votre solde",
    current_balance_warning: "Votre solde actuel est de {balance}. Vous pouvez transférer de l'argent de vos objectifs.",
    no_objectives_with_savings: "Aucun objectif avec des économies",
    amount_to_transfer: "Montant à transférer",
    max_amount: "Max:",
    
    // VoiceInputModal
    voice_input: "Saisie Vocale",
    voice_input_example: "Dites votre dépense, par exemple: \"25 euros au restaurant\"",
    browser_no_speech: "Votre navigateur ne supporte pas la reconnaissance vocale",
    no_speech_detected: "Aucune voix détectée",
    tap_to_speak: "Appuyez pour parler",
    error_adding: "Erreur lors de l'ajout",
    expense_description_placeholder: "Description de la dépense",
    which_category: "Quelle catégorie ?",
    
    // AI Predictions Explanation
    how_predictions_calculated: "Comment sont calculées les prévisions ?",
    daily_rate: "Rythme journalier",
    daily_rate_explanation: "Dépenses du mois ÷ Jours écoulés = Moyenne par jour",
    end_month_forecast: "Prévision fin de mois",
    end_month_forecast_explanation: "Dépenses actuelles + (Rythme journalier × Jours restants)",
    recommended_daily_budget: "Budget journalier conseillé",
    recommended_daily_budget_explanation: "(Budget total - Dépenses actuelles) ÷ Jours restants",
    what_is_excluded: "Ce qui est exclu",
    what_is_excluded_explanation: "Les revenus et l'épargne ne sont pas comptés comme des dépenses dans ces calculs.",
    predictions_tip: "Les prévisions sont basées sur votre rythme de dépenses actuel ce mois-ci. Plus vous ajoutez de transactions, plus elles sont précises.",
    current_rate: "Rythme actuel",
    per_day: "/jour",
    
    // OCRScannerModal
    of_receipt: "Du ticket de caisse",
    select_image: "Sélectionner une image",
    image_formats: "JPG, PNG ou HEIC",
    choose_from_gallery: "Choisir depuis la galerie",
    installment_created: "Paiement en {count}x créé !",
    expense_added_exclaim: "Dépense ajoutée !",
    prices_stored: "Prix enregistrés.",
    invoice_saved: "Facture sauvegardée.",
    error_creating: "Erreur lors de la création",
    
    // SubscriptionsPage
    first_of_month: "1er",
    of_month: " du mois",
    subscription_description_placeholder: "Ex: Plan familial",
    delete_subscription_confirm: "Supprimer l'abonnement ?",
    subscription_will_be_deleted: "Cet abonnement sera supprimé définitivement.",
    
    // InvoicesPage
    file_type_not_allowed: "Type de fichier non autorisé",
    info_extracted_auto: "Informations extraites automatiquement !",
    fill_info_manually: "Remplissez les informations manuellement",
    delete_invoice_confirm: "Supprimer cette facture ?",
    analyzing: "Analyse en cours...",
    info_auto_extracted: "Informations extraites automatiquement",
    optional_notes_placeholder: "Notes optionnelles...",
    
    // ObjectivesPage
    objective_achieved: "Objectif atteint ! 🎉",
    objective_saving_recorded: "Économie enregistrée !",
    objective_paused: "Objectif mis en pause",
    objective_resumed: "Objectif repris !",
    contribution_added: "Contribution ajoutée !",
    suggested_contribution_applied: "Contribution suggérée: {amount}€/mois appliquée",
    auto_contribute_info: "Prélèvement auto: {amount}/mois le {day}",
    monthly_to_reach_goal: "{amount}/mois pour atteindre votre objectif",
    auto_contribute_enabled: "Prélèvement automatique activé !",
    
    // ReferralPage
    share_referral_code: "Rejoins SpenSpot avec mon code {code} et on gagne tous les deux 1 mois Premium ! 🎁",
    referral_link_copied: "Lien copié !",
    how_it_works: "Comment ça marche ?",
    share_your_code: "Partage ton code",
    share_your_code_desc: "Envoie ton code à tes amis",
    they_sign_up: "Ils s'inscrivent",
    they_sign_up_desc: "Ils utilisent ton code pour créer un compte",
    both_win_desc: "Tu gagnes 50 XP et eux commencent avec un bonus !",
    thanks_to: "Merci à {name} !",
    
    // InvitationPage
    invalid_invitation: "Invitation invalide",
    back_to_home: "Retour à l'accueil",
    account_created_exclaim: "Compte créé !",
    welcome_to_spenspot: "Bienvenue sur SpenSpot",
    role_label: "Rôle :",
    full_name: "Nom complet",
    create_my_account: "Créer mon compte",
    
    // BudgetsPage
    over_by: "Dépassé de",
    delete_budget_confirm: "Supprimer ce budget ?",
    choose_category: "Choisir une catégorie",
    total_budget_month: "Budget total ce mois",
    common_budgets: "Budgets Communs",
    personal_budgets: "Budgets Personnels",
    no_budget_category: "Pas encore de budget pour cette catégorie",
    suggested_budget: "Budget suggéré (50/30/20)",
    no_budgets_yet: "Pas encore de budgets définis",
    no_budgets_explanation: "Les budgets vous aident à contrôler vos dépenses par catégorie. Définissez des limites mensuelles pour chaque catégorie et suivez votre progression.",
    add_first_budget: "Ajouter mon premier budget",
    
    // NotificationSettings
    notifications_enabled: "Notifications activées !",
    notifications_permission_denied: "Permission refusée. Activez les notifications dans les paramètres de votre navigateur.",
    notifications_error: "Erreur lors de l'activation des notifications",
    notifications_disabled: "Notifications désactivées",
    test_notification_sent: "Notification de test envoyée !",
    test_notification_failed: "Impossible d'envoyer la notification",
    notifications_not_supported: "Notifications non supportées",
    browser_no_push_support: "Ton navigateur ne supporte pas les notifications push.",
    reminder_same_day: "Rappel le jour même",
    notification_on_debit_day: "Notification le jour du prélèvement",
    paused_objectives_reminders: "Rappels Objectifs en Pause",
    monthly_reminder: "Rappel mensuel",
    monthly_reminder_desc: "Notification au début du mois pour les objectifs en pause",
    show_until_day: "Afficher jusqu'au jour",
    show_reminder_until_desc: "Afficher le rappel du 1er jusqu'à ce jour du mois",
    realtime: "Temps réel",
    realtime_desc: "Notification immédiate dès qu'un seuil est atteint",
    daily_summary: "Résumé quotidien",
    daily_summary_desc: "Une seule notification par jour avec toutes les alertes",
    summary_time: "Heure du résumé :",
    send_test_notification: "Envoyer une notification de test",
    
    // NegativeBalanceModal
    previous_month_negative_balance: "Solde négatif du mois précédent",
    balance_ended_negative: "Votre solde de {month} s'est terminé en négatif.",
    overdraft_to_cover: "Découvert à combler",
    already_repaid: "Déjà remboursé",
    month_concerned: "Mois concerné :",
    deduct_from_balance_question: "Voulez-vous déduire ce montant de votre solde actuel ?",
    amount_to_deduct: "Montant à déduire",
    maximum: "Maximum :",
    ignore: "Ignorer",
    partial_amount: "Montant partiel",
    deduct_all: "Déduire tout",
    negative_balance_ignored: "Solde négatif ignoré",
    
    // InstallPWAPrompt
    install_spenspot: "Installer SpenSpot",
    quick_access_home_screen: "Accès rapide depuis ton écran",
    ios_install_instructions: "Pour installer SpenSpot sur ton iPhone/iPad :",
    tap_share: "1. Appuie sur ",
    share: "Partager",
    select_add_home: "2. Sélectionne \"Sur l'écran d'accueil\"",
    install_for_offline: "Installe SpenSpot pour un accès instantané, même hors ligne !",
    install: "Installer",
    faster: "Plus rapide",
    offline: "Hors ligne",
    app_installed: "App installée",
    install_app: "Installer l'app",
    ios_safari_instructions: "Utilise Safari → Partager → \"Sur l'écran d'accueil\"",
    
    // Contact Support
    contact_support: "Contacter le support",
    contact_support_desc: "Envoyez-nous un message, nous vous répondrons rapidement.",
    contact_support_short: "Problème, suggestion, question",
    contact_type: "Type de demande",
    problem: "Problème",
    suggestion: "Suggestion",
    question_label: "Question",
    subject: "Sujet",
    message: "Message",
    contact_subject_placeholder: "Ex: Problème de connexion",
    contact_message_placeholder: "Décrivez votre demande en détail...",
    contact_sent_as: "Envoyé en tant que",
    reply_to: "Réponse à",
    sending: "Envoi...",
    contact_sent_success: "Message envoyé ! Nous vous répondrons rapidement.",
    contact_send_error: "Erreur lors de l'envoi du message",
    message_required: "Le message est requis",
    message_too_short: "Le message doit contenir au moins 10 caractères",
    
    // Missing translations - Added Feb 2026
    checking_quota: "Vérification de votre quota...",
    limit_reached: "Limite atteinte",
    ocr_not_available: "Le scan OCR n'est pas disponible avec votre abonnement gratuit.",
    scans_used: "Vous avez utilisé {used}/{limit} scans ce mois-ci.",
    upgrade_for_ocr: "Passez à un abonnement supérieur pour obtenir plus de scans OCR.",
    view_subscriptions_btn: "Voir les abonnements",
    select_category: "Sélectionnez une catégorie",
    error_updating: "Erreur lors de la modification",
    edit_transaction: "Modifier la transaction",
    thinking: "Réflexion en cours...",
    invoice_limit: "Limite de factures",
    invoice_storage_limits: "Limites de stockage factures",
    limits_saved: "Limites enregistrées !",
    error_saving: "Erreur lors de la sauvegarde",
    challenge_management: "Gestion des Défis",
    create_manage_challenges: "Créer et gérer les défis quotidiens",
    new_challenge: "Nouveau défi",
    default_challenges: "Défis par défaut",
    custom_challenges: "Défis personnalisés",
    no_custom_challenges: "Aucun défi personnalisé",
    click_new_challenge: "Cliquez sur \"Nouveau défi\" pour en créer un",
    edit_challenge: "Modifier le défi",
    ai_generation: "Génération IA",
    theme_optional: "Thème (optionnel)",
    easy: "Facile",
    medium: "Moyen",
    hard: "Difficile",
    generate: "Générer",
    target_languages: "Langues cibles",
    xp_reward: "Récompense XP",
    target_number: "Objectif (nombre)",
    update: "Mettre à jour",
    create: "Créer",
    financial_analytics: "Analyse Financière",
    year: "Année",
    reset_password: "Réinitialiser le mot de passe",
    password_reset_success: "Mot de passe réinitialisé !",
    back_to_login: "Retour à la connexion",
    email_sent: "Email envoyé !",
    check_email_reset: "Vérifiez votre boîte mail pour le lien de réinitialisation.",
    send_reset_link: "Envoyer le lien",
    monthly_report: "Rapport Mensuel",
    report_opened_print: "Rapport ouvert - utilisez Ctrl+P pour imprimer en PDF",
    remaining: "Restant",
    not_available: "Non disponible",
    share_your_code: "Partage ton code",
    objectives_progress: "Progression des objectifs",
    transactions_this_month: "Transactions ce mois",
    ocr_scans_month: "Scans OCR/mois",
    ai_questions_month: "Questions IA/mois",
    voice_assistant: "Assistant vocal",
    members: "Membres",
    view_objectives: "Voir objectifs",
    view_budgets: "Voir budgets",
    offline_mode: "Mode hors ligne",
    sync: "Sync",
    overdraft_risk: "Risque de Découvert",
    budget_alert: "Alerte Budget",
    objectives_reminder: "Rappel Objectifs",
    sync_success: "Synchronisation",
    // Date picker for transactions
    expense_date: "Date de la dépense",
    date_today: "Aujourd'hui",
    date_warning_past_month: "⚠️ Cette dépense sera enregistrée dans un mois passé. Cela modifiera les rapports et statistiques de ce mois.",
    date_warning_future: "ℹ️ Cette dépense est dans le futur. Elle sera comptabilisée à la date indiquée.",
    date_warning_far_past: "⚠️ Date très ancienne (plus de 6 mois). Êtes-vous sûr ?",
    // Reflexive questions for simulator
    reflexive_section_title: "Réfléchis avant d'acheter",
    reflexive_section_subtitle: "Coche ce qui s'applique (optionnel) — l'IA en tiendra compte",
    reflexive_q_need: "J'en ai vraiment besoin (pas juste envie)",
    reflexive_q_thought: "J'y ai réfléchi (ce n'est pas un achat impulsif)",
    reflexive_q_compared: "J'ai comparé les prix ou cherché des alternatives",
    reflexive_q_aligned: "Ça correspond à mes priorités et valeurs",
    reflexive_q_long_term: "Je vais vraiment l'utiliser sur la durée",
    reflexive_reason_label: "Pourquoi je le veux ? (optionnel)",
    reflexive_reason_placeholder: "Décris en quelques mots la raison de cet achat...",
  },
  en: {
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    close: "Close",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    skip: "Skip",
    finish: "Finish",
    error: "Error",
    success: "Success",
    paused: "Paused",
    active: "Active",
    completed: "Completed",
    total: "Total",
    days: "days",
    weeks: "weeks",
    months: "months",
    years: "years",
    all: "All",
    filter: "Filter",
    search: "Search",
    clear: "Clear",
    no_results: "No results",
    
    // Form fields
    amount: "Amount",
    category: "Category",
    categories: "Categories",
    name: "Name",
    description: "Description",
    date: "Date",
    type: "Type",
    status: "Status",
    optional: "optional",
    required: "required",
    select: "Select...",
    password: "Password",
    confirm_password: "Confirm password",
    new_password: "New password",
    passwords_dont_match: "Passwords do not match",
    your_name: "Your name",
    situation: "Situation",
    budget_type: "Budget type",
    
    // Messages
    loading_error: "Loading error",
    invalid_amount: "Invalid amount",
    failed_to_delete: "Failed to delete",
    fill_all_fields: "Fill in all fields",
    redirecting_login: "Redirecting to login...",
    no_data: "No data",
    
    // Dashboard/Stats
    total_spent: "Total spent",
    top_category: "Top category",
    breakdown_by_category: "Breakdown by category",
    common_pot: "Common pot",
    ai_predictions: "AI Predictions",
    view_subscriptions: "View subscriptions",
    
    // Actions
    later: "Later",
    how_it_works: "💡 How does it work?",
    forward_receipts: "Forward your receipts",
    transcript: "Transcript:",
    installment_payment: "Installment payment",
    reached: "reached",
    
    // More common
    notes: "Notes",
    icon: "Icon",
    frequency: "Frequency",
    expenses: "Expenses",
    expense_added: "Expense added!",
    completed_plural: "Completed",
    avg_per_day: "Avg/day",
    monthly_amount: "Monthly amount",
    budget_management_mode: "Budget management mode",
    min_8_chars: "Minimum 8 characters",
    password_min_8: "Password must be at least 8 characters",
    payment_day: "Payment day",
    create_account: "Create account",
    forgot_password: "Forgot password?",
    failed_to_save: "Failed to save",
    failed_to_load: "Failed to load",
    example_grocery: "Ex: Grocery shopping",
    example_city: "Ex: Paris, Lyon, Marseille...",
    
    // UI labels
    invalid_code: "Invalid code",
    change_plan: "Change plan",
    no_budgets_configured: "No budgets configured",
    ai_assistant: "AI Assistant",
    enable: "Enable",
    disable: "Disable",
    take_photo: "📷 Take a photo",
    category_details: "📋 Category details",
    default_categories: "📋 Default categories",
    breakdown_by_category: "📊 Breakdown by category",
    predictions_by_category: "📊 Predictions by category",
    spending_trend: "📉 Spending trend",
    monthly_evolution: "📈 Monthly evolution",
    budget_progress: "💰 Budget progress",
    shared_budget: "💰 Shared budget",
    shared_family_budget: "💰 Shared family budget",
    hybrid_budget: "🔄 Hybrid budget",
    how_to_import: "💡 How to import receipts?",
    your_personal_balance: "👤 Your personal balance",
    
    // More UI labels
    separate_budgets: "👤 Separate budgets",
    common_pot_label: "🏦 Common pot",
    my_categories: "✨ My categories",
    complete: "✓ Complete",
    invitation_created: "✅ Invitation created",
    limit_reached: "⚠️ Limit reached",
    failed: "failed",
    saved: "Saved",
    total_savings: "Total savings",
    listening: "Listening...",
    potential_savings: "Potential savings",
    available_savings: "Available savings",
    deadline_passed: "Deadline passed",
    used: "used",
    budget_status: "Budget status",
    payment_history: "Payment history",
    total_objectives: "Total objectives",
    upcoming_payments: "Upcoming payments",
    scan_receipt: "Scan receipt",
    
    // OCR Scanner
    image_too_large: "Image too large (max 10MB)",
    could_not_read_receipt: "Could not read receipt",
    scan_error: "Scan error",
    prices_stored: "Prices stored.",
    invoice_saved: "Invoice saved.",
    error_creating: "Error creating transaction",
    of_receipt: "Of the receipt",
    select_image: "Select an image",
    jpg_png_heic: "JPG, PNG or HEIC",
    choose_from_gallery: "Choose from gallery",
    analyzing_receipt: "Analyzing receipt...",
    claude_extracting: "AI is extracting data",
    use_today_date: "Use today's date",
    keep_receipt_date: "Keep receipt date",
    merchant: "Merchant",
    merchant_name: "Merchant name",
    share_prices: "Share prices",
    save_as_invoice: "Save as invoice",
    retry: "Retry",
    create_installments: "Create {count}x",
    receipt_date_warning: "This receipt is dated {date}. If you save it with this date, it won't be counted in your current month's balance.",
    which_date_to_use: "Which date would you like to use?",
    counted_in_current_month: "Counted in current month",
    keep_original_date: "Keep original receipt date",
    not_counted_in_balance: "Won't be counted in your current balance",
    products_detected_community: "product(s) detected - help the community!",
    keep_receipt_warranties: "Keep this receipt for your warranties",
    
    // Quick Add Modal
    split_over_months: "Split over multiple months",
    number_of_installments: "Number of installments",
    preview: "Preview:",
    recurring_subscription: "Recurring subscription",
    auto_charged_monthly: "Automatically charged each month",
    select_day: "Select day",
    first_day: "1st",
    day_of_month: " of month",
    summary_label: "Summary:",
    split_payment: "+ Split payment",
    split_payment_label: "Split payment",
    remaining_amount: "Remaining: €{amount}",
    add_payment_method: "Add payment method",
    subscription_name: "Subscription name",
    subscription_placeholder: "Ex: Netflix, Spotify, Insurance...",
    name_required_subscription: "Name is required for a subscription",
    for_label: "For",
    adding: "Adding...",
    create_installment_payment: "Create {count}x payment",
    add_expense: "Add expense",
    charged_on_day: "Charged on the {day} of each month",
    
    // Email Scan Page
    paste_email_content: "Paste the email content",
    receipt_detected: "Receipt detected!",
    no_receipt_detected: "No receipt detected",
    analysis_error: "Analysis error",
    imported_as_transaction: "Imported as transaction!",
    email_scan: "Email Scan",
    auto_import_receipts: "Auto-import receipts",
    paste_confirmation_email: "Paste a confirmation email",
    receipts_to_import: "Receipts to import",
    import: "Import",
    imported_receipts: "Imported receipts",
    
    // Bank Import
    import_bank_statement: "Import bank statement",
    import_bank_desc: "Import your expenses from a PDF or Excel file",
    import_preview_desc: "Review and select transactions to import",
    supported_formats: "Supported formats",
    drop_file_here: "Drop your file here",
    select_file: "Select a file",
    change_file: "Change file",
    target_month: "Target month",
    current_month: "Current month",
    previous_month: "Previous month",
    analyzing: "Analyzing...",
    new: "New",
    possible_duplicates: "Possible duplicates",
    duplicates: "Duplicates",
    selected: "selected",
    select_all: "All",
    select_new_only: "New only",
    importing: "Importing...",
    transactions_imported: "transactions imported",
    no_transactions_selected: "No transaction selected",
    import_preview_error: "Error analyzing file",
    import_error: "Import error",
    invalid_file_format: "Invalid file format. Use PDF or Excel.",
    
    no_email_receipts: "No email receipts",
    copy_email: "Copy the email",
    paste_here: "Paste here",
    paste_email: "Paste an email",
    subject_optional: "Subject (optional)",
    subject_placeholder: "Ex: Amazon Order Confirmation",
    from_optional: "From (optional)",
    email_content_required: "Email content *",
    analyze: "Analyze",
    
    login: "Log in",
    connection: "Connection",
    paste_confirmation_email: "Paste confirmation email",
    quick_access: "Quick access from your screen",
    install_app: "Install app",
    adjust_income: "Adjust income",
    adjust_income_desc: "Bonuses, overtime, leave...",
    description_optional: "Description (optional)",
    email_or_username: "Email or username",
    of_month: "of month",
    
    // More contextual
    active_users: "active users",
    urgent: "urgent",
    transactions_label: "transactions",
    transaction_count: "transaction(s)",
    success_label: "successful",
    result_count: "result(s)",
    remaining: "remaining",
    to_add_expense: "to add an expense",
    paid: "paid",
    pauses: "pauses",
    rows_detected: "rows detected",
    detected: "detected",
    from_common_pot: "from common pot",
    from_your_budget: "from your budget",
    confidence: "confidence",
    purchase_count: "purchase(s)",
    active_subscriptions: "active subscriptions",
    invited_to_join: "You are invited to join the household",
    you_will_lose: "You will lose:",
    you_both_win: "You both win",
    you_set_password: "You set the password",
    invited_with_subscription: "You have been invited with a subscription",
    you_left_household: "You left the household",
    already_have_account: "Already have an account?",
    your_city: "Your city",
    your_monthly_income: "Your monthly net income",
    your_first_name: "Your first name",
    your_plan_includes: "Your plan includes:",
    visualize_spending: "Visualize your spending",
    city: "City",
    please_select_image: "Please select an image",
    enter_name_and_code: "Please enter your name and the code",
    enter_valid_amount: "Please enter a valid amount",
    validate_code: "Validate code",
    use_my_savings: "Use my savings",
    budget_usage: "Budget usage",
    an_error_occurred: "An error occurred",
    you_receive: "You receive",
    quarter: "Quarter",
    last_month: "Last month",
    last_quarter: "Last quarter",
    forward_to: "Forward to:",
    transfer: "Transfer",
    transfer_to_balance: "Transfer to current balance",
    unlimited_transactions: "Unlimited transactions",
    transaction_updated: "Transaction updated!",
    transaction_added: "Transaction added!",
    all_categories: "All categories",
    all_set: "All set!",
    everything_you_need: "Everything you need",
    all_rights_reserved: "All rights reserved.",
    total_target: "Total target",
    monthly_total: "Monthly total",
    monthly_recurring_total: "Monthly recurring total",
    total_expenses: "Total expenses",
    receipt_scanned: "Receipt scanned successfully!",
    receipt_previous_month: "Receipt from previous month",
    your_referrals: "Your referrals",
    switch_store_savings: "per month by switching stores",
    total_received: "Total received",
    
    // Landing
    hero_title: "Your buddy who gets money",
    hero_subtitle: "Without judging you.",
    hero_cta: "Start for free",
    hero_login: "Log in",
    feature1_title: "1 second to track",
    feature1_desc: "Add expenses in a snap",
    feature2_title: "Actionable advice",
    feature2_desc: "Personalized tips, not generic",
    feature3_title: "Reach your goals",
    feature3_desc: "3x faster than other apps",
    
    // Auth & Messages
    login_with_google: "Continue with Google",
    logout: "Log out",
    login_success: "Login successful!",
    invalid_credentials: "Invalid credentials",
    invalid_email: "Invalid email",
    password_8_chars: "At least 8 characters",
    password_uppercase: "At least one uppercase letter",
    password_lowercase: "At least one lowercase letter",
    password_digit: "At least one digit",
    password_too_weak: "Password too weak:",
    account_created: "Account created! Logging in...",
    registration_error: "Registration error",
    
    // Landing features
    fast: "Fast",
    fast_desc: "1 second to add an expense",
    reach_goals: "Reach your financial goals",
    insights: "Insights",
    understand_habits: "Understand your habits",
    secure: "Secure",
    encrypted_data: "Encrypted data",
    ai: "AI",
    personalized_advice: "Personalized advice",
    manage_together: "Manage together",
    goals_achieved: "of goals achieved",
    ready_to_start: "Ready to start?",
    free_no_commitment: "Free • No commitment • GDPR compliant",
    hero_main_title_fr: "Your buddy who gets",
    hero_main_title_highlight: "money",
    hero_main_desc: "Manage your finances in 1 second. Personalized AI advice. Advanced family mode.",
    join_thousands_users: "Join thousands of users who have taken control of their finances",
    
    // Onboarding
    onboarding_welcome: "Welcome to SpenSpot!",
    onboarding_salary: "What's your monthly net salary?",
    onboarding_city: "Which city do you live in?",
    onboarding_situation: "What's your situation?",
    onboarding_personality: "Choose your AI advisor",
    onboarding_budget: "Here's your suggested budget",
    situation_single: "Single",
    situation_couple: "Couple",
    situation_family: "Family with kids",
    
    // Onboarding - Join household
    have_invite_code: "I have an invite code",
    create_own_account: "Create my own account",
    join_household: "Join a Household",
    family_joined: "Family joined!",
    monthly_net_income: "Monthly net income",
    household_composition: "Household composition",
    add_household_members: "Add your household members",
    household_name: "Household name",
    first_name: "First name",
    cannot_join: "Cannot join",
    accept_and_join: "I accept and join",
    welcome_to_household: "Welcome to the household!",
    invitation_code: "Invitation code",
    invite_code_info: "The code starts with INV- and is valid for 7 days",
    example_name: "Ex: Mary",
    join_family_name: "Join",
    created_by_members: "Created by {owner} • {count} member(s)",
    example_household_name: "Ex: The Smiths",
    email_for_invitation: "Email (for invitation)",
    pocket_money_optional: "Pocket money (optional)",
    add_member: "Add member",
    summary: "Summary",
    how_manage_finances: "How do you want to manage household finances?",
    common_pot_config: "Common Pot Configuration",
    my_monthly_contribution: "My monthly contribution",
    member_contribution: "{name}'s contribution",
    total_monthly_pot: "Total monthly common pot",
    oops_cannot_join: "Oops, cannot join!",
    back_to_home: "Back to home",
    track_spending: "Track spending",
    challenges: "Challenges",
    lets_go: "Let's go! 🚀",
    income_label: "Income",
    household_label: "Household",
    budget_mode_label: "Budget mode",
    complete_profile_info: "Complete your information to finalize your profile",
    person_enter_salary_signup: "This person will enter their salary when they sign up",
    adults_children_count: "{adults} adult(s), {children} child(ren)",
    adult_set_salary_invite: "Each adult will set their salary when they sign up via the invite code",
    month: "month",
    member: "Member",
    
    // Dashboard
    dashboard: "Dashboard",
    available_balance: "Available balance",
    this_month: "This month",
    budget_progress: "Budget progress",
    recent_transactions: "Recent transactions",
    view_all: "View all",
    no_transactions: "No transactions",
    
    // Transactions
    transactions: "Transactions",
    add_expense: "Add expense",
    amount: "Amount",
    category: "Category",
    description: "Description",
    date: "Date",
    payment_method: "Payment method",
    
    // Categories
    cat_logement: "Housing",
    cat_transport: "Transport",
    cat_alimentation: "Food",
    cat_abonnements: "Subscriptions",
    cat_shopping: "Shopping",
    cat_loisirs: "Entertainment",
    cat_sante: "Health",
    cat_epargne: "Savings",
    cat_investissements: "Investments",
    cat_education: "Education",
    cat_animaux: "Pets",
    cat_enfants: "Kids",
    cat_cadeaux: "Gifts",
    cat_entretien: "Maintenance",
    cat_professionnel: "Professional",
    cat_divers: "Other",
    cat_personnalise: "Custom",
    cat_argent_de_poche: "Pocket money",
    cat_autre: "Other",
    cat_restaurant: "Restaurant",
    cat_food: "Food",
    cat_voyage: "Travel",
    cat_voyages: "Travel",
    cat_vetements: "Clothing",
    cat_beauté: "Beauty",
    cat_beaute: "Beauty",
    cat_carburant: "Fuel",
    cat_telephone: "Phone",
    cat_internet: "Internet",
    cat_assurance: "Insurance",
    cat_factures: "Bills",
    edit_budget: "Edit budget",
    save_changes: "Save changes",
    
    // Budget
    budgets: "Budgets",
    set_budget: "Set budget",
    budget_remaining: "Remaining",
    budget_spent: "Spent",
    over_budget: "Over budget",
    
    // Objectives
    objectives: "Objectives",
    create_objective: "Create objective",
    objective_name: "Objective name",
    target_amount: "Target amount",
    target_date: "Target date",
    days_remaining: "days left",
    monthly_required: "€/month required",
    add_contribution: "Add contribution",
    
    // Challenges
    daily_challenge: "Daily challenge",
    challenge_completed: "Challenge completed!",
    xp_gained: "XP gained",
    streak: "Streak",
    level: "Level",
    
    // AI
    ai_advice: "AI Advice",
    get_advice: "Get advice",
    ai_analyzing: "SpenSpot is analyzing your finances...",
    
    // Settings
    settings: "Settings",
    profile: "Profile",
    language: "Language",
    theme: "Theme",
    notifications: "Notifications",
    privacy: "Privacy",
    help: "Help",
    
    // Payment methods
    payment_card: "Card",
    payment_cash: "Cash",
    payment_transfer: "Transfer",
    payment_other: "Other",
    
    // Referral
    referral: "Referral",
    referral_title: "Invite your friends!",
    referral_subtitle: "You both get 1 free month of Premium",
    your_code: "Your code",
    copy_code: "Copy",
    copied: "Copied!",
    share: "Share",
    friends_invited: "Friends invited",
    premium_earned: "Premium months earned",
    enter_referral_code: "Enter a referral code",
    apply_code: "Apply",
    referral_success: "Code applied! +1 month Premium",
    leaderboard: "Top referrers",
    
    // OCR
    scan_receipt: "Scan receipt",
    take_photo: "Take a photo",
    select_image: "or select an image",
    analyzing: "Analyzing receipt...",
    merchant: "Merchant",
    
    // Alerts  
    alerts: "Alerts",
    budget_exceeded: "Budget exceeded",
    budget_warning: "Budget warning",
    overdraft_warning: "Overdraft risk",
    daily_budget_tip: "Daily budget",
    
    // Notification Settings
    preferences_saved: "Preferences saved",
    push_notifications: "Push Notifications",
    enabled: "Enabled",
    blocked_by_browser: "Blocked by browser",
    not_enabled: "Not enabled",
    budget_alerts: "Budget Alerts",
    alert_at_80: "80% Alert",
    alert_80_desc: "Notification when 80% of budget is used",
    alert_at_100: "100% Alert (exceeded)",
    alert_100_desc: "Notification when budget is exceeded",
    custom_threshold: "Custom threshold",
    custom_threshold_desc: "Set your own alert threshold",
    balance_alerts: "Balance Alerts",
    balance_alerts_enabled: "Balance alerts enabled",
    balance_alerts_desc: "Notification when balance is low",
    alert_threshold: "Alert threshold",
    alert_threshold_desc: "Alert when balance is below this amount",
    zero_balance_alert: "Zero balance alert",
    zero_balance_alert_desc: "Notification when balance reaches zero",
    overdraft_alert: "Overdraft alert",
    overdraft_alert_desc: "Notification when balance is negative",
    overdraft_prediction: "Overdraft prediction",
    overdraft_prediction_desc: "Notification if overdraft is predicted",
    propose_using_savings: "Propose using savings",
    propose_using_savings_desc: "Propose using your objectives if balance is low",
    installment_reminders: "Installment Reminders",
    reminder_x_days_before: "Reminder X days before",
    reminder_before_payment: "Notification before payment date",
    reminder_on_payment_day: "Reminder on payment day",
    paused_objectives_reminders: "Paused Objectives Reminders",
    monthly_reminder: "Monthly reminder",
    show_until_day: "Show until day",
    real_time: "Real-time",
    daily_summary: "Daily summary",
    summary_time: "Summary time:",
    send_test_notification: "Send test notification",
    unblock_in_browser: "Unblock in browser settings",
    
    // Family
    family: "Family",
    family_title: "My Household",
    create_family: "Create household",
    family_name: "Household name",
    family_created: "Household created!",
    join_family: "Join a household",
    leave_family: "Leave household",
    delete_family: "Delete household",
    invite_member: "Invite member",
    add_member: "Add member",
    add_child: "Add child",
    member_name: "Name",
    member_role: "Role",
    member_salary: "Monthly income",
    employment_status: "Employment status",
    status_employed: "Employed",
    status_freelance: "Self-employed",
    status_student: "Student",
    status_unemployed: "Unemployed",
    status_other: "Other",
    no_fixed_salary: "No fixed salary",
    variable_income_hint: "Variable income — manage in 'My income'",
    fixed_salary_question: "Do you have a fixed salary?",
    variable_income_nudge_title: "Your current income",
    variable_income_nudge_desc: "You don't have a fixed salary — remember to log your income when you receive it for accurate projections.",
    log_income_now: "Log income",
    manage_income_sources: "Manage my income",
    projected_this_month: "Projected this month",
    income_evolution_title: "My income evolution",
    income_evolution_subtitle: "Last {n} months",
    income_evolution_empty: "No income logged yet. Start logging to track evolution.",
    fixed_income_label: "Fixed",
    variable_income_label: "Variable",
    view_variable_income_details: "View variable income details",
    variable_income_breakdown_title: "Variable income details",
    variable_income_breakdown_desc: "List of non-recurring incomes logged this month",
    variable_income_breakdown_empty: "No variable income logged this month.",
    total_variable_income: "Total variable income",
    details: "Details",
    role_owner: "Owner",
    role_adult: "Adult",
    role_child: "Child",
    family_code: "Invite code",
    enter_family_code: "Enter family code",
    total_income: "Total household income",
    family_balance: "Family balance",
    spending_by_member: "Spending by member",
    budget_mode: "Budget mode",
    budget_shared: "Shared budget",
    budget_separate: "Separate budgets",
    budget_hybrid: "Hybrid budget",
    budget_mode_shared_desc: "One budget for the whole household",
    budget_mode_separate_desc: "Each adult manages their own budget",
    budget_mode_hybrid_desc: "Individual budgets + common account",
    common_budget: "Common budget",
    personal_budget: "Personal budget",
    common_percentage: "% to common account",
    for_member: "For",
    family_general: "Family expense",
    no_family: "No household yet",
    no_family_desc: "Create a household to track family finances together",
    family_dashboard: "Family Dashboard",
    family_transactions: "Family transactions",
    invite_by_email: "Invite by email",
    invite_by_code: "Share code",
    pending_invites: "Pending invites",
    
    // Additional common translations
    spent: "spent",
    month: "month",
    shared: "Shared",
    household: "Household",
    show_amounts: "Show amounts",
    hide_amounts: "Hide amounts",
    subscriptions: "Subscriptions",
    preferences: "Preferences",
    daily_challenge_title: "Daily challenge",
    no_challenge: "No challenge today",
    challenge_answer: "Answer",
    good_job: "Good job!",
    try_again: "Try again",
    
    // Dashboard specific
    hello: "Hello",
    hi: "Hi",
    see_all: "See all",
    reports: "Reports",
    scanner: "Scanner",
    scan_receipt: "Scan receipt",
    voice: "Voice",
    voice_input: "Voice input",
    shopping: "Shopping",
    recurring: "Recurring",
    personal: "Personal",
    common_pot: "Common Pot",
    monthly_common_pot: "Monthly common pot",
    household_income: "Household income",
    personal_income: "Personal income",
    monthly_income: "Monthly income",
    initial_balance: "Initial balance",
    members: "members",
    per_week: "per week",
    per_month: "per month",
    on_request: "on request",
    you_receive: "You receive",
    pocket_money_low: "Your pocket money is almost empty!",
    no_expenses_yet: "No expenses yet this month",
    refused: "Refused",
    approved: "Approved",
    pending: "Pending",
    what_for_optional: "What for? (optional)",
    budget_shared_label: "💰 Shared budget",
    budget_separate_label: "👤 Separate budgets",
    budget_hybrid_label: "🔄 Hybrid budget",
    hybrid: "Hybrid",
    more_info: "More info",
    
    // Family page
    household_members: "Household members",
    household_deleted: "Household deleted",
    edit_salary: "Edit my salary",
    net_monthly_income: "Net monthly income",
    update_income_desc: "Update your net monthly income. This amount will be used to calculate the family budget.",
    pocket_money: "Pocket money",
    my_pocket_money: "My pocket money",
    balance_label: "Balance:",
    current_balance: "Current balance:",
    optional: "optional",
    transfer_ownership: "Transfer ownership",
    first_name: "First name",
    family_name_label: "Last name",
    invalid_amount: "Invalid amount",
    member_added: "Member added!",
    passwords_dont_match: "Passwords don't match",
    close: "Close",
    register: "Register",
    give: "Give",
    copy_code: "Copy code",
    common: "Common",
    code_copied: "Code copied!",
    adult: "Adult",
    invitation_created: "✅ Invitation created",
    limit_reached: "⚠️ Limit reached",
    contribution_evolution: "Contribution evolution",
    or: "or",
    you: "You",
    you_set_password: "You set the password",
    left_household: "You left the household",
    view_subscriptions: "View subscriptions",
    enter_valid_amount: "Please enter a valid amount",
    budget_usage: "Budget usage",
    total_received: "Total received",
    total_spent: "Total spent",
    total_this_month: "Total this month",
    total_subscriptions_month: "Total subscriptions this month",
    total_family: "Total Family",
    savings_rate: "Savings rate",
    salary_updated: "Salary updated!",
    request_money: "Request money",
    request_sent: "Request sent!",
    request_amount: "Requested amount",
    my_pending_requests: "My pending requests",
    manage_family_finances: "Manage family finances",
    charts: "Charts",
    tools: "Tools",
    my_expenses: "My expenses",
    hide: "Hide",
    my_family: "My family",
    i_want: "I want",
    spending_by_category: "Spending by category",
    no_data: "No data",
    child_dashboard: "Child dashboard",
    owner: "Owner",
    child: "Child",
    username: "Username",
    password: "Password",
    confirm_password: "Confirm password",
    create_account: "Create account",
    account_created: "Account created!",
    email_address: "Email address",
    invite_by_email_desc: "An invitation email will be sent",
    send_invitation: "Send invitation",
    invitation_sent: "Invitation sent!",
    pending_invitations: "Pending invitations",
    cancel_invitation: "Cancel invitation",
    remove_member: "Remove member",
    confirm_remove_member: "Are you sure you want to remove this member?",
    allowance_settings: "Allowance settings",
    enable_pocket_money: "Enable pocket money",
    allowance_amount: "Allowance amount",
    allowance_frequency: "Frequency",
    weekly: "Weekly",
    monthly: "Monthly",
    on_demand: "On demand",
    approve: "Approve",
    reject: "Reject",
    request_approved: "Request approved",
    request_rejected: "Request rejected",
    child_requests: "Children's requests",
    no_pending_requests: "No pending requests",
    member_spending: "Member spending",
    family_overview: "Family overview",
    contribution_to_common: "Contribution to common pot",
    personal_remaining: "Personal remaining",
    
    // Objectives
    my_objectives: "My Objectives",
    save_for_projects: "Save for your projects",
    create: "Create",
    statistics: "Statistics",
    progress: "Progress",
    challenge_completed: "Challenge completed!",
    keep_going: "Keep going to complete the challenge!",
    well_done: "Well done!",
    not_quite: "Not quite...",
    checking: "Checking...",
    submit_answer: "Submit answer",
    
    // Family page additional
    join_my_household: "Join my SpenSpot household",
    failed_to_update_salary: "Failed to update salary",
    failed_to_remove_member: "Failed to remove member",
    ownership_transferred: "Ownership transferred successfully!",
    failed_to_transfer: "Failed to transfer ownership",
    settings_saved: "Settings saved!",
    request_approved_emoji: "Request approved! 💰",
    request_denied: "Request denied",
    invite_a_member: "Invite a member",
    pending_invitations: "Pending invitations",
    invitation_resent: "Invitation resent!",
    resend_invitation: "Resend invitation",
    invitation_cancelled: "Invitation cancelled",
    cancel_invitation: "Cancel invitation",
    create_invitation: "Create invitation",
    upgrade_subscription: "Upgrade subscription",
    members_count: " members",
    contribute: "Contribute",
    monthly_budget: "Monthly budget",
    spent_this_month: "Spent this month",
    common_expenses_by_member: "Common expenses by member",
    expected_contributions: "Expected monthly contributions",
    not_set: "Not set",
    manage_pocket_money: "Manage pocket money",
    remove_from_household: "Remove from household",
    pocket_money_requests: "Pocket Money Requests",
    deny: "Deny",
    accept: "Approve",
    breakdown_by_member: "Breakdown by member",
    monthly_subscriptions: "Monthly Subscriptions",
    manage: "Manage",
    by_category: "By category",
    upcoming_debits: "Upcoming",
    spending_by_category_title: "Spending by Category",
    allowance: "Allowance",
    cumulative: "Cumulative",
    spending_categories: "Spending categories",
    edit_my_salary: "Edit my salary",
    net_monthly_income_euro: "Net monthly income (€)",
    raise_bonus_job: "Raise, bonus, job change",
    instant_budget_update: "Instant family budget update",
    remove_a_member: "Remove member",
    remove: "Remove",
    give_pocket_money: "Give Pocket Money",
    enable_pocket_money_child: "Enable pocket money",
    monthly_allowance_optional: "Monthly allowance (optional)",
    shared_budget: "Shared budget",
    separate_budget: "Separate budget",
    code_for: "Code for",
    continue_action: "Continue",
    contribute_to_common_pot: "Contribute to Common Pot",
    contribution_added: "Contribution added!",
    common_pot_contribution: "Common pot contribution",
    create_credentials: "Create credentials",
    from_which_balance: "From which balance?",
    email_sent_to: "Email sent to",
    example_groceries: "Ex: Monthly groceries",
    example_family_name: "Ex: The Martins",
    example_name: "Ex: Mary",
    settings_updated: "Settings updated",
    regular_allowance: "Regular allowance",
    amount_to_give: "Amount to give",
    pocket_money_settings: "Pocket Money Settings",
    my_balance: "My balance",
    savings_rate: "Savings rate",
    available_exclaim: "Available!",
    
    // Subscriptions
    subscription_created: "Subscription created!",
    subscription_paused: "Subscription paused",
    subscription_updated: "Subscription updated!",
    subscription_reactivated: "Subscription reactivated",
    subscription_deleted: "Subscription deleted",
    common_subscriptions: "Common Subscriptions",
    personal_subscriptions: "Personal Subscriptions",
    active_subscriptions_label: "Active subscriptions",
    add_subscription: "Add a subscription",
    no_subscriptions_yet: "No subscriptions yet",
    no_results_found: "No results found",
    clear_filters: "Clear filters",
    paused_label: "Paused",
    example_netflix: "Ex: Netflix, Spotify...",
    example_family_plan: "Ex: Family plan",
    manage_recurring_subs: "Manage your recurring subscriptions",
    my_subscriptions: "My Subscriptions",
    pause_subscription: "Pause",
    name_amount_required: "Name and amount required",
    new_subscription: "New Subscription",
    search_subscription: "Search subscription...",
    reactivate: "Reactivate",
    by_category_label: "By Category",
    of_month_suffix: " of month",
    // Subscription frequencies & schedule
    frequency: "Frequency",
    first_payment: "First payment",
    first_payment_date: "First payment date",
    end_date: "End date",
    end_date_optional: "End date (optional)",
    next_payment: "Next payment",
    custom_days_label: "Every X days",
    custom_days_placeholder: "e.g. 60",
    freq_weekly: "Every week",
    freq_monthly: "Every month",
    freq_quarterly: "Every 3 months",
    freq_semi_annual: "Every 6 months",
    freq_yearly: "Every year",
    freq_custom_days: "Custom (days)",
    upcoming_payments: "Upcoming payments",
    filter_this_month: "This month",
    filter_30_days: "Next 30 days",
    filter_90_days: "Next 90 days",
    total_to_plan: "Total to plan",
    no_upcoming_payments: "No upcoming payments in this period",
    // Multi-member attribution
    attributed_to_members: "For which member(s)?",
    attributed_to_members_hint: "Select one or more members this expense is for. The debit stays on the payer's account.",
    spending_by_member: "Spending by member",
    no_attributed_spending: "No attributed spending this month",
    filter_by_member: "Filter by member",
    all_members: "All members",
    
    // More common strings
    view_objectives: "View objectives",
    view_budgets: "View budgets",
    total_colon: "Total:",
    real_time: "Real-time",
    success_rate: "Success rate",
    select_category: "Select a category",
    select_image: "Select an image",
    secure: "Secure",
    sync: "Sync",
    delete_transaction_confirm: "Delete this transaction?",
    delete_category_confirm: "Delete this category?",
    delete_budget_confirm: "Delete this budget?",
    subject_optional: "Subject (optional)",
    track_spending: "Track spending",
    suggest_budget: "Suggest budget",
    suggestions_for_you: "Suggestions for you",
    objectives_statistics: "Objectives Statistics",
    price_statistics: "Price Statistics",
    smart_shopping: "Smart Shopping",
    custom_threshold: "Custom threshold",
    will_be_deducted_from: "Will be deducted from:",
    sign_in: "Sign in",
    scan_receipt: "Scan a receipt",
    email_scan: "Email Scan",
    no_expiration: "No expiration",
    voice_input: "Voice input",
    voice_input_title: "Voice Input",
    role_colon: "Role:",
    summary: "Summary",
    daily_summary: "Daily summary",
    results_for: "Results for",
    split_over_months: "Split over multiple months",
    negative_balance_ignored: "Negative balance ignored",
    previous_month_negative: "Previous month negative balance",
    initial_balance_short: "Initial",
    personal_situation: "Personal situation",
    overdraft_risk_alert: "🚨 Overdraft Risk",
    sync_complete_alert: "✅ Sync Complete",
    budget_alert: "⚠️ Budget Alert",
    objectives_reminder_alert: "⏸️ Objectives Reminder",
    email_placeholder: "email@example.com or username",
    
    // Additional translations
    email_required_adults: "Email is required for adults",
    child_added_create_account: "Child added! Create their account so they can log in.",
    username_unavailable: "Username not available",
    invitation_sent_to_email: "Invitation sent to email",
    invite_by_email: "Invite by email",
    child_creates_account: "Child creates their account",
    childs_email: "Child's email",
    create_account_btn: "Create account",
    send_invitation: "Send invitation",
    allow_child_manage_money: "Allow child to manage their money",
    auto_monthly_allowance_desc: "Automatic monthly amount (leave empty to disable)",
    common_pot_balance_label: "Common pot balance",
    deducted_from_personal: "Money will be deducted from your personal balance",
    update_income_desc: "Update your net monthly income. This amount will be used to calculate the family budget.",
    household_deleted: "Household deleted",
    
    // Objectives page
    add_contribution: "Add contribution",
    achieved_exclaim: "Achieved!",
    no_objectives_yet: "No objectives yet",
    shared_label: "Shared",
    pause_behavior: "Pause behavior",
    auto_contribute: "Auto-contribute",
    contributed: "Contributed",
    create_first_objective: "Create my first objective",
    target_date: "Target date",
    avg_duration_days: "Avg duration (days)",
    failed_to_create: "Failed to create",
    failed_to_update: "Failed to update",
    example_vacation: "Ex: For family vacation",
    contribution_day: "Contribution day",
    days_paused: "Days paused",
    leave_empty_default: "Leave empty to restore default amount",
    change_this_month_contribution: "Change this month's contribution",
    change_label: "Change",
    modified_this_month: "Modified this month",
    target_amount: "Target amount",
    total_pauses: "Total pauses",
    new_amount: "New amount",
    new_objective: "New Objective",
    objective_created: "Objective created!",
    most_paused_objective: "Most paused objective",
    objective_paused: "Objective paused",
    objective_updated: "Objective updated!",
    objective_reactivated: "Objective reactivated",
    objective_deleted: "Objective deleted",
    objectives_paused_1x: "Objectives paused 1x+",
    objective_achieved: "Objective achieved!",
    personal_label: "Personal",
    who_pays_label: "Who saves?",
    i_only: "Only me",
    everyone: "Everyone",
    pause_reason: "Pause reason",
    no_objectives_to_display: "No objectives to display",
    start_saving_for: "Start saving for",
    remaining_amount: "Remaining to save",
    monthly_contrib_of: "Monthly contribution of",
    will_pause_until: "Will pause until",
    
    // Missing keys
    fill_required_fields: "Fill in all required fields",
    daily: "Daily",
    objective: "Objective",
    send: "Send",
    suggestion: "Suggestion",
    deleted: "Deleted",
    code: "Code",
    state: "State",
    
    // Invoices page
    invoices: "Invoices",
    my_invoices: "My Invoices",
    manage_invoices_desc: "Store your invoices and receipts securely",
    upload_invoice: "Add Invoice",
    product_name: "Product Name",
    store_name: "Store",
    purchase_date: "Purchase Date",
    warranty_months: "Warranty (months)",
    warranty_expires: "Warranty Expires",
    warranty_status: "Warranty Status",
    warranty_active: "Active",
    warranty_expiring: "Expiring Soon",
    warranty_expired: "Expired",
    warranty_expiring_alert: "Warranty expiring soon",
    warranty_reminder: "Warranty reminder",
    view_invoice: "View invoice",
    no_invoices_yet: "No invoices yet",
    add_first_invoice: "Add my first invoice",
    invoice_uploaded: "Invoice uploaded!",
    invoice_deleted: "Invoice deleted",
    invoice_updated: "Invoice updated",
    download_invoice: "Download",
    search_invoices: "Search by product, store...",
    invoice_limit_reached: "Invoice limit reached",
    upgrade_for_more: "Upgrade your subscription for more storage",
    expiring_warranties: "Expiring Warranties",
    days_remaining: "days remaining",
    invoice_details: "Invoice Details",
    select_file: "Select a file",
    max_file_size: "Max size: 5 MB",
    allowed_formats: "Formats: JPG, PNG, PDF",
    storage_used: "Storage used",
    of_limit: "of",
    
    // Settings page
    subscription_cancelled: "Subscription cancelled",
    quick_access_home: "Quick access from home screen",
    anticipate_overruns: "Anticipate budget overruns",
    interface_language: "Interface language",
    voice_ai_assistant: "Voice AI assistant",
    ai_advisor: "AI Advisor",
    create_join_household: "Create or join a household",
    failed_change_plan: "Failed to change plan",
    expires_on: "Expires",
    subscription: "Subscription",
    free: "Free",
    made_with_love: "Made with ❤️ in France",
    earn_free_premium: "Earn 1 free month of Premium!",
    keep_subscription: "Keep subscription",
    manage_custom_categories: "Manage my custom categories",
    manage_household: "Manage my household",
    import_csv: "Import CSV",
    import_bank_history: "Import bank history",
    ai_personality: "AI Personality",
    preferences: "Preferences",
    unlimited_ai_questions: "Unlimited AI questions",
    replay_tutorial: "Replay tutorial",
    other_label: "Other",
    cancel_subscription: "Cancel subscription",
    cancel_subscription_warning: "Are you sure you want to cancel? You will lose access to premium features.",
    member_limit_reduced: "Member limit reduced to 2",
    confirm_cancellation: "Confirm cancellation",
    
    // EmailScanPage - additional strings
    copy_paste_order_email: "Open your order confirmation email (Amazon, Fnac, etc.) and copy all content",
    click_paste_email: "Click \"Paste an email\" and paste the content",
    spenspot_auto_detect: "SpenSpot automatically detects the amount and merchant. Click Import!",
    feature_in_development: "This feature is in development. For now, use the \"Paste an email\" option.",
    auto_import_forward: "To auto-import, forward your order emails",
    paste_order_email_content: "Copy-paste the content of your order email",
    paste_email_text_html: "Paste the email content here (text or HTML)...",
    paste_order_confirmation_instructions: "Paste your order confirmation emails to import them automatically",
    
    // RecurringPage - strings
    recurring_expenses: "Recurring Expenses",
    subscriptions_payments: "Subscriptions & payments",
    detect: "Detect",
    patterns_detected_count: "{count} pattern(s) detected",
    detection_error: "Detection error",
    recurring_confirmed: "Recurring expense confirmed",
    confirmation_error: "Confirmation error",
    detected_patterns: "Detected patterns",
    confirmed_subscriptions: "Confirmed subscriptions",
    no_confirmed_subscriptions: "No confirmed subscriptions",
    click_detect_analyze: "Click \"Detect\" to analyze your transactions",
    spenspot_analyzes_6_months: "SpenSpot analyzes your last 6 months of transactions",
    patterns_auto_detected: "Recurring patterns are detected automatically",
    confirm_subs_for_reminders: "Confirm your subscriptions to receive reminders",
    confirm_recurring_expense: "Confirm this recurring expense?",
    occurrences: "Occurrences",
    next_expected_date: "Next expected date",
    in_days: "In",
    monthly_freq: "Monthly",
    weekly_freq: "Weekly",
    yearly_freq: "Yearly",
    
    // ShoppingPage
    search_error: "Search error",
    smart_shopping: "Smart Shopping",
    price_comparison: "Price comparison",
    search_product: "Search a product...",
    compare: "Compare",
    results_for_query: "Results for",
    not_enough_data_scan: "Not enough data. Scan more receipts to compare prices!",
    price_statistics: "Price Statistics",
    suggestions_for_you: "Suggestions for you",
    best_savings_opportunities: "Best savings opportunities",
    no_data_yet: "No data yet",
    scan_receipts_to_compare: "Scan your receipts to start comparing prices",
    scan_receipt_ocr: "Scan your receipts with OCR",
    spenspot_analyzes_prices: "SpenSpot analyzes and stores product prices",
    compare_prices_save: "Compare prices between stores and save!",
    
    // ObjectivesPage
    delete_objective_confirm: "Delete objective?",
    objectives_statistics: "Objectives Statistics",
    success_rate: "Success rate",
    pause_behavior: "Pause behavior",
    total_pauses: "Total pauses",
    days_paused: "Days paused",
    objectives_paused_1x: "Objectives paused 1x+",
    avg_duration_days: "Avg duration (days)",
    most_paused_objective: "Most paused objective",
    savings_evolution: "Savings Evolution",
    auto_monthly_contribution: "Automatic monthly contribution",
    first_ordinal: "1st",
    action_irreversible: "This action cannot be undone.",
    of_target: "out of {amount} targeted",
    example_vacation: "Ex: Summer vacation",
    
    // DashboardPage
    no_pocket_money_ask_parents: "No more pocket money! Ask your parents.",
    i_spent: "I spent",
    example_video_game: "Ex: New video game",
    no_objectives: "No objectives",
    create_household: "Create a household",
    manage_finances_together: "Manage finances together",
    charts: "Analytics",
    emails: "Emails",
    export: "Export",
    voice_input_label: "Voice input",
    scan_receipt_label: "Scan receipt",
    add_expense_label: "Add expense",
    hybrid_label: "Hybrid",
    initial_colon: "Initial",
    
    // CategoriesPage
    name_required: "Name required",
    category_created: "Category created",
    category_updated: "Category updated",
    delete_category_question: "Delete this category?",
    category_deleted: "Category deleted",
    manage_expense_categories: "Manage your expense categories",
    new_label: "New",
    no_custom_categories: "No custom categories",
    create_category: "Create category",
    edit_category: "Edit category",
    new_category: "New category",
    example_category_vacation: "Ex: Vacation",
    color: "Color",
    create_categories_to_organize: "Create categories to better organize your expenses",
    
    // OnboardingPage
    pocket_money_optional: "Pocket money (optional)",
    back_to_home: "Back to home",
    track_spending: "Track spending",
    challenges: "Challenges",
    lets_go: "Let's go! 🚀",
    income_label: "Income",
    household_label: "Household",
    budget_mode_label: "Budget mode",
    
    // FamilyPage
    allow_child_manage_money: "Allow child to manage their money",
    continue_btn: "Continue",
    create_account_btn: "Create account",
    example_name_marie: "Ex: Mary",
    send_invitation: "Send invitation",
    code_for: "Code for",
    email_sent_to: "Email sent to",
    settings_updated: "Settings updated",
    common_pot_contribution: "Common pot contribution",
    contribution_added: "Contribution added!",
    contribute_common_pot: "Contribute to Common Pot",
    description_optional: "Description (optional)",
    example_monthly_groceries: "Ex: Monthly groceries",
    
    // IncomeAdjustmentModal
    adjustment_added: "Adjustment added",
    adjustment_add_failed: "Failed to add",
    adjustment_deleted: "Adjustment deleted",
    adjust_my_income: "Adjust my income",
    base_salary: "Base salary",
    adjustments: "Adjustments",
    effective_income: "Effective income",
    add_adjustment: "Add adjustment",
    adjustment_type: "Adjustment type",
    amount_euro: "Amount (€)",
    this_month_adjustments: "This month's adjustments",
    invite_email_auto_sent: "An email with the invitation code will be sent automatically",
    current_balance: "Current balance",

    // Income Sources (multi-revenues)
    income_sources: "Income sources",
    income_sources_title: "Manage my income",
    income_sources_desc: "Salary, aids, self-employed, rentals...",
    add_income_source: "Add a source",
    edit_income_source: "Edit source",
    income_type: "Income type",
    income_amount: "Amount",
    income_frequency: "Frequency",
    income_frequency_monthly: "Monthly",
    income_frequency_weekly: "Weekly",
    income_frequency_daily: "Daily",
    income_frequency_one_time: "One-time",
    income_date: "Date",
    income_start_date: "Start date (optional)",
    income_end_date: "End date (optional)",
    income_custom_label: "Custom label (optional)",
    income_notes: "Notes (optional)",
    income_active: "Active source",
    income_inactive: "Inactive",
    total_monthly_income: "Total monthly income",
    income_source_added: "Source added",
    income_source_updated: "Source updated",
    income_source_deleted: "Source deleted",
    income_source_save_failed: "Failed to save",
    income_source_delete_failed: "Failed to delete",
    confirm_delete_income_source: "Delete this income source?",
    no_income_sources: "No income sources. Add your salary to get started.",
    monthly_contribution: "Contribution this month",
    income_breakdown_by_frequency: "Breakdown by frequency",
    family_total_income: "Household income",
    personal_income: "Personal income",
    quick_add_income: "Quick add income",
    quick_add_income_desc: "Quickly add today's income",

    // LandingPage - Auth
    quick_secure_login: "Quick and secure login",
    credentials: "Credentials",
    email_or_username: "Email or username",
    login: "Login",
    create_spenspot_account: "Create your SpenSpot account (email required)",
    enter_email_or_username: "Enter your email or username",
    required_password_recovery: "Required for password recovery",
    email_or_username_placeholder: "email@example.com or username",
    already_have_account: "Already have an account? Log in",
    no_account_signup: "No account? Sign up",
    signup: "Sign up",
    log_in: "Log in",
    
    // QuickAddModal
    split_payment: "Split payment",
    add_split_payment: "+ Split payment",
    remaining_amount: "Remaining",
    add_payment_method: "Add payment method",
    subscription_name: "Subscription name",
    subscription_placeholder: "Ex: Netflix, Spotify, Insurance...",
    name_required_subscription: "Name is required for a subscription",
    for_label: "For",
    adding: "Adding...",
    create_installment_payment: "Create {count}x payment",
    add_expense: "Add expense",
    
    // PredictionsPage
    anticipate_spending: "Anticipate your spending",
    refresh: "Refresh",
    total_budget: "Total budget",
    predicted_end_month: "Predicted end of month",
    daily_budget_remaining: "Daily budget remaining",
    days_remaining_month: "days remaining this month",
    urgent_alerts: "Urgent alerts",
    no_predictions_available: "No predictions available",
    create_budget: "Create budget",
    
    // ChatPage
    error_loading_conversation: "Error loading conversation",
    conversation_deleted: "Conversation deleted",
    error_deleting: "Error deleting",
    limited_access: "Limited Access",
    ai_financial_assistant: "AI Financial Assistant",
    analyze_spending: "Analyze spending",
    suggest_budget: "Suggest budget",
    example_questions: "Example questions:",
    thinking: "Thinking...",
    ask_question_placeholder: "Ask your question...",
    password_requirements: "Min. 8 chars, 1 uppercase, 1 lowercase, 1 digit",
    create_budgets_to_see_predictions: "Create budgets to see predictions",
    ai_assistant_premium_required: "AI assistant is available with Standard and Premium subscriptions.",
    ai_assistant_description: "Ask questions about your finances, get budget advice, or analyze your spending.",
    setup_profile_desc: "In less than 3 minutes, let's set up your profile for personalized advice.",
    enter_name_and_code: "Enter your first name and the invitation code you received",
    max_member_limit_reached: "The maximum member limit has been reached.",
    ask_parents_upgrade: "Ask your parents to upgrade to {plan} so you can join the household.",
    welcome_name: "Welcome {name}!",
    now_part_of_household: "You're now part of the \"{name}\" household",
    parents_can_give_pocket_money: "Your parents can give you pocket money that you can manage here!",
    
    // VoiceInputModal
    voice_input: "Voice Input",
    voice_input_example: "Say your expense, for example: \"25 euros at restaurant\"",
    browser_no_speech: "Your browser does not support speech recognition",
    no_speech_detected: "No speech detected",
    tap_to_speak: "Tap to speak",
    error_adding: "Failed to add",
    expense_description_placeholder: "Expense description",
    which_category: "Which category?",
    
    // AI Predictions Explanation
    how_predictions_calculated: "How are predictions calculated?",
    daily_rate: "Daily rate",
    daily_rate_explanation: "Month spending ÷ Days passed = Average per day",
    end_month_forecast: "End of month forecast",
    end_month_forecast_explanation: "Current spending + (Daily rate × Remaining days)",
    recommended_daily_budget: "Recommended daily budget",
    recommended_daily_budget_explanation: "(Total budget - Current spending) ÷ Remaining days",
    what_is_excluded: "What is excluded",
    what_is_excluded_explanation: "Income and savings are not counted as expenses in these calculations.",
    predictions_tip: "Predictions are based on your current spending rate this month. The more transactions you add, the more accurate they become.",
    current_rate: "Current rate",
    per_day: "/day",
    
    // OCRScannerModal
    of_receipt: "Of the receipt",
    select_image: "Select an image",
    image_formats: "JPG, PNG or HEIC",
    choose_from_gallery: "Choose from gallery",
    installment_created: "{count}x payment created!",
    expense_added_exclaim: "Expense added!",
    prices_stored: "Prices stored.",
    invoice_saved: "Invoice saved.",
    error_creating: "Error creating transaction",
    
    // ImportCSVModal
    import_csv_file: "Import CSV file",
    import_csv_desc: "Import your bank history from an exported CSV or Excel file",
    click_to_choose_file: "Click to choose a file",
    export_csv_tip: "Export your statements from your online bank in CSV format",
    map_columns: "Map columns:",
    preview_first_rows: "Preview (first 3 rows):",
    importing: "Importing...",
    import_complete: "Import complete!",
    import_rows: "Import {count} rows",
    transactions_imported: "{count} transaction(s) imported",
    
    // AlertsPanel
    alerts: "Alerts",
    other_info: "Other info",
    amount_transferred_from: "{amount}€ transferred from \"{name}\" to your balance",
    current_balance_warning: "Your current balance is {balance}. You can transfer money from your objectives.",
    no_objectives_with_savings: "No objectives with savings",
    amount_to_transfer: "Amount to transfer",
    max_amount: "Max:",
    
    // SubscriptionsPage
    first_of_month: "1st",
    of_month: " of month",
    subscription_description_placeholder: "Ex: Family plan",
    delete_subscription_confirm: "Delete subscription?",
    subscription_will_be_deleted: "This subscription will be permanently deleted.",
    
    // InvoicesPage
    file_type_not_allowed: "File type not allowed",
    info_extracted_auto: "Information extracted automatically!",
    fill_info_manually: "Fill in the information manually",
    delete_invoice_confirm: "Delete this invoice?",
    analyzing: "Analyzing...",
    info_auto_extracted: "Information auto-extracted",
    optional_notes_placeholder: "Optional notes...",
    
    // ObjectivesPage
    objective_achieved: "Objective achieved! 🎉",
    objective_saving_recorded: "Saving recorded!",
    objective_paused: "Objective paused",
    objective_resumed: "Objective resumed!",
    contribution_added: "Contribution added!",
    suggested_contribution_applied: "Suggested contribution: {amount}€/month applied",
    auto_contribute_info: "Auto: {amount}/month on the {day}th",
    monthly_to_reach_goal: "{amount}/month to reach your goal",
    auto_contribute_enabled: "Auto-contribute enabled!",
    
    // ReferralPage
    share_referral_code: "Join SpenSpot with my code {code} and we both get 1 month Premium! 🎁",
    referral_link_copied: "Link copied!",
    how_it_works: "How does it work?",
    share_your_code: "Share your code",
    share_your_code_desc: "Send your code to friends",
    they_sign_up: "They sign up",
    they_sign_up_desc: "They use your code to create an account",
    both_win_desc: "You earn 50 XP and they start with a bonus!",
    thanks_to: "Thanks to {name}!",
    
    // InvitationPage
    invalid_invitation: "Invalid invitation",
    back_to_home: "Back to home",
    account_created_exclaim: "Account created!",
    welcome_to_spenspot: "Welcome to SpenSpot",
    role_label: "Role:",
    full_name: "Full name",
    create_my_account: "Create my account",
    
    // BudgetsPage
    over_by: "Over by",
    delete_budget_confirm: "Delete this budget?",
    choose_category: "Choose a category",
    total_budget_month: "Total budget this month",
    common_budgets: "Common Budgets",
    personal_budgets: "Personal Budgets",
    no_budget_category: "No budget yet for this category",
    suggested_budget: "Suggested budget (50/30/20)",
    no_budgets_yet: "No budgets defined yet",
    no_budgets_explanation: "Budgets help you control your spending by category. Set monthly limits for each category and track your progress.",
    add_first_budget: "Add my first budget",
    
    // NotificationSettings
    notifications_enabled: "Notifications enabled!",
    notifications_permission_denied: "Permission denied. Enable notifications in your browser settings.",
    notifications_error: "Failed to enable notifications",
    notifications_disabled: "Notifications disabled",
    test_notification_sent: "Test notification sent!",
    test_notification_failed: "Failed to send notification",
    notifications_not_supported: "Notifications not supported",
    browser_no_push_support: "Your browser does not support push notifications.",
    reminder_same_day: "Reminder on payment day",
    notification_on_debit_day: "Notification on debit day",
    paused_objectives_reminders: "Paused Objectives Reminders",
    monthly_reminder: "Monthly reminder",
    monthly_reminder_desc: "Notification at beginning of month for paused objectives",
    show_until_day: "Show until day",
    show_reminder_until_desc: "Show reminder from 1st until this day of month",
    realtime: "Real-time",
    realtime_desc: "Immediate notification when threshold is reached",
    daily_summary: "Daily summary",
    daily_summary_desc: "Single daily notification with all alerts",
    summary_time: "Summary time:",
    send_test_notification: "Send test notification",
    
    // NegativeBalanceModal
    previous_month_negative_balance: "Previous month negative balance",
    balance_ended_negative: "Your {month} balance ended negative.",
    overdraft_to_cover: "Overdraft to cover",
    already_repaid: "Already repaid",
    month_concerned: "Month:",
    deduct_from_balance_question: "Do you want to deduct this amount from your current balance?",
    amount_to_deduct: "Amount to deduct",
    maximum: "Maximum:",
    ignore: "Ignore",
    partial_amount: "Partial amount",
    deduct_all: "Deduct all",
    negative_balance_ignored: "Negative balance ignored",
    
    // FamilyPage additional
    member_removed: "{name} has been removed from the household",
    pocket_money_given: "{amount}€ given to {name}! 🎉",
    member_limit_reached: "Limit of {max} members reached",
    subscription_limited: "Your {plan} plan is limited.",
    children_count: "{count} child",
    children_count_plural: "{count} children",
    join_my_household_text: "Join my household \"{name}\" on SpenSpot with code: {code}",
    child_no_account_warning: "⚠️ This child doesn't have an account yet. Expenses won't be tracked.",
    delete_family_warning: "This action cannot be undone. All members will be removed from the household.",
    remove_member_confirm: "Are you sure you want to remove {name} from the household? This action cannot be undone.",
    budget_reduction_warning: "⚠️ Total household budget will be reduced by {amount}/month",
    transfer_ownership_description: "Choose an adult member who will become the new owner of the household. You will become a standard member.",
    no_eligible_members_transfer: "No eligible members for transfer. Invite another adult first.",
    pocket_money_saved: "Pocket money updated!",
    allowance_saved: "Allowance updated!",
    give_money_to: "Give money to {name}",
    edit_child_pocket_money: "Edit {name}'s pocket money",
    edit_child_allowance: "Edit {name}'s allowance",
    monthly_allowance: "Monthly allowance",
    no_allowance_set: "No allowance set",
    allowance_frequency: "Allowance frequency",
    username_placeholder_fr: "emma_dupont",
    username_placeholder_en: "emma_smith",
    money_from_shared_budget: "Money will be taken from the shared family budget",
    money_from_personal_balance: "Money will be taken from your personal balance",
    child_account_created: "Child account created! 🎉",
    invite_sent: "Invitation sent!",
    money_from_shared_or_personal: "Money will be taken from shared budget or your personal balance based on your choice",
    no_children_with_pocket_money: "No children with pocket money enabled",
    salary_optional: "Salary (optional)",
    common_pot_balance: "Common pot: {amount}",
    money_deducted_personal: "Money will be deducted from your personal balance",
    manage_pocket_money_for: "Manage pocket money for {name}",
    monthly_auto_amount_desc: "Automatic monthly amount (leave empty to disable)",
    create_family_description: "Create a household to manage family finances together",
    enter_invite_code_desc: "Enter the invite code shared by the household owner",
    invite_code_validity: "The code starts with INV- and is valid for 7 days",
    invitation_sent_to_email: "Invitation sent to {email}. The adult needs to create their account.",
    account_created_login: "Account created! {name} can log in with \"{username}\"",
    add_child_or_adult: "Add a child or adult",
    how_will_login: "How will {name} log in?",
    invitation_will_be_sent: "An invitation will be sent to create their account",
    pocket_money_description: "Enable if this child receives pocket money and can track expenses",
    initial_balance_child: "Initial balance (0€ default)",
    initial_balance_child_desc: "Amount already saved by the child",
    allowance_schedule_desc: "Money will be transferred automatically according to the schedule",
    create_child_account_title: "Create a child account?",
    create_child_account_desc: "{name} will be able to log in to view their balance and expenses",
    skip_for_now: "Skip for now",
    create_child_account: "Create account",
    username_simple_desc: "Easy to remember - no email required",
    child_password_desc: "An easy password for the child to remember",
    invite_code_copied: "Invite code copied!",
    how_to_create_account: "How do you want to create the account?",
    invitation_will_be_sent_child: "An invitation will be sent. The child can create their own password.",
    username_requirements: "Letters, numbers and _ only. Min 4 characters.",
    invitation_sent_by_email: "Invitation sent by email!",
    code_created_share_manually: "Code created! Share it manually.",
    upgrade_for_more_members: "Upgrade to {plan} to invite up to {limit} members.",
    unlimited_members: "unlimited",
    max_limit_reached: "You have reached the maximum limit for your subscription.",
    code_single_use_warning: "⚠️ This code is single-use and expires in 7 days",
    only_owner_can_modify: "Only the owner can modify household settings.",
    
    // OnboardingPage
    welcome_exclaim: "Welcome! 🎉",
    already_member_of: "You're already a member of this household!",
    already_in_family: "You're already part of a household.",
    join_new_family: "Join this new household",
    enter_your_salary: "Enter your salary (can change later)",
    join_this_family: "Join this household",
    continue_without_family: "Continue without household",
    cant_find_code: "Don't have a code?",
    i_have_code: "I have an invite code",
    
    // TransactionsPage
    delete_all_installments: "Delete all installments of \"{description}\"?",
    installments_deleted: "Installment payments deleted",
    delete_transaction_confirm: "Delete this transaction?",
    search_placeholder: "Search...",
    common_transactions: "Common",
    personal_transactions: "Personal",
    active_installments: "Active installment payments",
    total_label: "Total:",
    monthly_label: "Monthly:",
    no_transactions_period: "No transactions for this period",
    start_tracking_expenses: "Start tracking your expenses!",
    
    // ExportPage
    tax_export: "Tax Export",
    annual_expense_report: "Annual expense report",
    tax_year: "Tax year",
    monthly_average: "Monthly average",
    for_accountant: "For accountant",
    visual_report: "Visual report",
    csv_downloaded: "CSV downloaded!",
    export_error: "Export error",
    report_downloaded: "Report downloaded! Open it and print to PDF (Ctrl+P)",
    no_data_for_year: "No data for ",
    add_transactions_for_report: "Add transactions to generate your tax report",
    export_disclaimer: "This report is generated from your expenses recorded in SpenSpot. Verify amounts with your bank statements before using them for your tax return.",
    
    // InstallPWAPrompt
    install_spenspot: "Install SpenSpot",
    quick_access_home_screen: "Quick access from your home screen",
    ios_install_instructions: "To install SpenSpot on your iPhone/iPad:",
    tap_share: "1. Tap ",
    share: "Share",
    select_add_home: "2. Select \"Add to Home Screen\"",
    install_for_offline: "Install SpenSpot for instant access, even offline!",
    install: "Install",
    faster: "Faster",
    offline: "Offline",
    app_installed: "App installed",
    install_app: "Install app",
    ios_safari_instructions: "Use Safari → Share → \"Add to Home Screen\"",
    
    // Contact Support
    contact_support: "Contact Support",
    contact_support_desc: "Send us a message, we'll get back to you quickly.",
    contact_support_short: "Problem, suggestion, question",
    contact_type: "Request type",
    problem: "Problem",
    suggestion: "Suggestion",
    question_label: "Question",
    subject: "Subject",
    message: "Message",
    contact_subject_placeholder: "Ex: Login issue",
    contact_message_placeholder: "Describe your request in detail...",
    contact_sent_as: "Sent as",
    reply_to: "Reply to",
    sending: "Sending...",
    contact_sent_success: "Message sent! We'll respond quickly.",
    contact_send_error: "Error sending message",
    message_required: "Message is required",
    message_too_short: "Message must be at least 10 characters",
    
    // Missing translations - Added Feb 2026
    checking_quota: "Checking your quota...",
    limit_reached: "Limit reached",
    ocr_not_available: "OCR scanning is not available with your free subscription.",
    scans_used: "You have used {used}/{limit} scans this month.",
    upgrade_for_ocr: "Upgrade your subscription to get more OCR scans.",
    view_subscriptions_btn: "View subscriptions",
    select_category: "Select a category",
    error_updating: "Error updating",
    edit_transaction: "Edit transaction",
    thinking: "Thinking...",
    invoice_limit: "Invoice limit",
    invoice_storage_limits: "Invoice storage limits",
    limits_saved: "Limits saved!",
    error_saving: "Error saving",
    challenge_management: "Challenge Management",
    create_manage_challenges: "Create and manage daily challenges",
    new_challenge: "New challenge",
    default_challenges: "Default challenges",
    custom_challenges: "Custom challenges",
    no_custom_challenges: "No custom challenges",
    click_new_challenge: "Click \"New challenge\" to create one",
    edit_challenge: "Edit challenge",
    ai_generation: "AI Generation",
    theme_optional: "Theme (optional)",
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    generate: "Generate",
    target_languages: "Target languages",
    xp_reward: "XP Reward",
    target_number: "Target (number)",
    update: "Update",
    create: "Create",
    financial_analytics: "Financial Analytics",
    year: "Year",
    reset_password: "Reset password",
    password_reset_success: "Password reset!",
    back_to_login: "Back to login",
    email_sent: "Email sent!",
    check_email_reset: "Check your email for the reset link.",
    send_reset_link: "Send reset link",
    monthly_report: "Monthly Report",
    report_opened_print: "Report opened - use Ctrl+P to print as PDF",
    remaining: "Remaining",
    not_available: "Not available",
    share_your_code: "Share your code",
    objectives_progress: "Objectives progress",
    transactions_this_month: "Transactions this month",
    ocr_scans_month: "OCR scans/month",
    ai_questions_month: "AI questions/month",
    voice_assistant: "Voice assistant",
    members: "Members",
    view_objectives: "View objectives",
    view_budgets: "View budgets",
    offline_mode: "Offline mode",
    sync: "Sync",
    overdraft_risk: "Overdraft Risk",
    budget_alert: "Budget Alert",
    objectives_reminder: "Objectives Reminder",
    sync_success: "Sync successful",
    // Date picker for transactions
    expense_date: "Expense date",
    date_today: "Today",
    date_warning_past_month: "⚠️ This expense will be recorded in a past month. It will affect that month's reports and statistics.",
    date_warning_future: "ℹ️ This expense is in the future. It will be counted on the indicated date.",
    date_warning_far_past: "⚠️ Date is very old (more than 6 months). Are you sure?",
    // Reflexive questions for simulator
    reflexive_section_title: "Think before you buy",
    reflexive_section_subtitle: "Check what applies (optional) — the AI will take it into account",
    reflexive_q_need: "I really need it (not just want it)",
    reflexive_q_thought: "I've thought about it (not an impulse buy)",
    reflexive_q_compared: "I've compared prices or looked at alternatives",
    reflexive_q_aligned: "It fits my priorities and values",
    reflexive_q_long_term: "I will actually use it long-term",
    reflexive_reason_label: "Why do I want it? (optional)",
    reflexive_reason_placeholder: "Describe in a few words the reason for this purchase...",
  },
  pt: {
    // Common
    loading: "A carregar...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    add: "Adicionar",
    close: "Fechar",
    confirm: "Confirmar",
    back: "Voltar",
    next: "Seguinte",
    skip: "Saltar",
    finish: "Terminar",
    error: "Erro",
    success: "Sucesso",
    paused: "Em pausa",
    active: "Ativo",
    completed: "Concluído",
    total: "Total",
    days: "dias",
    weeks: "semanas",
    months: "meses",
    years: "anos",
    all: "Todas",
    filter: "Filtrar",
    search: "Pesquisar",
    clear: "Limpar",
    no_results: "Sem resultados",
    
    // Form fields
    amount: "Montante",
    category: "Categoria",
    categories: "Categorias",
    name: "Nome",
    description: "Descrição",
    date: "Data",
    type: "Tipo",
    status: "Estado",
    optional: "opcional",
    required: "obrigatório",
    select: "Selecionar...",
    password: "Palavra-passe",
    confirm_password: "Confirmar palavra-passe",
    new_password: "Nova palavra-passe",
    passwords_dont_match: "As palavras-passe não correspondem",
    your_name: "O teu nome",
    situation: "Situação",
    budget_type: "Tipo de orçamento",
    
    // Messages
    loading_error: "Erro ao carregar",
    invalid_amount: "Montante inválido",
    failed_to_delete: "Falha ao eliminar",
    fill_all_fields: "Preenche todos os campos",
    redirecting_login: "A redirecionar para início de sessão...",
    no_data: "Sem dados",
    
    // Dashboard/Stats
    total_spent: "Total gasto",
    top_category: "Categoria principal",
    breakdown_by_category: "Divisão por categoria",
    common_pot: "Pote comum",
    ai_predictions: "Previsões IA",
    view_subscriptions: "Ver subscrições",
    
    // Actions
    later: "Mais tarde",
    how_it_works: "💡 Como funciona?",
    forward_receipts: "Reencaminha os teus recibos",
    transcript: "Transcrição:",
    installment_payment: "Pagamento em prestações",
    reached: "atingido",
    
    // More common
    notes: "Notas",
    icon: "Ícone",
    frequency: "Frequência",
    expenses: "Despesas",
    expense_added: "Despesa adicionada!",
    completed_plural: "Concluídos",
    avg_per_day: "Média/dia",
    monthly_amount: "Montante mensal",
    budget_management_mode: "Modo de gestão do orçamento",
    min_8_chars: "Mínimo 8 caracteres",
    password_min_8: "A palavra-passe deve ter pelo menos 8 caracteres",
    payment_day: "Dia de pagamento",
    create_account: "Criar conta",
    forgot_password: "Esqueceste a palavra-passe?",
    failed_to_save: "Falha ao guardar",
    failed_to_load: "Falha ao carregar",
    example_grocery: "Ex: Compras no supermercado",
    example_city: "Ex: Lisboa, Porto, Coimbra...",
    
    // UI labels
    invalid_code: "Código inválido",
    change_plan: "Mudar plano",
    no_budgets_configured: "Nenhum orçamento configurado",
    ai_assistant: "Assistente IA",
    enable: "Ativar",
    disable: "Desativar",
    take_photo: "📷 Tirar uma fotografia",
    category_details: "📋 Detalhes por categoria",
    default_categories: "📋 Categorias predefinidas",
    breakdown_by_category: "📊 Divisão por categoria",
    predictions_by_category: "📊 Previsões por categoria",
    spending_trend: "📉 Tendência de gastos",
    monthly_evolution: "📈 Evolução mensal",
    budget_progress: "💰 Progresso do orçamento",
    shared_budget: "💰 Orçamento partilhado",
    shared_family_budget: "💰 Orçamento familiar partilhado",
    hybrid_budget: "🔄 Orçamento misto",
    how_to_import: "💡 Como importar recibos?",
    your_personal_balance: "👤 O teu saldo pessoal",
    
    // Bank Import
    import_bank_statement: "Importar extrato bancário",
    import_bank_desc: "Importe as suas despesas de um ficheiro PDF ou Excel",
    import_preview_desc: "Reveja e selecione as transações a importar",
    supported_formats: "Formatos suportados",
    drop_file_here: "Arraste o seu ficheiro aqui",
    select_file: "Selecionar ficheiro",
    change_file: "Mudar ficheiro",
    target_month: "Mês alvo",
    current_month: "Mês atual",
    previous_month: "Mês anterior",
    analyzing: "A analisar...",
    analyze: "Analisar",
    import: "Importar",
    new: "Novas",
    possible_duplicates: "Possíveis duplicados",
    duplicates: "Duplicados",
    selected: "selecionadas",
    select_all: "Todos",
    select_new_only: "Apenas novas",
    importing: "A importar...",
    transactions_imported: "transações importadas",
    no_transactions_selected: "Nenhuma transação selecionada",
    import_preview_error: "Erro ao analisar ficheiro",
    import_error: "Erro na importação",
    invalid_file_format: "Formato de ficheiro inválido. Use PDF ou Excel.",
    
    // More UI labels
    separate_budgets: "👤 Orçamentos separados",
    common_pot_label: "🏦 Pote comum",
    my_categories: "✨ As minhas categorias",
    complete: "✓ Completo",
    invitation_created: "✅ Convite criado",
    limit_reached: "⚠️ Limite atingido",
    failed: "falhou",
    saved: "Poupado",
    total_savings: "Poupança total",
    listening: "A ouvir...",
    potential_savings: "Poupanças potenciais",
    available_savings: "Poupanças disponíveis",
    deadline_passed: "Prazo expirado",
    used: "usado",
    budget_status: "Estado do orçamento",
    payment_history: "Histórico de pagamentos",
    total_objectives: "Total de objetivos",
    upcoming_payments: "Próximos pagamentos",
    scan_receipt: "Digitalizar recibo",
    
    // OCR Scanner
    image_too_large: "Imagem demasiado grande (máx 10MB)",
    could_not_read_receipt: "Não foi possível ler o recibo",
    scan_error: "Erro ao digitalizar",
    prices_stored: "Preços guardados.",
    invoice_saved: "Fatura guardada.",
    error_creating: "Erro ao criar transação",
    of_receipt: "Do recibo",
    select_image: "Selecionar uma imagem",
    jpg_png_heic: "JPG, PNG ou HEIC",
    choose_from_gallery: "Escolher da galeria",
    analyzing_receipt: "A analisar recibo...",
    claude_extracting: "A IA está a extrair dados",
    use_today_date: "Usar a data de hoje",
    keep_receipt_date: "Manter a data do recibo",
    merchant: "Comerciante",
    merchant_name: "Nome do comerciante",
    share_prices: "Partilhar preços",
    save_as_invoice: "Guardar como fatura",
    retry: "Tentar novamente",
    create_installments: "Criar {count}x",
    receipt_date_warning: "Este recibo tem a data de {date}. Se o guardares com esta data, não será contabilizado no saldo do mês atual.",
    which_date_to_use: "Que data queres usar?",
    counted_in_current_month: "Contabilizado no mês atual",
    keep_original_date: "Manter a data original do recibo",
    not_counted_in_balance: "Não será contabilizado no teu saldo atual",
    products_detected_community: "produto(s) detetado(s) - ajuda a comunidade!",
    keep_receipt_warranties: "Guarda este recibo para as tuas garantias",
    
    // Quick Add Modal
    split_over_months: "Dividir em vários meses",
    number_of_installments: "Número de prestações",
    preview: "Pré-visualização:",
    recurring_subscription: "Subscrição recorrente",
    auto_charged_monthly: "Cobrado automaticamente a cada mês",
    select_day: "Selecionar dia",
    first_day: "1º",
    day_of_month: " do mês",
    summary_label: "Resumo:",
    split_payment: "+ Pagamento dividido",
    split_payment_label: "Pagamento dividido",
    remaining_amount: "Restante: {amount}€",
    add_payment_method: "Adicionar método de pagamento",
    subscription_name: "Nome da subscrição",
    subscription_placeholder: "Ex: Netflix, Spotify, Seguro...",
    name_required_subscription: "O nome é obrigatório para uma subscrição",
    for_label: "Para",
    adding: "A adicionar...",
    create_installment_payment: "Criar pagamento em {count}x",
    add_expense: "Adicionar despesa",
    charged_on_day: "Cobrado no dia {day} de cada mês",
    
    login: "Entrar",
    connection: "Ligação",
    paste_confirmation_email: "Colar email de confirmação",
    quick_access: "Acesso rápido a partir do ecrã",
    install_app: "Instalar aplicação",
    adjust_income: "Ajustar rendimento",
    adjust_income_desc: "Prémios, horas extra, férias...",
    description_optional: "Descrição (opcional)",
    email_or_username: "Email ou nome de utilizador",
    of_month: "do mês",
    
    // More contextual
    active_users: "utilizadores ativos",
    urgent: "urgente",
    transactions_label: "transações",
    transaction_count: "transação(ões)",
    success_label: "bem-sucedidas",
    result_count: "resultado(s)",
    remaining: "restante(s)",
    to_add_expense: "para adicionar uma despesa",
    paid: "pago(s)",
    pauses: "pausas",
    rows_detected: "linhas detetadas",
    detected: "detetado",
    from_common_pot: "do pote comum",
    from_your_budget: "do teu orçamento",
    confidence: "confiança",
    purchase_count: "compra(s)",
    active_subscriptions: "subscrições ativas",
    invited_to_join: "Foste convidado(a) para te juntares ao agregado",
    you_will_lose: "Vais perder:",
    you_both_win: "Ambos ganham",
    you_set_password: "Defines a palavra-passe",
    invited_with_subscription: "Foste convidado(a) com uma subscrição",
    you_left_household: "Saíste do agregado",
    already_have_account: "Já tens uma conta?",
    your_city: "A tua cidade",
    your_monthly_income: "O teu rendimento mensal líquido",
    your_first_name: "O teu primeiro nome",
    your_plan_includes: "O teu plano inclui:",
    visualize_spending: "Visualiza os teus gastos",
    city: "Cidade",
    please_select_image: "Por favor, seleciona uma imagem",
    enter_name_and_code: "Por favor, introduz o teu nome e o código",
    enter_valid_amount: "Por favor, introduz um montante válido",
    validate_code: "Validar código",
    use_my_savings: "Usar as minhas poupanças",
    budget_usage: "Utilização do orçamento",
    an_error_occurred: "Ocorreu um erro",
    you_receive: "Recebes",
    quarter: "Trimestre",
    last_month: "Mês anterior",
    last_quarter: "Trimestre anterior",
    forward_to: "Reencaminhar para:",
    transfer: "Transferir",
    transfer_to_balance: "Transferir para saldo atual",
    unlimited_transactions: "Transações ilimitadas",
    transaction_updated: "Transação atualizada!",
    transaction_added: "Transação adicionada!",
    all_categories: "Todas as categorias",
    all_set: "Tudo pronto!",
    everything_you_need: "Tudo o que precisas",
    all_rights_reserved: "Todos os direitos reservados.",
    total_target: "Total de objetivos",
    monthly_total: "Total mensal",
    monthly_recurring_total: "Total mensal recorrente",
    total_expenses: "Total de despesas",
    receipt_scanned: "Recibo digitalizado com sucesso!",
    receipt_previous_month: "Recibo do mês anterior",
    your_referrals: "As tuas referências",
    switch_store_savings: "por mês ao mudar de loja",
    total_received: "Total recebido",
    
    // Landing
    hero_title: "O teu parceiro que percebe de dinheiro",
    hero_subtitle: "Sem te julgar.",
    hero_cta: "Começar gratuitamente",
    hero_login: "Entrar",
    feature1_title: "1 segundo para registar",
    feature1_desc: "Adiciona as tuas despesas rapidamente",
    feature2_title: "Conselhos práticos",
    feature2_desc: "Dicas personalizadas, não genéricas",
    feature3_title: "Alcança os teus objetivos",
    feature3_desc: "3x mais rápido que outras apps",
    
    // Auth & Messages
    login_with_google: "Continuar com Google",
    logout: "Terminar sessão",
    login_success: "Sessão iniciada com sucesso!",
    invalid_credentials: "Credenciais inválidas",
    invalid_email: "Email inválido",
    password_8_chars: "Pelo menos 8 caracteres",
    password_uppercase: "Pelo menos uma maiúscula",
    password_lowercase: "Pelo menos uma minúscula",
    password_digit: "Pelo menos um dígito",
    password_too_weak: "Palavra-passe demasiado fraca:",
    account_created: "Conta criada! A iniciar sessão...",
    registration_error: "Erro de registo",
    
    // Landing features
    fast: "Rápido",
    fast_desc: "1 segundo para adicionar uma despesa",
    reach_goals: "Alcança os teus objetivos financeiros",
    insights: "Análises",
    understand_habits: "Compreende os teus hábitos",
    secure: "Seguro",
    encrypted_data: "Dados encriptados",
    ai: "IA",
    personalized_advice: "Conselhos personalizados",
    manage_together: "Gerir em conjunto",
    goals_achieved: "de objetivos alcançados",
    ready_to_start: "Pronto para começar?",
    free_no_commitment: "Gratuito • Sem compromisso • Conforme RGPD",
    hero_main_title_fr: "O teu parceiro que percebe de",
    hero_main_title_highlight: "dinheiro",
    hero_main_desc: "Gere as tuas finanças em 1 segundo. Conselhos IA personalizados. Modo família avançado.",
    join_thousands_users: "Junta-te a milhares de utilizadores que retomaram o controlo das suas finanças",
    
    // Onboarding
    onboarding_welcome: "Bem-vindo ao SpenSpot!",
    onboarding_salary: "Qual é o teu ordenado líquido mensal?",
    onboarding_city: "Em que cidade vives?",
    onboarding_situation: "Qual é a tua situação?",
    onboarding_personality: "Escolhe o teu consultor IA",
    onboarding_budget: "Aqui está o teu orçamento sugerido",
    situation_single: "Solteiro(a)",
    situation_couple: "Em casal",
    situation_family: "Família com filhos",
    
    // Onboarding - Join household
    have_invite_code: "Tenho um código de convite",
    create_own_account: "Criar a minha própria conta",
    join_household: "Aderir a um agregado",
    family_joined: "Agregado aderido!",
    monthly_net_income: "Rendimento mensal líquido",
    household_composition: "Composição do agregado",
    add_household_members: "Adiciona os membros do teu agregado",
    household_name: "Nome do agregado",
    first_name: "Primeiro nome",
    cannot_join: "Não é possível aderir",
    accept_and_join: "Aceito e adiro",
    welcome_to_household: "Bem-vindo ao agregado!",
    invitation_code: "Código de convite",
    invite_code_info: "O código começa por INV- e é válido por 7 dias",
    example_name: "Ex: Maria",
    join_family_name: "Aderir",
    created_by_members: "Criado por {owner} • {count} membro(s)",
    example_household_name: "Ex: Família Silva",
    email_for_invitation: "Email (para convite)",
    pocket_money_optional: "Semanada (opcional)",
    add_member: "Adicionar membro",
    summary: "Resumo",
    how_manage_finances: "Como queres gerir as finanças do agregado?",
    common_pot_config: "Configuração do Pote Comum",
    my_monthly_contribution: "A minha contribuição mensal",
    member_contribution: "Contribuição de {name}",
    total_monthly_pot: "Pote comum mensal total",
    oops_cannot_join: "Ups, não é possível aderir!",
    back_to_home: "Voltar ao início",
    track_spending: "Acompanhar gastos",
    challenges: "Desafios",
    lets_go: "Vamos lá! 🚀",
    income_label: "Rendimento",
    household_label: "Agregado",
    budget_mode_label: "Modo de orçamento",
    complete_profile_info: "Completa as tuas informações para finalizar o teu perfil",
    person_enter_salary_signup: "Esta pessoa introduzirá o seu ordenado quando se registar",
    adults_children_count: "{adults} adulto(s), {children} criança(s)",
    adult_set_salary_invite: "Cada adulto definirá o seu ordenado quando se registar através do código de convite",
    month: "mês",
    member: "Membro",
    
    // Dashboard
    dashboard: "Painel",
    available_balance: "Saldo disponível",
    this_month: "Este mês",
    spending: "Gastos",
    
    // Transactions
    transactions: "Transações",
    add_transaction: "Adicionar transação",
    transaction_amount: "Montante",
    transaction_category: "Categoria",
    transaction_date: "Data",
    transaction_description: "Descrição",
    income: "Receita",
    expense: "Despesa",
    no_transactions: "Sem transações",
    all: "Todos",
    today: "Hoje",
    this_week: "Esta semana",
    
    // Categories
    cat_logement: "Habitação",
    cat_transport: "Transporte",
    cat_alimentation: "Alimentação",
    cat_abonnements: "Subscrições",
    cat_shopping: "Compras",
    cat_loisirs: "Lazer",
    cat_sante: "Saúde",
    cat_epargne: "Poupança",
    cat_investissements: "Investimentos",
    cat_education: "Educação",
    cat_animaux: "Animais",
    cat_enfants: "Crianças",
    cat_cadeaux: "Presentes",
    cat_entretien: "Manutenção",
    cat_professionnel: "Profissional",
    cat_divers: "Outros",
    cat_personnalise: "Personalizado",
    cat_argent_de_poche: "Semanada",
    cat_autre: "Outro",
    cat_restaurant: "Restaurante",
    cat_food: "Alimentação",
    cat_voyage: "Viagem",
    cat_voyages: "Viagens",
    cat_vetements: "Vestuário",
    cat_beauté: "Beleza",
    cat_beaute: "Beleza",
    cat_carburant: "Combustível",
    cat_telephone: "Telefone",
    cat_internet: "Internet",
    cat_assurance: "Seguro",
    cat_factures: "Contas",
    edit_budget: "Editar orçamento",
    save_changes: "Guardar alterações",
    
    // Budget
    budgets: "Orçamentos",
    set_budget: "Definir orçamento",
    budget_remaining: "Restante",
    budget_spent: "Gasto",
    over_budget: "Orçamento excedido",
    
    // Objectives
    objectives: "Objetivos",
    create_objective: "Criar objetivo",
    objective_name: "Nome do objetivo",
    target_amount: "Montante alvo",
    target_date: "Data alvo",
    days_remaining: "dias restantes",
    monthly_required: "€/mês necessário",
    add_contribution: "Adicionar contribuição",
    
    // Challenges
    daily_challenge: "Desafio diário",
    challenge_completed: "Desafio concluído!",
    xp_gained: "XP ganho",
    streak: "Sequência",
    level: "Nível",
    
    // AI
    ai_advice: "Conselho IA",
    get_advice: "Obter conselho",
    ai_analyzing: "O SpenSpot está a analisar as tuas finanças...",
    
    // Settings
    settings: "Definições",
    profile: "Perfil",
    language: "Idioma",
    theme: "Tema",
    notifications: "Notificações",
    privacy: "Privacidade",
    help: "Ajuda",
    
    // Payment methods
    payment_card: "Cartão",
    payment_cash: "Dinheiro",
    payment_transfer: "Transferência",
    payment_other: "Outro",
    
    // Referral
    referral: "Indicação",
    referral_title: "Convide seus amigos!",
    referral_subtitle: "Vocês dois ganham 1 mês grátis de Premium",
    your_code: "Seu código",
    copy_code: "Copiar",
    copied: "Copiado!",
    share: "Compartilhar",
    friends_invited: "Amigos convidados",
    premium_earned: "Meses Premium ganhos",
    enter_referral_code: "Digite um código de indicação",
    apply_code: "Aplicar",
    referral_success: "Código aplicado! +1 mês Premium",
    leaderboard: "Melhores indicadores",
    
    // OCR
    scan_receipt: "Escanear recibo",
    take_photo: "Tirar foto",
    select_image: "ou selecionar imagem",
    analyzing: "Analisando recibo...",
    merchant: "Comerciante",
    
    // Alerts  
    alerts: "Alertas",
    other_info: "Outras informações",
    budget_exceeded: "Orçamento excedido",
    budget_warning: "Alerta de orçamento",
    overdraft_warning: "Risco de descoberto",
    daily_budget_tip: "Orçamento diário",
    
    // Notification Settings
    preferences_saved: "Preferências guardadas",
    push_notifications: "Notificações Push",
    enabled: "Ativadas",
    blocked_by_browser: "Bloqueadas pelo navegador",
    not_enabled: "Não ativadas",
    budget_alerts: "Alertas de Orçamento",
    alert_at_80: "Alerta a 80%",
    alert_80_desc: "Notificação quando 80% do orçamento é utilizado",
    alert_at_100: "Alerta a 100% (excedido)",
    alert_100_desc: "Notificação quando o orçamento é excedido",
    custom_threshold: "Limite personalizado",
    custom_threshold_desc: "Definir o teu próprio limite de alerta",
    balance_alerts: "Alertas de Saldo",
    balance_alerts_enabled: "Alertas de saldo ativados",
    balance_alerts_desc: "Notificação quando o saldo está baixo",
    alert_threshold: "Limite de alerta",
    alert_threshold_desc: "Alertar quando o saldo for inferior a este valor",
    zero_balance_alert: "Alerta de saldo zero",
    zero_balance_alert_desc: "Notificação quando o saldo atinge zero",
    overdraft_alert: "Alerta de descoberto",
    overdraft_alert_desc: "Notificação quando o saldo é negativo",
    overdraft_prediction: "Previsão de descoberto",
    overdraft_prediction_desc: "Notificação se o descoberto é previsto",
    propose_using_savings: "Propor utilizar poupanças",
    propose_using_savings_desc: "Propor utilizar os teus objetivos se o saldo estiver baixo",
    installment_reminders: "Lembretes de Prestações",
    reminder_x_days_before: "Lembrete X dias antes",
    reminder_before_payment: "Notificação antes da data de pagamento",
    reminder_on_payment_day: "Lembrete no dia do pagamento",
    paused_objectives_reminders: "Lembretes de Objetivos em Pausa",
    monthly_reminder: "Lembrete mensal",
    show_until_day: "Mostrar até ao dia",
    real_time: "Tempo real",
    daily_summary: "Resumo diário",
    summary_time: "Hora do resumo:",
    send_test_notification: "Enviar notificação de teste",
    unblock_in_browser: "Desbloquear nas definições do navegador",
    
    // Family
    family: "Família",
    family_title: "Minha Casa",
    create_family: "Criar casa",
    family_name: "Nome da casa",
    family_created: "Casa criada!",
    join_family: "Entrar em uma casa",
    leave_family: "Sair da casa",
    delete_family: "Excluir casa",
    invite_member: "Convidar membro",
    add_member: "Adicionar membro",
    add_child: "Adicionar filho",
    member_name: "Nome",
    member_role: "Função",
    member_salary: "Renda mensal",
    employment_status: "Estatuto profissional",
    status_employed: "Empregado(a)",
    status_freelance: "Independente",
    status_student: "Estudante",
    status_unemployed: "Desempregado(a)",
    status_other: "Outro",
    no_fixed_salary: "Sem salário fixo",
    variable_income_hint: "Rendimento variável — gerir em « Meus rendimentos »",
    fixed_salary_question: "Tens um salário fixo?",
    variable_income_nudge_title: "Os teus rendimentos atuais",
    variable_income_nudge_desc: "Não tens um salário fixo — lembra-te de registar os teus rendimentos quando os recebes para projeções precisas.",
    log_income_now: "Registar rendimento",
    manage_income_sources: "Gerir os meus rendimentos",
    projected_this_month: "Projeção este mês",
    income_evolution_title: "Evolução dos meus rendimentos",
    income_evolution_subtitle: "Últimos {n} meses",
    income_evolution_empty: "Ainda sem rendimentos registados. Começa a registar para acompanhar a evolução.",
    fixed_income_label: "Fixo",
    variable_income_label: "Variável",
    view_variable_income_details: "Ver detalhes dos rendimentos variáveis",
    variable_income_breakdown_title: "Detalhes dos rendimentos variáveis",
    variable_income_breakdown_desc: "Lista dos rendimentos não recorrentes registados este mês",
    variable_income_breakdown_empty: "Sem rendimento variável registado este mês.",
    total_variable_income: "Total rendimento variável",
    details: "Detalhes",
    role_owner: "Proprietário",
    role_adult: "Adulto",
    role_child: "Criança",
    family_code: "Código de convite",
    enter_family_code: "Digite o código da família",
    total_income: "Renda total da casa",
    family_balance: "Saldo familiar",
    spending_by_member: "Gastos por membro",
    budget_mode: "Modo de orçamento",
    budget_shared: "Orçamento compartilhado",
    budget_separate: "Orçamentos separados",
    budget_hybrid: "Orçamento híbrido",
    budget_mode_shared_desc: "Um orçamento para toda a casa",
    budget_mode_separate_desc: "Cada adulto gerencia seu próprio orçamento",
    budget_mode_hybrid_desc: "Orçamentos individuais + conta comum",
    common_budget: "Orçamento comum",
    personal_budget: "Orçamento pessoal",
    common_percentage: "% para conta comum",
    for_member: "Para",
    family_general: "Despesa familiar",
    no_family: "Sem casa ainda",
    no_family_desc: "Crie uma casa para acompanhar as finanças da família juntos",
    family_dashboard: "Painel Familiar",
    family_transactions: "Transações familiares",
    invite_by_email: "Convidar por email",
    invite_by_code: "Compartilhar código",
    pending_invites: "Convites pendentes",
    
    // Additional common translations
    spent: "gasto",
    month: "mês",
    shared: "Partilhado",
    household: "Casa",
    show_amounts: "Mostrar valores",
    hide_amounts: "Ocultar valores",
    subscriptions: "Assinaturas",
    preferences: "Preferências",
    daily_challenge_title: "Desafio do dia",
    no_challenge: "Nenhum desafio hoje",
    challenge_answer: "Responder",
    good_job: "Muito bem!",
    try_again: "Tentar novamente",
    
    // Dashboard specific
    hello: "Olá",
    hi: "Olá",
    see_all: "Ver tudo",
    reports: "Relatórios",
    scanner: "Scanner",
    scan_receipt: "Digitalizar recibo",
    voice: "Voz",
    voice_input: "Entrada por voz",
    shopping: "Compras",
    recurring: "Recorrente",
    personal: "Pessoal",
    common_pot: "Pote Comum",
    monthly_common_pot: "Pote comum mensal",
    household_income: "Rendimento da casa",
    personal_income: "Rendimento pessoal",
    monthly_income: "Renda mensal",
    initial_balance: "Saldo inicial",
    members: "membros",
    per_week: "por semana",
    per_month: "por mês",
    on_request: "sob demanda",
    you_receive: "Você recebe",
    pocket_money_low: "Sua mesada está quase vazia!",
    no_expenses_yet: "Ainda sem despesas este mês",
    refused: "Recusada",
    approved: "Aprovada",
    pending: "Pendente",
    what_for_optional: "Para quê? (opcional)",
    budget_shared_label: "💰 Orçamento compartilhado",
    budget_separate_label: "👤 Orçamentos separados",
    budget_hybrid_label: "🔄 Orçamento misto",
    hybrid: "Misto",
    more_info: "Mais informações",
    
    // Family page
    household_members: "Membros da casa",
    household_deleted: "Casa excluída",
    edit_salary: "Editar meu salário",
    net_monthly_income: "Renda mensal líquida",
    update_income_desc: "Atualize sua renda mensal líquida. Este valor será usado para calcular o orçamento familiar.",
    
    // Objectives
    my_objectives: "Meus Objetivos",
    save_for_projects: "Poupe para seus projetos",
    create: "Criar",
    statistics: "Estatísticas",
    progress: "Progresso",
    challenge_completed: "Desafio concluído!",
    keep_going: "Continue assim para completar o desafio!",
    well_done: "Parabéns!",
    not_quite: "Não exatamente...",
    checking: "Verificando...",
    submit_answer: "Validar resposta",
    
    // Family page (Portuguese)
    pocket_money: "Mesada",
    my_pocket_money: "Minha mesada",
    balance_label: "Saldo:",
    current_balance: "Saldo atual:",
    optional: "opcional",
    transfer_ownership: "Transferir propriedade",
    first_name: "Nome",
    family_name_label: "Sobrenome",
    invalid_amount: "Valor inválido",
    member_added: "Membro adicionado!",
    passwords_dont_match: "As senhas não correspondem",
    register: "Registrar",
    give: "Dar",
    copy_code: "Copiar código",
    common: "Comum",
    code_copied: "Código copiado!",
    adult: "Adulto",
    invitation_created: "✅ Convite criado",
    limit_reached: "⚠️ Limite atingido",
    contribution_evolution: "Evolução das contribuições",
    or: "ou",
    you: "Você",
    you_set_password: "Você define a senha",
    left_household: "Você saiu da casa",
    view_subscriptions: "Ver assinaturas",
    enter_valid_amount: "Por favor, insira um valor válido",
    budget_usage: "Uso do orçamento",
    total_received: "Total recebido",
    total_spent: "Total gasto",
    total_this_month: "Total este mês",
    total_subscriptions_month: "Total de assinaturas este mês",
    total_family: "Total Família",
    savings_rate: "Taxa de poupança",
    salary_updated: "Salário atualizado!",
    request_money: "Solicitar dinheiro",
    request_sent: "Solicitação enviada!",
    request_amount: "Valor solicitado",
    my_pending_requests: "Minhas solicitações pendentes",
    manage_family_finances: "Gerencie as finanças da família",
    charts: "Gráficos",
    tools: "Ferramentas",
    my_expenses: "Minhas despesas",
    hide: "Ocultar",
    my_family: "Minha família",
    i_want: "Eu quero",
    spending_by_category: "Gastos por categoria",
    no_data: "Sem dados",
    child_dashboard: "Painel infantil",
    owner: "Proprietário",
    child: "Criança",
    username: "Nome de usuário",
    password: "Senha",
    confirm_password: "Confirmar senha",
    create_account: "Criar conta",
    account_created: "Conta criada!",
    email_address: "Endereço de email",
    invite_by_email_desc: "Um email de convite será enviado",
    send_invitation: "Enviar convite",
    invitation_sent: "Convite enviado!",
    pending_invitations: "Convites pendentes",
    cancel_invitation: "Cancelar convite",
    remove_member: "Remover membro",
    confirm_remove_member: "Tem certeza de que deseja remover este membro?",
    allowance_settings: "Configurações de mesada",
    enable_pocket_money: "Ativar mesada",
    allowance_amount: "Valor da mesada",
    allowance_frequency: "Frequência",
    weekly: "Semanal",
    monthly: "Mensal",
    on_demand: "Sob demanda",
    approve: "Aprovar",
    reject: "Rejeitar",
    request_approved: "Solicitação aprovada",
    request_rejected: "Solicitação rejeitada",
    child_requests: "Solicitações das crianças",
    no_pending_requests: "Nenhuma solicitação pendente",
    member_spending: "Gastos do membro",
    family_overview: "Visão geral da família",
    contribution_to_common: "Contribuição para o pote comum",
    personal_remaining: "Restante pessoal",
    
    // Family page additional
    join_my_household: "Junte-se à minha casa SpenSpot",
    failed_to_update_salary: "Falha ao atualizar salário",
    failed_to_remove_member: "Falha ao remover membro",
    ownership_transferred: "Propriedade transferida com sucesso!",
    failed_to_transfer: "Falha ao transferir propriedade",
    settings_saved: "Configurações salvas!",
    request_approved_emoji: "Solicitação aprovada! 💰",
    request_denied: "Solicitação negada",
    invite_a_member: "Convidar um membro",
    pending_invitations: "Convites pendentes",
    invitation_resent: "Convite reenviado!",
    resend_invitation: "Reenviar convite",
    invitation_cancelled: "Convite cancelado",
    cancel_invitation: "Cancelar convite",
    create_invitation: "Criar convite",
    upgrade_subscription: "Atualizar assinatura",
    members_count: " membros",
    contribute: "Contribuir",
    monthly_budget: "Orçamento mensal",
    spent_this_month: "Gasto este mês",
    common_expenses_by_member: "Despesas comuns por membro",
    expected_contributions: "Contribuições mensais esperadas",
    not_set: "Não definido",
    manage_pocket_money: "Gerir mesada",
    remove_from_household: "Remover da casa",
    pocket_money_requests: "Pedidos de mesada",
    deny: "Negar",
    accept: "Aprovar",
    breakdown_by_member: "Detalhes por membro",
    monthly_subscriptions: "Assinaturas mensais",
    manage: "Gerir",
    by_category: "Por categoria",
    upcoming_debits: "Próximos",
    spending_by_category_title: "Gastos por categoria",
    allowance: "Mesada",
    cumulative: "Acumulado",
    spending_categories: "Categorias de gastos",
    edit_my_salary: "Editar meu salário",
    net_monthly_income_euro: "Renda mensal líquida (€)",
    raise_bonus_job: "Aumento, bônus, mudança de emprego",
    instant_budget_update: "Atualização instantânea do orçamento familiar",
    remove_a_member: "Remover membro",
    remove: "Remover",
    give_pocket_money: "Dar mesada",
    enable_pocket_money_child: "Ativar mesada",
    monthly_allowance_optional: "Mesada mensal (opcional)",
    shared_budget: "Orçamento compartilhado",
    separate_budget: "Orçamento separado",
    code_for: "Código para",
    continue_action: "Continuar",
    contribute_to_common_pot: "Contribuir para o Pote Comum",
    contribution_added: "Contribuição adicionada!",
    common_pot_contribution: "Contribuição para o pote comum",
    create_credentials: "Criar credenciais",
    from_which_balance: "De qual saldo?",
    email_sent_to: "Email enviado para",
    example_groceries: "Ex: Compras do mês",
    example_family_name: "Ex: Família Silva",
    example_name: "Ex: Maria",
    settings_updated: "Configurações atualizadas",
    regular_allowance: "Valor regular",
    amount_to_give: "Valor a dar",
    pocket_money_settings: "Configurações de mesada",
    my_balance: "Meu saldo",
    available_exclaim: "Disponível!",
    
    // Subscriptions
    subscription_created: "Assinatura criada!",
    subscription_paused: "Assinatura pausada",
    subscription_updated: "Assinatura atualizada!",
    subscription_reactivated: "Assinatura reativada",
    subscription_deleted: "Assinatura excluída",
    common_subscriptions: "Assinaturas Comuns",
    personal_subscriptions: "Assinaturas Pessoais",
    active_subscriptions_label: "Assinaturas ativas",
    add_subscription: "Adicionar uma assinatura",
    no_subscriptions_yet: "Sem assinaturas ainda",
    no_results_found: "Nenhum resultado encontrado",
    clear_filters: "Limpar filtros",
    paused_label: "Pausado",
    example_netflix: "Ex: Netflix, Spotify...",
    example_family_plan: "Ex: Plano familiar",
    manage_recurring_subs: "Gerencie suas assinaturas recorrentes",
    my_subscriptions: "Minhas Assinaturas",
    pause_subscription: "Pausar",
    name_amount_required: "Nome e valor obrigatórios",
    new_subscription: "Nova Assinatura",
    search_subscription: "Pesquisar assinatura...",
    reactivate: "Reativar",
    by_category_label: "Por Categoria",
    of_month_suffix: " do mês",
    // Subscription frequencies & schedule
    frequency: "Frequência",
    first_payment: "Primeiro pagamento",
    first_payment_date: "Data do primeiro pagamento",
    end_date: "Data de término",
    end_date_optional: "Data de término (opcional)",
    next_payment: "Próximo pagamento",
    custom_days_label: "A cada X dias",
    custom_days_placeholder: "Ex: 60",
    freq_weekly: "Toda semana",
    freq_monthly: "Todo mês",
    freq_quarterly: "A cada 3 meses",
    freq_semi_annual: "A cada 6 meses",
    freq_yearly: "Todo ano",
    freq_custom_days: "Personalizado (dias)",
    upcoming_payments: "Próximos vencimentos",
    filter_this_month: "Este mês",
    filter_30_days: "Próximos 30 dias",
    filter_90_days: "Próximos 90 dias",
    total_to_plan: "Total a prever",
    no_upcoming_payments: "Nenhum vencimento neste período",
    // Multi-member attribution
    attributed_to_members: "Para qual(is) membro(s)?",
    attributed_to_members_hint: "Selecione um ou mais membros para quem é esta despesa. O débito permanece na conta de quem paga.",
    spending_by_member: "Gastos por membro",
    no_attributed_spending: "Nenhuma despesa atribuída este mês",
    filter_by_member: "Filtrar por membro",
    all_members: "Todos os membros",
    
    // More common strings
    view_objectives: "Ver objetivos",
    view_budgets: "Ver orçamentos",
    total_colon: "Total:",
    real_time: "Tempo real",
    success_rate: "Taxa de sucesso",
    select_category: "Selecione uma categoria",
    select_image: "Selecionar uma imagem",
    secure: "Seguro",
    sync: "Sync",
    delete_transaction_confirm: "Excluir esta transação?",
    delete_category_confirm: "Excluir esta categoria?",
    delete_budget_confirm: "Excluir este orçamento?",
    subject_optional: "Assunto (opcional)",
    track_spending: "Acompanhar gastos",
    suggest_budget: "Sugerir orçamento",
    suggestions_for_you: "Sugestões para você",
    objectives_statistics: "Estatísticas de Objetivos",
    price_statistics: "Estatísticas de preços",
    smart_shopping: "Compras Inteligentes",
    custom_threshold: "Limite personalizado",
    will_be_deducted_from: "Será deduzido de:",
    sign_in: "Entrar",
    scan_receipt: "Escanear um recibo",
    email_scan: "Scan de Emails",
    no_expiration: "Sem expiração",
    voice_input: "Entrada de voz",
    voice_input_title: "Entrada de Voz",
    role_colon: "Função:",
    summary: "Resumo",
    daily_summary: "Resumo diário",
    results_for: "Resultados para",
    split_over_months: "Dividir em vários meses",
    negative_balance_ignored: "Saldo negativo ignorado",
    previous_month_negative: "Saldo negativo do mês anterior",
    initial_balance_short: "Inicial",
    personal_situation: "Situação pessoal",
    overdraft_risk_alert: "🚨 Risco de Cheque Especial",
    sync_complete_alert: "✅ Sincronização Completa",
    budget_alert: "⚠️ Alerta de Orçamento",
    objectives_reminder_alert: "⏸️ Lembrete de Objetivos",
    email_placeholder: "email@exemplo.com ou usuário",
    
    // Additional translations
    email_required_adults: "Email é obrigatório para adultos",
    child_added_create_account: "Criança adicionada! Crie sua conta para que possa fazer login.",
    username_unavailable: "Nome de usuário não disponível",
    invitation_sent_to_email: "Convite enviado para o email",
    invite_by_email: "Convidar por email",
    child_creates_account: "Criança cria sua conta",
    childs_email: "Email da criança",
    create_account_btn: "Criar conta",
    send_invitation: "Enviar convite",
    allow_child_manage_money: "Permitir que a criança gerencie seu dinheiro",
    auto_monthly_allowance_desc: "Valor mensal automático (deixe vazio para desativar)",
    common_pot_balance_label: "Saldo do pote comum",
    deducted_from_personal: "O dinheiro será deduzido do seu saldo pessoal",
    update_income_desc: "Atualize sua renda mensal líquida. Este valor será usado para calcular o orçamento familiar.",
    household_deleted: "Casa excluída",
    
    // Objectives page
    add_contribution: "Adicionar contribuição",
    achieved_exclaim: "Alcançado!",
    no_objectives_yet: "Nenhum objetivo ainda",
    shared_label: "Compartilhado",
    pause_behavior: "Comportamento de pausa",
    auto_contribute: "Contribuição automática",
    contributed: "Contribuído",
    create_first_objective: "Criar meu primeiro objetivo",
    target_date: "Data alvo",
    avg_duration_days: "Duração média (dias)",
    failed_to_create: "Falha ao criar",
    failed_to_update: "Falha ao atualizar",
    example_vacation: "Ex: Para férias em família",
    contribution_day: "Dia da contribuição",
    days_paused: "Dias pausados",
    leave_empty_default: "Deixe vazio para restaurar o valor padrão",
    change_this_month_contribution: "Mudar contribuição deste mês",
    change_label: "Mudar",
    modified_this_month: "Modificado este mês",
    target_amount: "Valor alvo",
    total_pauses: "Total de pausas",
    new_amount: "Novo valor",
    new_objective: "Novo Objetivo",
    objective_created: "Objetivo criado!",
    most_paused_objective: "Objetivo mais pausado",
    objective_paused: "Objetivo pausado",
    objective_updated: "Objetivo atualizado!",
    objective_reactivated: "Objetivo reativado",
    objective_deleted: "Objetivo excluído",
    objectives_paused_1x: "Objetivos pausados 1x+",
    objective_achieved: "Objetivo alcançado!",
    personal_label: "Pessoal",
    who_pays_label: "Quem economiza?",
    i_only: "Só eu",
    everyone: "Todos",
    pause_reason: "Motivo da pausa",
    no_objectives_to_display: "Nenhum objetivo para exibir",
    start_saving_for: "Comece a economizar para",
    remaining_amount: "Restante para economizar",
    monthly_contrib_of: "Contribuição mensal de",
    will_pause_until: "Pausado até",
    
    // Missing keys
    fill_required_fields: "Preencha todos os campos obrigatórios",
    daily: "Diário",
    objective: "Objetivo",
    send: "Enviar",
    suggestion: "Sugestão",
    deleted: "Excluído",
    code: "Código",
    state: "Estado",
    
    // Export / Reports
    export_error: "Erro na exportação",
    report_downloaded: "Relatório transferido! Abra-o e imprima em PDF (Ctrl+P)",
    no_data_for_year: "Sem dados para ",
    add_transactions_for_report: "Adicione transações para gerar o seu relatório fiscal",
    export_disclaimer: "Este relatório é gerado a partir das suas despesas registadas no SpenSpot. Verifique os montantes com os seus extratos bancários antes de os utilizar para a sua declaração fiscal.",
    
    // Invoices page
    invoices: "Faturas",
    my_invoices: "Minhas Faturas",
    manage_invoices_desc: "Armazene suas faturas e recibos com segurança",
    upload_invoice: "Adicionar Fatura",
    product_name: "Nome do Produto",
    store_name: "Loja",
    purchase_date: "Data da Compra",
    warranty_months: "Garantia (meses)",
    warranty_expires: "Garantia Expira",
    warranty_status: "Status da Garantia",
    warranty_active: "Ativa",
    warranty_expiring: "Expirando em Breve",
    warranty_expired: "Expirada",
    warranty_expiring_alert: "Garantia expirando em breve",
    warranty_reminder: "Lembrete de garantia",
    view_invoice: "Ver fatura",
    no_invoices_yet: "Nenhuma fatura ainda",
    add_first_invoice: "Adicionar minha primeira fatura",
    invoice_uploaded: "Fatura adicionada!",
    invoice_deleted: "Fatura excluída",
    invoice_updated: "Fatura atualizada",
    download_invoice: "Baixar",
    search_invoices: "Pesquisar por produto, loja...",
    invoice_limit_reached: "Limite de faturas atingido",
    upgrade_for_more: "Atualize sua assinatura para mais armazenamento",
    expiring_warranties: "Garantias Expirando",
    days_remaining: "dias restantes",
    invoice_details: "Detalhes da Fatura",
    select_file: "Selecionar arquivo",
    max_file_size: "Tamanho máx: 5 MB",
    allowed_formats: "Formatos: JPG, PNG, PDF",
    storage_used: "Armazenamento usado",
    of_limit: "de",
    
    // Settings page
    subscription_cancelled: "Assinatura cancelada",
    quick_access_home: "Acesso rápido da tela inicial",
    anticipate_overruns: "Antecipe estouros de orçamento",
    interface_language: "Idioma da interface",
    voice_ai_assistant: "Assistente de voz IA",
    ai_advisor: "Consultor IA",
    create_join_household: "Criar ou aderir a um agregado",
    failed_change_plan: "Falha ao mudar plano",
    expires_on: "Expira em",
    subscription: "Subscrição",
    free: "Gratuito",
    made_with_love: "Feito com ❤️ em França",
    earn_free_premium: "Ganha 1 mês grátis de Premium!",
    keep_subscription: "Manter subscrição",
    manage_custom_categories: "Gerir as minhas categorias",
    manage_household: "Gerir o meu agregado",
    import_csv: "Importar CSV",
    import_bank_history: "Importar histórico bancário",
    ai_personality: "Personalidade IA",
    preferences: "Preferências",
    unlimited_ai_questions: "Perguntas IA ilimitadas",
    replay_tutorial: "Rever tutorial",
    other_label: "Outros",
    cancel_subscription: "Cancelar subscrição",
    cancel_subscription_warning: "Tens a certeza de que queres cancelar? Perderás o acesso às funcionalidades premium.",
    member_limit_reduced: "Limite de membros reduzido para 2",
    confirm_cancellation: "Confirmar cancelamento",
    
    // EmailScanPage - additional strings
    copy_paste_order_email: "Abre o teu email de confirmação de encomenda (Amazon, Fnac, etc.) e copia todo o conteúdo",
    click_paste_email: "Clica em \"Colar um email\" e cola o conteúdo",
    spenspot_auto_detect: "O SpenSpot deteta automaticamente o montante e o comerciante. Clica em Importar!",
    feature_in_development: "Esta funcionalidade está em desenvolvimento. Por agora, usa a opção \"Colar um email\".",
    auto_import_forward: "Para importar automaticamente, reencaminha os teus emails de encomenda",
    paste_order_email_content: "Copia e cola o conteúdo do teu email de encomenda",
    paste_email_text_html: "Cola aqui o conteúdo do email (texto ou HTML)...",
    paste_order_confirmation_instructions: "Cola os teus emails de confirmação de encomenda para os importar automaticamente",
    
    // RecurringPage - strings
    recurring_expenses: "Despesas Recorrentes",
    subscriptions_payments: "Subscrições e pagamentos",
    detect: "Detetar",
    patterns_detected_count: "{count} padrão(ões) detetado(s)",
    detection_error: "Erro de deteção",
    recurring_confirmed: "Despesa recorrente confirmada",
    confirmation_error: "Erro de confirmação",
    detected_patterns: "Padrões detetados",
    confirmed_subscriptions: "Subscrições confirmadas",
    no_confirmed_subscriptions: "Nenhuma subscrição confirmada",
    click_detect_analyze: "Clica em \"Detetar\" para analisar as tuas transações",
    spenspot_analyzes_6_months: "O SpenSpot analisa os teus últimos 6 meses de transações",
    patterns_auto_detected: "Os padrões recorrentes são detetados automaticamente",
    confirm_subs_for_reminders: "Confirma as tuas subscrições para receber lembretes",
    confirm_recurring_expense: "Confirmar esta despesa recorrente?",
    occurrences: "Ocorrências",
    next_expected_date: "Próxima data prevista",
    in_days: "Em",
    monthly_freq: "Mensal",
    weekly_freq: "Semanal",
    yearly_freq: "Anual",
    
    // ShoppingPage
    search_error: "Erro de pesquisa",
    smart_shopping: "Compras Inteligentes",
    price_comparison: "Comparador de preços",
    search_product: "Procurar um produto...",
    compare: "Comparar",
    results_for_query: "Resultados para",
    not_enough_data_scan: "Dados insuficientes. Digitaliza mais recibos para comparar preços!",
    price_statistics: "Estatísticas de preços",
    suggestions_for_you: "Sugestões para ti",
    best_savings_opportunities: "Melhores oportunidades de poupança",
    no_data_yet: "Ainda sem dados",
    scan_receipts_to_compare: "Digitaliza os teus recibos para começar a comparar preços",
    scan_receipt_ocr: "Digitaliza os teus recibos com OCR",
    spenspot_analyzes_prices: "O SpenSpot analisa e armazena os preços dos produtos",
    compare_prices_save: "Compara preços entre lojas e poupa!",
    
    // ObjectivesPage
    delete_objective_confirm: "Eliminar objetivo?",
    objectives_statistics: "Estatísticas dos Objetivos",
    success_rate: "Taxa de sucesso",
    pause_behavior: "Comportamento de pausa",
    total_pauses: "Número de pausas",
    days_paused: "Dias em pausa",
    objectives_paused_1x: "Objetivos pausados pelo menos 1x",
    avg_duration_days: "Duração média (dias)",
    most_paused_objective: "Objetivo mais pausado",
    savings_evolution: "Evolução da poupança",
    auto_monthly_contribution: "Contribuição mensal automática",
    first_ordinal: "1º",
    action_irreversible: "Esta ação é irreversível.",
    of_target: "de {amount} em objetivo",
    example_vacation: "Ex: Férias de verão",
    
    // DashboardPage
    no_pocket_money_ask_parents: "Sem mais dinheiro de bolso! Pede aos teus pais.",
    i_spent: "Gastei",
    example_video_game: "Ex: Novo jogo de vídeo",
    no_objectives: "Sem objetivos",
    create_household: "Criar agregado familiar",
    manage_finances_together: "Gerir finanças em família",
    charts: "Gráficos",
    emails: "Emails",
    export: "Exportar",
    voice_input_label: "Entrada de voz",
    scan_receipt_label: "Digitalizar recibo",
    add_expense_label: "Adicionar despesa",
    hybrid_label: "Misto",
    initial_colon: "Inicial",
    
    // CategoriesPage
    name_required: "Nome obrigatório",
    category_created: "Categoria criada",
    category_updated: "Categoria atualizada",
    delete_category_question: "Eliminar esta categoria?",
    category_deleted: "Categoria eliminada",
    manage_expense_categories: "Gere as suas categorias de despesas",
    new_label: "Nova",
    no_custom_categories: "Sem categorias personalizadas",
    create_category: "Criar categoria",
    edit_category: "Editar categoria",
    new_category: "Nova categoria",
    example_category_vacation: "Ex: Férias",
    color: "Cor",
    create_categories_to_organize: "Cria categorias para organizar melhor as tuas despesas",
    
    // OnboardingPage
    pocket_money_optional: "Dinheiro de bolso (opcional)",
    back_to_home: "Voltar ao início",
    track_spending: "Acompanhar gastos",
    challenges: "Desafios",
    lets_go: "Vamos lá! 🚀",
    income_label: "Rendimento",
    household_label: "Agregado",
    budget_mode_label: "Modo de orçamento",
    
    // FamilyPage
    allow_child_manage_money: "Permitir à criança gerir o seu dinheiro",
    continue_btn: "Continuar",
    create_account_btn: "Criar conta",
    example_name_marie: "Ex: Maria",
    send_invitation: "Enviar convite",
    code_for: "Código para",
    email_sent_to: "Email enviado para",
    settings_updated: "Definições atualizadas",
    common_pot_contribution: "Contribuição para o pote comum",
    contribution_added: "Contribuição adicionada!",
    contribute_common_pot: "Contribuir para o Pote Comum",
    description_optional: "Descrição (opcional)",
    example_monthly_groceries: "Ex: Compras do mês",
    
    // IncomeAdjustmentModal
    adjustment_added: "Ajuste adicionado",
    adjustment_add_failed: "Erro ao adicionar",
    adjustment_deleted: "Ajuste eliminado",
    adjust_my_income: "Ajustar o meu rendimento",
    base_salary: "Salário base",
    adjustments: "Ajustes",
    effective_income: "Rendimento efetivo",
    add_adjustment: "Adicionar ajuste",
    adjustment_type: "Tipo de ajuste",
    amount_euro: "Valor (€)",
    this_month_adjustments: "Ajustes deste mês",
    invite_email_auto_sent: "Um email com o código de convite será enviado automaticamente",
    current_balance: "Saldo atual",

    // Income Sources (multi-rendimentos)
    income_sources: "Fontes de rendimento",
    income_sources_title: "Gerir os meus rendimentos",
    income_sources_desc: "Salário, auxílios, independente, aluguer...",
    add_income_source: "Adicionar uma fonte",
    edit_income_source: "Editar fonte",
    income_type: "Tipo de rendimento",
    income_amount: "Valor",
    income_frequency: "Frequência",
    income_frequency_monthly: "Mensal",
    income_frequency_weekly: "Semanal",
    income_frequency_daily: "Diário",
    income_frequency_one_time: "Pontual",
    income_date: "Data",
    income_start_date: "Data de início (opcional)",
    income_end_date: "Data de fim (opcional)",
    income_custom_label: "Etiqueta personalizada (opcional)",
    income_notes: "Notas (opcional)",
    income_active: "Fonte ativa",
    income_inactive: "Inativa",
    total_monthly_income: "Total de rendimentos do mês",
    income_source_added: "Fonte adicionada",
    income_source_updated: "Fonte atualizada",
    income_source_deleted: "Fonte eliminada",
    income_source_save_failed: "Falha ao guardar",
    income_source_delete_failed: "Falha ao eliminar",
    confirm_delete_income_source: "Eliminar esta fonte de rendimento?",
    no_income_sources: "Nenhuma fonte de rendimento. Adicione o seu salário para começar.",
    monthly_contribution: "Contribuição este mês",
    income_breakdown_by_frequency: "Detalhe por frequência",
    family_total_income: "Rendimentos do agregado",
    personal_income: "Rendimentos pessoais",

    // LandingPage - Auth
    quick_secure_login: "Início de sessão rápido e seguro",
    credentials: "Credenciais",
    email_or_username: "Email ou nome de utilizador",
    login: "Iniciar sessão",
    create_spenspot_account: "Cria a tua conta SpenSpot (email obrigatório)",
    enter_email_or_username: "Introduz o teu email ou nome de utilizador",
    required_password_recovery: "Necessário para recuperar a tua palavra-passe",
    email_or_username_placeholder: "email@exemplo.com ou identificador",
    already_have_account: "Já tens conta? Iniciar sessão",
    no_account_signup: "Não tens conta? Regista-te",
    signup: "Registar",
    log_in: "Iniciar sessão",
    
    // QuickAddModal
    split_payment: "Pagamento misto",
    add_split_payment: "+ Pagamento misto",
    remaining_amount: "Restante",
    add_payment_method: "Adicionar método de pagamento",
    subscription_name: "Nome da subscrição",
    subscription_placeholder: "Ex: Netflix, Spotify, Seguro...",
    name_required_subscription: "O nome é obrigatório para uma subscrição",
    for_label: "Para",
    adding: "A adicionar...",
    create_installment_payment: "Criar pagamento em {count}x",
    add_expense: "Adicionar despesa",
    
    // VoiceInputModal
    voice_input: "Entrada de Voz",
    voice_input_example: "Diz a tua despesa, por exemplo: \"25 euros no restaurante\"",
    browser_no_speech: "O teu navegador não suporta reconhecimento de voz",
    no_speech_detected: "Nenhuma voz detetada",
    tap_to_speak: "Toca para falar",
    error_adding: "Erro ao adicionar",
    expense_description_placeholder: "Descrição da despesa",
    which_category: "Qual categoria?",
    
    // PredictionsPage
    anticipate_spending: "Antecipa os teus gastos",
    refresh: "Atualizar",
    total_budget: "Orçamento total",
    predicted_end_month: "Previsão fim do mês",
    daily_budget_remaining: "Orçamento/dia restante",
    days_remaining_month: "dias restantes este mês",
    urgent_alerts: "Alertas urgentes",
    no_predictions_available: "Nenhuma previsão disponível",
    create_budget: "Criar orçamento",
    
    // AI Predictions Explanation
    how_predictions_calculated: "Como são calculadas as previsões?",
    daily_rate: "Ritmo diário",
    daily_rate_explanation: "Despesas do mês ÷ Dias passados = Média por dia",
    end_month_forecast: "Previsão fim do mês",
    end_month_forecast_explanation: "Despesas atuais + (Ritmo diário × Dias restantes)",
    recommended_daily_budget: "Orçamento diário aconselhado",
    recommended_daily_budget_explanation: "(Orçamento total - Despesas atuais) ÷ Dias restantes",
    what_is_excluded: "O que é excluído",
    what_is_excluded_explanation: "Receitas e poupanças não são contadas como despesas nestes cálculos.",
    predictions_tip: "As previsões são baseadas no teu ritmo de gastos atual este mês. Quanto mais transações adicionares, mais precisas ficam.",
    current_rate: "Ritmo atual",
    per_day: "/dia",
    
    // ChatPage
    error_loading_conversation: "Erro ao carregar",
    conversation_deleted: "Conversa eliminada",
    error_deleting: "Erro ao eliminar",
    limited_access: "Acesso limitado",
    ai_financial_assistant: "Assistente Financeiro IA",
    analyze_spending: "Analisar gastos",
    suggest_budget: "Sugerir orçamento",
    example_questions: "Exemplos de perguntas:",
    thinking: "A pensar...",
    ask_question_placeholder: "Faz a tua pergunta...",
    password_requirements: "Min. 8 caracteres, 1 maiúscula, 1 minúscula, 1 dígito",
    create_budgets_to_see_predictions: "Cria orçamentos para ver previsões",
    ai_assistant_premium_required: "O assistente IA está disponível com as subscrições Standard e Premium.",
    ai_assistant_description: "Faz perguntas sobre as tuas finanças, obtém conselhos de orçamento ou analisa os teus gastos.",
    
    // Contact Support
    contact_support: "Contactar o suporte",
    contact_support_desc: "Envie-nos uma mensagem, responderemos rapidamente.",
    contact_support_short: "Problema, sugestão, pergunta",
    contact_type: "Tipo de pedido",
    problem: "Problema",
    suggestion: "Sugestão",
    question_label: "Pergunta",
    subject: "Assunto",
    message: "Mensagem",
    contact_subject_placeholder: "Ex: Problema de conexão",
    contact_message_placeholder: "Descreva o seu pedido em detalhe...",
    contact_sent_as: "Enviado como",
    reply_to: "Responder a",
    sending: "A enviar...",
    contact_sent_success: "Mensagem enviada! Responderemos rapidamente.",
    contact_send_error: "Erro ao enviar mensagem",
    message_required: "A mensagem é obrigatória",
    message_too_short: "A mensagem deve ter pelo menos 10 caracteres",
    
    // Missing translations - Added Feb 2026
    checking_quota: "A verificar o seu quota...",
    limit_reached: "Limite atingido",
    ocr_not_available: "A digitalização OCR não está disponível com a sua assinatura gratuita.",
    scans_used: "Utilizou {used}/{limit} digitalizações este mês.",
    upgrade_for_ocr: "Atualize a sua assinatura para obter mais digitalizações OCR.",
    view_subscriptions_btn: "Ver assinaturas",
    select_category: "Selecione uma categoria",
    error_updating: "Erro ao atualizar",
    edit_transaction: "Editar transação",
    thinking: "A pensar...",
    invoice_limit: "Limite de faturas",
    invoice_storage_limits: "Limites de armazenamento de faturas",
    limits_saved: "Limites guardados!",
    error_saving: "Erro ao guardar",
    challenge_management: "Gestão de Desafios",
    create_manage_challenges: "Criar e gerir desafios diários",
    new_challenge: "Novo desafio",
    default_challenges: "Desafios padrão",
    custom_challenges: "Desafios personalizados",
    no_custom_challenges: "Sem desafios personalizados",
    click_new_challenge: "Clique em \"Novo desafio\" para criar um",
    edit_challenge: "Editar desafio",
    ai_generation: "Geração IA",
    theme_optional: "Tema (opcional)",
    easy: "Fácil",
    medium: "Médio",
    hard: "Difícil",
    generate: "Gerar",
    target_languages: "Idiomas alvo",
    xp_reward: "Recompensa XP",
    target_number: "Objetivo (número)",
    update: "Atualizar",
    create: "Criar",
    financial_analytics: "Análise Financeira",
    year: "Ano",
    reset_password: "Redefinir palavra-passe",
    password_reset_success: "Palavra-passe redefinida!",
    back_to_login: "Voltar ao login",
    email_sent: "Email enviado!",
    check_email_reset: "Verifique o seu email para o link de redefinição.",
    send_reset_link: "Enviar link de redefinição",
    monthly_report: "Relatório Mensal",
    report_opened_print: "Relatório aberto - use Ctrl+P para imprimir como PDF",
    remaining: "Restante",
    not_available: "Não disponível",
    share_your_code: "Partilha o teu código",
    objectives_progress: "Progresso dos objetivos",
    transactions_this_month: "Transações este mês",
    ocr_scans_month: "Digitalizações OCR/mês",
    ai_questions_month: "Perguntas IA/mês",
    voice_assistant: "Assistente de voz",
    members: "Membros",
    view_objectives: "Ver objetivos",
    view_budgets: "Ver orçamentos",
    offline_mode: "Modo offline",
    sync: "Sincronizar",
    overdraft_risk: "Risco de Descoberto",
    budget_alert: "Alerta de Orçamento",
    objectives_reminder: "Lembrete de Objetivos",
    sync_success: "Sincronização bem-sucedida",
    
    // Budget page translations
    choose_category: "Escolher uma categoria",
    total_budget_month: "Orçamento total este mês",
    suggested_budget: "Orçamento sugerido (50/30/20)",
    no_budgets_yet: "Ainda não tens orçamentos definidos",
    no_budgets_explanation: "Os orçamentos ajudam-te a controlar os teus gastos por categoria. Define limites mensais para cada categoria e acompanha o teu progresso.",
    add_first_budget: "Adicionar o primeiro orçamento",
    // Date picker for transactions
    expense_date: "Data da despesa",
    date_today: "Hoje",
    date_warning_past_month: "⚠️ Esta despesa será registada num mês passado. Irá afetar os relatórios e estatísticas desse mês.",
    date_warning_future: "ℹ️ Esta despesa é no futuro. Será contabilizada na data indicada.",
    date_warning_far_past: "⚠️ Data muito antiga (mais de 6 meses). Tem a certeza?",
    // Reflexive questions for simulator
    reflexive_section_title: "Reflete antes de comprar",
    reflexive_section_subtitle: "Marca o que se aplica (opcional) — a IA terá em conta",
    reflexive_q_need: "Realmente preciso disto (não é só vontade)",
    reflexive_q_thought: "Já refleti (não é compra por impulso)",
    reflexive_q_compared: "Comparei preços ou procurei alternativas",
    reflexive_q_aligned: "Está alinhado com as minhas prioridades e valores",
    reflexive_q_long_term: "Vou mesmo usar a longo prazo",
    reflexive_reason_label: "Porque o quero? (opcional)",
    reflexive_reason_placeholder: "Descreve em poucas palavras a razão desta compra...",
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Available languages
export const availableLanguages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem('spenspot_language');
    if (stored && ['fr', 'en', 'pt'].includes(stored)) {
      return stored;
    }
    
    // Auto-detect from browser language
    const browserLang = navigator.language || navigator.userLanguage || 'fr';
    const langCode = browserLang.split('-')[0].toLowerCase();
    
    // Map browser language to supported languages
    if (langCode === 'pt') return 'pt';
    if (langCode === 'en') return 'en';
    return 'fr'; // Default to French
  });

  useEffect(() => {
    localStorage.setItem('spenspot_language', language);
  }, [language]);

  const t = useCallback((key) => {
    const value = translations[language]?.[key] || translations.fr[key];
    if (value) return value;
    // Graceful fallback for cat_* keys: humanize the suffix instead of leaking "cat_xxx"
    if (typeof key === 'string' && key.startsWith('cat_')) {
      const suffix = key.slice(4).replace(/_/g, ' ');
      return suffix.charAt(0).toUpperCase() + suffix.slice(1);
    }
    return key;
  }, [language]);

  const toggleLanguage = () => {
    const langs = ['fr', 'en', 'pt'];
    const currentIndex = langs.indexOf(language);
    const nextIndex = (currentIndex + 1) % langs.length;
    setLanguage(langs[nextIndex]);
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translations: translations[language],
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
